import { IndirectStorageBufferAttribute, StorageBufferAttribute } from 'three/webgpu';
import { storage, uniform, workgroupId, localId, wgsl, wgslFn } from './tslCompat';
import { qrot, qconj, obbSupport } from './quatUtils';
import { AVBD_COLLISION_MARGIN, AVBD_FRICTION_STATIC } from '../avbdParams';
import { assertStorageBufferBudget } from './bindingBudget';
import {
  CONTACT_INFO_SPECULATIVE_BIT,
  CONTACT_INFO_SPECULATIVE_FEATURE_BASE,
  CONTACT_RECORD_VEC4S,
  contactRecordBaseFloatIndex,
  contactRecordHelpers,
} from './contactRecord';
import { shapeEncodingHelpers } from './shapeEncoding';
import { ccdHelpers } from './ccdHelpers';
import {
  COMPACT_CONTACT_POOL_HEADER_WORDS,
  COMPACT_CONTACT_POOL_GENERATION_MODE,
  COMPACT_CONTACT_POOL_EPOCH_MAX,
  COMPACT_CONTACT_POOL_RECLAIM_MODE_PRESSURE_FULL,
  COMPACT_CONTACT_POOL_RECLAIM_MODE_STRIPE,
  COMPACT_CONTACT_POOL_RECLAIM_MODE_WRAP_FULL,
  COMPACT_CONTACT_POOL_RECLAIM_PERIOD,
  CompactContactPoolFatalBit,
  CompactContactPoolHeaderOffset,
  type CompactContactPoolLayout,
  advanceCompactContactPoolEpoch,
  planCompactContactPoolLayout,
} from './compactContactPool';
import type { PhysicsSubmissionLedger } from './physicsSubmissionLedger';
import {
  CONSTRAINT_SAFETY_CCD_FATAL_BIT,
  CONSTRAINT_SAFETY_CONTACT_FATAL_BIT,
  normalizeConstraintSafetyReadbackTimeoutMs,
} from './constraintSafety';

const WORKGROUP_SIZE = 256;
const INERTIAL_POSE_VEC4S_PER_BODY = 5;
const IGNORED_PAIR_HASH_STATE_EMPTY = 0;
const IGNORED_PAIR_HASH_STATE_OCCUPIED = 1;
const PERSISTENT_MANIFOLD_HEADER_WORDS = 4;
const PERSISTENT_MANIFOLD_POOL_ARRAY_COUNT = 4;
// WebGPU does not guarantee forward progress for workgroups that spin on
// another invocation.  Keep every open-addressing/CAS path strictly bounded;
// exceeding these generous limits becomes a visible capacity/protocol event
// instead of one command buffer monopolising the GPU indefinitely.
const PERSISTENT_HASH_MAX_PROBES = 128;
const PERSISTENT_HASH_MAX_RESTARTS = 8;
const PERSISTENT_CAS_MAX_ATTEMPTS = 32;
const CCD_DIAGNOSTIC_WORDS = 25;
// The high bit is transient metadata in pairActivity's active-candidate
// scratch only. Descriptor and candidate capacities are both bounded below
// 2^31, so every consumer can mask it without changing the persistent ABI.
const RETAINED_SLEEPING_PAIR_BIT = 0x8000_0000;
const ACTIVE_PAIR_VALUE_MASK = 0x7fff_ffff;
// The same local-only tag distinguishes fixed adjacency addresses from spill
// node ids while one manifold invocation writes its bulk reservation. Stored
// solver refs remain untagged physical contact rows.
const PAIR_BODY_SPILL_DESTINATION_BIT = 0x8000_0000;
const PAIR_BODY_DESTINATION_VALUE_MASK = 0x7fff_ffff;

interface CompactPairShaderTransformOptions {
  activeCandidateSlotsOffset: number;
  slotRowOffset: number;
  outputMaskOffset: number;
  freeRows0Offset: number;
  freeRows1Offset: number;
  poolKeyOffset: number;
  poolResumeEpochOffset: number;
  pairManifoldSlots: number;
  contactPoolCapacity: number;
}

function replaceRequired(source: string, needle: string, replacement: string, expected = 1): string {
  const count = source.split(needle).length - 1;
  if (count !== expected) {
    throw new Error(`Compact contact shader transform expected ${expected} occurrence(s), found ${count}: ${needle.slice(0, 80)}`);
  }
  return source.split(needle).join(replacement);
}

/**
 * Produces the production CCD shader graph. The diagnostic graph is kept as
 * the source of truth, then every counter binding/write/call is removed before
 * WGSL compilation. This gives production real CCD without a dummy storage
 * buffer, a clear dispatch, or contended global atomics in either hot kernel.
 */
function stripCcdDiagnosticInstrumentation(source: string): string {
  let stripped = source.replace(
    /^[ \t]*ccdCounters: ptr<storage, array<atomic<u32>>, read_write>,\r?\n/gm,
    '',
  );
  stripped = stripped.replace(
    /^[ \t]*ccdRecord(?:Prefilter|Speculative)Status\(ccdCounters,[^\r\n]*\);\r?\n/gm,
    '',
  );
  stripped = stripped.replace(
    /^[ \t]*atomicAdd\(&ccdCounters\[[^\r\n]*;\r?\n/gm,
    '',
  );
  if (/\bccdCounters\b/.test(stripped)) {
    throw new Error('Production CCD shader still contains diagnostic counter access.');
  }
  return stripped;
}

/**
 * Derives the compact-row DCD shader from the byte-for-byte legacy source.
 * Keeping this transform isolated makes the feature flag incapable of
 * changing the default legacy pipeline while still sharing the SAT/manifold
 * implementation.
 */
function buildCompactPairShaderSource(
  legacySource: string,
  options: CompactPairShaderTransformOptions,
): string {
  const {
    activeCandidateSlotsOffset,
    slotRowOffset,
    outputMaskOffset,
    freeRows0Offset,
    freeRows1Offset,
    poolKeyOffset,
    poolResumeEpochOffset,
    pairManifoldSlots,
    contactPoolCapacity,
  } = options;
  let source = replaceRequired(
    legacySource,
    'persistentManifoldMap: ptr<storage, array<u32>, read>,',
    'persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,',
  );
  source = replaceRequired(
    source,
    '        var manifold = gid;\n',
    '        var manifold = gid;\n        var retainedSleepingPair = false;\n',
  );
  source = replaceRequired(
    source,
    `          manifold = pairActivity[${activeCandidateSlotsOffset}u + gid + 1u];
          if (manifold >=`,
    `          let rawManifold = pairActivity[${activeCandidateSlotsOffset}u + gid + 1u];
          retainedSleepingPair = (rawManifold & ${RETAINED_SLEEPING_PAIR_BIT}u) != 0u;
          manifold = rawManifold & ${ACTIVE_PAIR_VALUE_MASK}u;
          if (manifold >=`,
  );
  source = replaceRequired(
    source,
    `let packedPair = persistentManifoldMap[${poolKeyOffset}u + manifold];`,
    `let packedPair = atomicLoad(&persistentManifoldMap[${poolKeyOffset}u + manifold]);`,
  );
  source = replaceRequired(
    source,
    `if (persistentManifoldMap[${poolResumeEpochOffset}u + manifold] == manifoldEpoch) {`,
    `if (atomicLoad(&persistentManifoldMap[${poolResumeEpochOffset}u + manifold]) == manifoldEpoch) {`,
  );
  source = replaceRequired(
    source,
    `          // Do not carry AVBD dual/penalty state across a real candidate-stream
          // interruption (or across reuse of a reclaimed pool slot).`,
    `          if (retainedSleepingPair) {
            let retainedMask = atomicLoad(
              &persistentManifoldMap[${outputMaskOffset}u + manifold]
            );
            let validMask = ${(2 ** pairManifoldSlots) - 1}u;
            var retainedRowsValid = retainedMask != 0u
              && (retainedMask & ~validMask) == 0u;
            let retainedLogicalBase = manifold * ${pairManifoldSlots}u;
            for (var retainedSlot = 0u; retainedSlot < ${pairManifoldSlots}u; retainedSlot++) {
              if ((retainedMask & (1u << retainedSlot)) == 0u) { continue; }
              let retainedRow = atomicLoad(
                &persistentManifoldMap[${slotRowOffset}u + retainedLogicalBase + retainedSlot]
              );
              if (retainedRow >= ${contactPoolCapacity}u) {
                retainedRowsValid = false;
                continue;
              }
              let retainedInfo = loadContactMeta(pairContacts, retainedRow);
              let retainedEncodedInfo = bitcast<u32>(retainedInfo.w);
              if (
                retainedInfo.z < 0.5
                || retainedInfo.x != f32(i)
                || retainedInfo.y != f32(j)
                || retainedInfo.z != f32(manifold + 1u)
                || contactInfoIsSpeculative(retainedEncodedInfo)
                || contactInfoLogicalSlot(retainedEncodedInfo) != retainedSlot
              ) {
                retainedRowsValid = false;
              }
            }
            if (retainedRowsValid) { return; }
            // This pair alone lost its reuse proof. Remove the transient tag
            // so ordinary DCD and CCD rebuild it conservatively this substep.
            pairActivity[${activeCandidateSlotsOffset}u + gid + 1u] = manifold;
          }

          // Do not carry AVBD dual/penalty state across a real candidate-stream
          // interruption (or across reuse of a reclaimed pool slot).`,
  );
  source = replaceRequired(
    source,
    'deactivateManifold(pairContacts, pairBase,',
    'deactivateCompactManifold(pairContacts, persistentManifoldMap, pairBase,',
    8,
  );
  // Every early-out deactivation needs the epoch to retire rows into the
  // producer list.
  source = replaceRequired(
    source,
    'deactivateCompactManifold(pairContacts, persistentManifoldMap, pairBase, f32(i), f32(j));',
    'deactivateCompactManifold(pairContacts, persistentManifoldMap, pairBase, f32(i), f32(j), manifoldEpoch);',
    7,
  );
  source = replaceRequired(
    source,
    'deactivateCompactManifold(pairContacts, persistentManifoldMap, pairBase, 0.0, 0.0);',
    'deactivateCompactManifold(pairContacts, persistentManifoldMap, pairBase, 0.0, 0.0, manifoldEpoch);',
  );
  source = replaceRequired(
    source,
    'let firstInfo = loadContactMeta(pairContacts, pairBase);',
    `var firstInfo = vec4f(f32(i), f32(j), 0.0, 0.0);
        let firstRow = compactContactRow(persistentManifoldMap, pairBase);
        if (firstRow < ${contactPoolCapacity}u) {
          firstInfo = loadContactMeta(pairContacts, firstRow);
        }`,
  );
  source = replaceRequired(
    source,
    `        var wasActive = false;
        var prevPairFeature: i32 = -1;
        var prevPairNormal = vec3f(0.0);
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let slot = pairBase + s;
          let slotInfo = loadContactMeta(pairContacts, slot);`,
    `        var wasActive = false;
        var prevPairFeature: i32 = -1;
        var prevPairNormal = vec3f(0.0);
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let slot = compactContactRow(persistentManifoldMap, pairBase + s);
          if (slot >= ${contactPoolCapacity}u) { continue; }
          let slotInfo = loadContactMeta(pairContacts, slot);`,
  );
  source = replaceRequired(
    source,
    `        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let slot = pairBase + s;
          let slotInfo = loadContactMeta(pairContacts, slot);
          let slotEncodedInfo = bitcast<u32>(slotInfo.w);`,
    `        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let slot = compactContactRow(persistentManifoldMap, pairBase + s);
          if (slot >= ${contactPoolCapacity}u) {
            prevSlotActive[s] = 0u;
            prevSlotMeta[s] = vec4f(0.0);
            prevSlotArmA[s] = vec4f(0.0);
            prevSlotArmB[s] = vec4f(0.0);
            continue;
          }
          let slotInfo = loadContactMeta(pairContacts, slot);
          let slotEncodedInfo = bitcast<u32>(slotInfo.w);`,
  );
  source = replaceRequired(
    source,
    'let matchedPrevContact = pairBase + matchedPrevSlot;',
    'let matchedPrevContact = compactContactRow(persistentManifoldMap, pairBase + matchedPrevSlot);',
    2,
  );
  source = replaceRequired(
    source,
    '        // Preserve exact matches in their previous slot. Unmatched contacts',
    `        // Resolve the complete logical-slot batch before writing any row.
        // Exact matches retain their row; unmatched candidates preferentially
        // reuse an unmatched old row, then allocate only genuinely missing rows.
        var candidateTargetSlot: array<u32, ${pairManifoldSlots}>;
        var reservedTargetMask = 0u;
        for (var c = 0u; c < candidateCount; c++) {
          var targetSlot = candidatePrevSlot[c];
          if (targetSlot == invalidSlot) {
            for (var slotCandidate = 0u; slotCandidate < ${pairManifoldSlots}u; slotCandidate++) {
              let slotBit = 1u << slotCandidate;
              if (prevSlotMatched[slotCandidate] == 0u && (reservedTargetMask & slotBit) == 0u) {
                targetSlot = slotCandidate;
                break;
              }
            }
          }
          candidateTargetSlot[c] = targetSlot;
          if (targetSlot != invalidSlot) {
            reservedTargetMask |= 1u << targetSlot;
          }
        }
        // This explicit missing-row batch is separate from geometry writes, so
        // allocator failure cannot alias a partially written contact record.
        for (var c = 0u; c < candidateCount; c++) {
          let targetSlot = candidateTargetSlot[c];
          if (targetSlot != invalidSlot) {
            let allocatedRow = ensureCompactContactRow(
              persistentManifoldMap,
              pairBase + targetSlot,
              manifoldEpoch,
            );
          }
        }

        // Preserve exact matches in their previous slot. Unmatched contacts`,
  );
  source = replaceRequired(
    source,
    `          var targetSlot = matchedPrevSlot;
          if (targetSlot == invalidSlot) {
            for (var slotCandidate = 0u; slotCandidate < ${pairManifoldSlots}u; slotCandidate++) {
              let slotBit = 1u << slotCandidate;
              if (prevSlotMatched[slotCandidate] == 0u && (outputSlotMask & slotBit) == 0u) {
                targetSlot = slotCandidate;
                break;
              }
            }
          }
          if (targetSlot == invalidSlot) { continue; }`,
    `          let targetSlot = candidateTargetSlot[c];
          if (targetSlot == invalidSlot) { continue; }`,
  );
  source = replaceRequired(
    source,
    'let targetIndex = pairBase + targetSlot;',
    `let targetIndex = ensureCompactContactRow(
            persistentManifoldMap,
            pairBase + targetSlot,
            manifoldEpoch,
          );
          if (targetIndex >= ${contactPoolCapacity}u) { continue; }`,
  );
  source = replaceRequired(
    source,
    'deactivateContactSlot(pairContacts, pairBase + s, f32(i), f32(j));',
    'releaseCompactContactSlot(pairContacts, persistentManifoldMap, pairBase + s, f32(i), f32(j), manifoldEpoch);',
  );
  source = replaceRequired(
    source,
    `        if (debugEnabled > 0u && isFloorPair) {
          if (writeCount == 0u) {`,
    `        atomicStore(
          &persistentManifoldMap[${outputMaskOffset}u + manifold],
          outputSlotMask,
        );

        if (debugEnabled > 0u && isFloorPair) {
          if (writeCount == 0u) {`,
  );

  const compactHelpers = /* wgsl */`
      fn compactContactRow(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        logicalSlot: u32,
      ) -> u32 {
        return atomicLoad(&persistentManifoldMap[${slotRowOffset}u + logicalSlot]);
      }

      fn ensureCompactContactRow(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        logicalSlot: u32,
        currentEpoch: u32,
      ) -> u32 {
        let address = ${slotRowOffset}u + logicalSlot;
        let existing = atomicLoad(&persistentManifoldMap[address]);
        if (existing < ${contactPoolCapacity}u) { return existing; }

        let parity = atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u]);
        let countAddress = select(
          ${CompactContactPoolHeaderOffset.freeCount0}u,
          ${CompactContactPoolHeaderOffset.freeCount1}u,
          parity != 0u,
        );
        let cursorAddress = select(
          ${CompactContactPoolHeaderOffset.freeCursor0}u,
          ${CompactContactPoolHeaderOffset.freeCursor1}u,
          parity != 0u,
        );
        let freeBase = select(${freeRows0Offset}u, ${freeRows1Offset}u, parity != 0u);
        let freeIndex = atomicAdd(&persistentManifoldMap[cursorAddress], 1u);
        let freeCount = min(atomicLoad(&persistentManifoldMap[countAddress]), ${contactPoolCapacity}u);
        var row = ${contactPoolCapacity}u;
        if (freeIndex < freeCount) {
          row = atomicLoad(&persistentManifoldMap[freeBase + freeIndex]);
          if (row >= ${contactPoolCapacity}u) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
              currentEpoch,
            );
            return ${contactPoolCapacity}u;
          }
        } else {
          row = atomicAdd(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowHighWater}u],
            1u,
          );
          if (row >= ${contactPoolCapacity}u) {
            atomicMin(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowHighWater}u],
              ${contactPoolCapacity}u,
            );
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.rowPoolOverflow}u,
              currentEpoch,
            );
            return ${contactPoolCapacity}u;
          }
        }
        var installed = false;
        var owner = ${contactPoolCapacity}u;
        for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
          let result = atomicCompareExchangeWeak(
            &persistentManifoldMap[address],
            ${contactPoolCapacity}u,
            row,
          );
          owner = result.old_value;
          if (result.exchanged) {
            installed = true;
            owner = row;
            break;
          }
          // A weak CAS may fail spuriously while the sentinel is unchanged.
          // Only a real competing owner is permission to return our row.
          if (owner != ${contactPoolCapacity}u) { break; }
        }
        if (!installed) {
          appendCompactFreeRow(persistentManifoldMap, row, currentEpoch);
          if (owner == ${contactPoolCapacity}u) {
            owner = atomicLoad(&persistentManifoldMap[address]);
          }
          if (owner >= ${contactPoolCapacity}u) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
              currentEpoch,
            );
          }
          return owner;
        }
        let live = atomicAdd(
          &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u],
          1u,
        ) + 1u;
        atomicMax(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowPeakLive}u], live);
        atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.newRowsThisStep}u], 1u);
        return row;
      }

      fn releaseCompactContactSlot(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        logicalSlot: u32,
        pairI: f32,
        pairJ: f32,
        currentEpoch: u32,
      ) {
        let address = ${slotRowOffset}u + logicalSlot;
        let row = atomicExchange(&persistentManifoldMap[address], ${contactPoolCapacity}u);
        if (row >= ${contactPoolCapacity}u) { return; }
        deactivateContactSlot(pairContacts, row, pairI, pairJ);
        appendCompactFreeRow(persistentManifoldMap, row, currentEpoch);
        let oldLive = atomicSub(
          &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u],
          1u,
        );
        if (oldLive == 0u) {
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u], 0u);
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
            currentEpoch,
          );
        }
        atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.retiredRowsThisStep}u], 1u);
      }

      fn deactivateCompactManifold(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        logicalBase: u32,
        pairI: f32,
        pairJ: f32,
        currentEpoch: u32,
      ) {
        for (var slot = 0u; slot < ${pairManifoldSlots}u; slot++) {
          releaseCompactContactSlot(
            pairContacts,
            persistentManifoldMap,
            logicalBase + slot,
            pairI,
            pairJ,
            currentEpoch,
          );
        }
        atomicStore(
          &persistentManifoldMap[${outputMaskOffset}u + logicalBase / ${pairManifoldSlots}u],
          0u,
        );
      }

      fn appendCompactFreeRow(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        row: u32,
        currentEpoch: u32,
      ) {
        let parity = atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u]);
        let countAddress = select(
          ${CompactContactPoolHeaderOffset.freeCount1}u,
          ${CompactContactPoolHeaderOffset.freeCount0}u,
          parity != 0u,
        );
        let freeBase = select(${freeRows1Offset}u, ${freeRows0Offset}u, parity != 0u);
        let writeIndex = atomicAdd(&persistentManifoldMap[countAddress], 1u);
        if (writeIndex < ${contactPoolCapacity}u) {
          atomicStore(&persistentManifoldMap[freeBase + writeIndex], row);
        } else {
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
            currentEpoch,
          );
        }
      }

      fn recordCompactFatal(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        bit: u32,
        currentEpoch: u32,
      ) {
        atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
        for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
          let result = atomicCompareExchangeWeak(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
            0u,
            currentEpoch,
          );
          if (result.exchanged || result.old_value != 0u) { break; }
        }
      }

`;
  source = replaceRequired(source, '      fn hashIgnoredPairKey(', `${compactHelpers}      fn hashIgnoredPairKey(`);
  return source;
}

function buildCompactCcdShaderSource(
  legacySource: string,
  options: CompactPairShaderTransformOptions,
): string {
  const {
    activeCandidateSlotsOffset,
    slotRowOffset,
    outputMaskOffset,
    freeRows0Offset,
    freeRows1Offset,
    poolKeyOffset,
    pairManifoldSlots,
    contactPoolCapacity,
  } = options;
  let source = replaceRequired(
    legacySource,
    'persistentManifoldMap: ptr<storage, array<u32>, read>,',
    'persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,',
  );
  source = replaceRequired(
    source,
    `            manifold = pairActivity[${activeCandidateSlotsOffset}u + gid + 1u];
            if (manifold >=`,
    `            let rawManifold = pairActivity[${activeCandidateSlotsOffset}u + gid + 1u];
            if ((rawManifold & ${RETAINED_SLEEPING_PAIR_BIT}u) != 0u) { return; }
            manifold = rawManifold & ${ACTIVE_PAIR_VALUE_MASK}u;
            if (manifold >=`,
  );
  source = replaceRequired(
    source,
    '          collisionMargin: f32,',
    '          currentEpoch: u32,\n          collisionMargin: f32,',
  );
  source = replaceRequired(
    source,
    `let packedPair = persistentManifoldMap[${poolKeyOffset}u + manifold];`,
    `let packedPair = atomicLoad(&persistentManifoldMap[${poolKeyOffset}u + manifold]);`,
  );
  source = replaceRequired(
    source,
    `          let pairBase = manifold * ${pairManifoldSlots}u;
          var dcdOwnsPair = false;
          for (var slotIndex = 0u; slotIndex < ${pairManifoldSlots}u; slotIndex++) {
            if (loadContactMeta(pairContacts, pairBase + slotIndex).z >= 0.5) {
              dcdOwnsPair = true;
              atomicAdd(&ccdCounters[12u], 1u);
              break;
            }
          }`,
    `          let pairBase = manifold * ${pairManifoldSlots}u;
          let dcdOutputMask = atomicLoad(
            &persistentManifoldMap[${outputMaskOffset}u + manifold]
          );
          let dcdOwnsPair = dcdOutputMask != 0u;
          if (dcdOwnsPair) {
            atomicAdd(&ccdCounters[12u], 1u);
          }`,
  );
  source = replaceRequired(
    source,
    '          let contactIndex = pairBase;',
    `          var contactIndex = ${contactPoolCapacity}u;
          if (dcdOwnsPair) {
            if ((dcdOutputMask & 1u) == 0u) { return; }
            contactIndex = atomicLoad(
              &persistentManifoldMap[${slotRowOffset}u + pairBase]
            );
          } else {
            contactIndex = ensureCompactCcdRow(
              persistentManifoldMap,
              pairBase,
              currentEpoch,
            );
          }
          if (contactIndex >= ${contactPoolCapacity}u) { return; }`,
  );
  source = replaceRequired(
    source,
    `          for (var slotIndex = 1u; slotIndex < ${pairManifoldSlots}u; slotIndex++) {
            let contact = pairBase + slotIndex;
            storeContactMeta(pairContacts, contact, vec4f(f32(i), f32(j), 0.0, 0.0));
            storeContactCacheWord(pairContacts, contact, 0u);
          }
          atomicAdd(&ccdCounters[3u], 1u);`,
    `          for (var slotIndex = 1u; slotIndex < ${pairManifoldSlots}u; slotIndex++) {
            releaseCompactCcdRow(
              pairContacts,
              persistentManifoldMap,
              pairBase + slotIndex,
              f32(i),
              f32(j),
              currentEpoch,
            );
          }
          atomicStore(&persistentManifoldMap[${outputMaskOffset}u + manifold], 1u);
          atomicAdd(&ccdCounters[3u], 1u);`,
  );

  const helpers = /* wgsl */`
        fn ensureCompactCcdRow(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          logicalSlot: u32,
          currentEpoch: u32,
        ) -> u32 {
          let address = ${slotRowOffset}u + logicalSlot;
          let existing = atomicLoad(&persistentManifoldMap[address]);
          if (existing < ${contactPoolCapacity}u) { return existing; }
          let parity = atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u]);
          let countAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            parity != 0u,
          );
          let cursorAddress = select(
            ${CompactContactPoolHeaderOffset.freeCursor0}u,
            ${CompactContactPoolHeaderOffset.freeCursor1}u,
            parity != 0u,
          );
          let freeBase = select(${freeRows0Offset}u, ${freeRows1Offset}u, parity != 0u);
          let freeIndex = atomicAdd(&persistentManifoldMap[cursorAddress], 1u);
          let freeCount = min(atomicLoad(&persistentManifoldMap[countAddress]), ${contactPoolCapacity}u);
          var row = ${contactPoolCapacity}u;
          if (freeIndex < freeCount) {
            row = atomicLoad(&persistentManifoldMap[freeBase + freeIndex]);
          } else {
            row = atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowHighWater}u], 1u);
          }
          if (row >= ${contactPoolCapacity}u) {
            atomicMin(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowHighWater}u],
              ${contactPoolCapacity}u,
            );
            recordCompactCcdFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.rowPoolOverflow}u,
              currentEpoch,
            );
            return ${contactPoolCapacity}u;
          }
          var installed = false;
          var owner = ${contactPoolCapacity}u;
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[address],
              ${contactPoolCapacity}u,
              row,
            );
            owner = result.old_value;
            if (result.exchanged) {
              installed = true;
              owner = row;
              break;
            }
            if (owner != ${contactPoolCapacity}u) { break; }
          }
          if (!installed) {
            appendCompactCcdFreeRow(persistentManifoldMap, row, currentEpoch);
            if (owner == ${contactPoolCapacity}u) {
              owner = atomicLoad(&persistentManifoldMap[address]);
            }
            if (owner >= ${contactPoolCapacity}u) {
              recordCompactCcdFatal(
                persistentManifoldMap,
                ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
                currentEpoch,
              );
            }
            return owner;
          }
          let live = atomicAdd(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u],
            1u,
          ) + 1u;
          atomicMax(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowPeakLive}u], live);
          atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.newRowsThisStep}u], 1u);
          return row;
        }

        fn releaseCompactCcdRow(
          pairContacts: ptr<storage, array<vec4f>, read_write>,
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          logicalSlot: u32,
          pairI: f32,
          pairJ: f32,
          currentEpoch: u32,
        ) {
          let address = ${slotRowOffset}u + logicalSlot;
          let row = atomicExchange(&persistentManifoldMap[address], ${contactPoolCapacity}u);
          if (row >= ${contactPoolCapacity}u) { return; }
          storeContactMeta(pairContacts, row, vec4f(pairI, pairJ, 0.0, 0.0));
          storeContactCacheWord(pairContacts, row, 0u);
          appendCompactCcdFreeRow(persistentManifoldMap, row, currentEpoch);
          let oldLive = atomicSub(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u],
            1u,
          );
          if (oldLive == 0u) {
            atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u], 0u);
            recordCompactCcdFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
              currentEpoch,
            );
          }
          atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.retiredRowsThisStep}u], 1u);
        }

        fn appendCompactCcdFreeRow(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          row: u32,
          currentEpoch: u32,
        ) {
          let parity = atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u]);
          let countAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            parity != 0u,
          );
          let freeBase = select(${freeRows1Offset}u, ${freeRows0Offset}u, parity != 0u);
          let writeIndex = atomicAdd(&persistentManifoldMap[countAddress], 1u);
          if (writeIndex < ${contactPoolCapacity}u) {
            atomicStore(&persistentManifoldMap[freeBase + writeIndex], row);
          } else {
            recordCompactCcdFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
        }

        fn recordCompactCcdFatal(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          bit: u32,
          currentEpoch: u32,
        ) {
          atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
              0u,
              currentEpoch,
            );
            if (result.exchanged || result.old_value != 0u) { break; }
          }
        }

`;
  source = replaceRequired(source, '        struct CcdSpecAnchorPair {', `${helpers}        struct CcdSpecAnchorPair {`);
  return source;
}

export interface CcdDiagnostics {
  enabled: boolean;
  candidateTests: number;
  candidateHits: number;
  speculativeTests: number;
  speculativeRows: number;
  failOpen: number;
  initialOverlapRejected: number;
  analyticRows: number;
  conservativeAdvancementRows: number;
  unsupported: number;
  nonFinite: number;
  iterationLimit: number;
  invalidInput: number;
  dcdActiveSkips: number;
  noClosingMisses: number;
  totalIterations: number;
  cheapMisses: number;
  prefilterCheapMisses: number;
  speculativeCheapMisses: number;
  prefilterCaTests: number;
  prefilterCaIterations: number;
  speculativeCaTests: number;
  speculativeCaIterations: number;
  activationSkips: number;
  prefilterActivationSkips: number;
  speculativeActivationSkips: number;
  /** Finite early rows emitted instead of silently accepting an unresolved sweep. */
  conservativeFallbacks: number;
  /** Speculative sweeps with no finite geometry from which any safe row could be built. */
  fatalFailOpen: number;
}

export interface ContactGenerationOptions {
  /** Legacy fixed rows remain the default until the engine opts in explicitly. */
  persistentMode?: 'legacy' | 'compact';
  /** Required in compact mode; this is the physical row count, not an epoch half. */
  compactContactPoolCapacity?: number;
  /** Optional canonical layout supplied by the capacity planner/integration layer. */
  compactContactPoolLayout?: CompactContactPoolLayout;
  /** Descriptor is stale after this many completely unseen epochs. Defaults to 1. */
  compactStaleEpochs?: number;
  /** Records only automatic telemetry submits; explicit public readbacks stay out-of-band. */
  submissionLedger?: PhysicsSubmissionLedger;
  /** Builds the instrumented CCD shader graph. Production defaults to no diagnostics. */
  enableCcdDiagnostics?: boolean;
  /** Publishes the compact live-manifold stream used by experimental AVBD capture. */
  enableActiveManifoldStream?: boolean;
  /** Shared scene-cumulative GPU gate. Broadphase writes bit 0 into the same word. */
  constraintSafetyGate?: StorageBufferAttribute;
  /** Caller-owned indirect dispatch used for fatal-state snapshot and rollback. */
  fatalBodyDispatch?: IndirectStorageBufferAttribute;
  /** Production defaults to the shared 10-second safety-readback watchdog. */
  safetyReadbackTimeoutMs?: number;
}

type ContactDetailReadbackPhase =
  | 'debug-pending'
  | 'debug-in-flight'
  | 'ccd-pending'
  | 'ccd-in-flight'
  | 'allocator-pending'
  | 'allocator-in-flight'
  | 'complete'
  | 'failed';

type ContactDetailAllocatorState = {
  fatalBits?: number;
  firstFatalEpoch?: number;
};

type PendingContactDetailReadback = {
  phase: ContactDetailReadbackPhase;
  readbackGeneration: number;
  frameId: number;
  bodyCount: number;
  pairDispatchCount: number;
  lastMaintenanceFrameId: number | null;
  debugRaw: ArrayBuffer | null;
  ccdFatalBits: number;
  compactState: ContactDetailAllocatorState | null;
  error: unknown;
};

const tangentBasisHelpers = wgsl(/* wgsl */`
      const TANGENT_BASIS_TWO_PI: f32 = 6.283185307179586;

      struct TangentBasis {
        t1: vec3f,
        t2: vec3f,
      };

      fn canonicalTangentBasis(n: vec3f) -> TangentBasis {
        let refAxis = select(vec3f(0.0, 1.0, 0.0), vec3f(1.0, 0.0, 0.0), abs(n.y) > 0.999);
        let t1Raw = cross(refAxis, n);
        let t1Len2 = dot(t1Raw, t1Raw);
        var t1 = vec3f(0.0, 0.0, 1.0);
        if (t1Len2 > 1e-12) {
          t1 = t1Raw * inverseSqrt(t1Len2);
        }
        let t2Raw = cross(n, t1);
        let t2Len2 = dot(t2Raw, t2Raw);
        var t2 = vec3f(1.0, 0.0, 0.0);
        if (t2Len2 > 1e-12) {
          t2 = t2Raw * inverseSqrt(t2Len2);
        }
        return TangentBasis(t1, t2);
      }

      fn tangentBasisFromPreferredT1(n: vec3f, preferredT1: vec3f) -> TangentBasis {
        let projected = preferredT1 - n * dot(preferredT1, n);
        let projectedLen2 = dot(projected, projected);
        if (projectedLen2 <= 1e-12) {
          return canonicalTangentBasis(n);
        }
        let t1 = projected * inverseSqrt(projectedLen2);
        let t2Raw = cross(n, t1);
        let t2Len2 = dot(t2Raw, t2Raw);
        if (t2Len2 <= 1e-12) {
          return canonicalTangentBasis(n);
        }
        let t2 = t2Raw * inverseSqrt(t2Len2);
        return TangentBasis(t1, t2);
      }

      fn tangentBasisFromAngle(n: vec3f, theta: f32) -> TangentBasis {
        let canonical = canonicalTangentBasis(n);
        let c = cos(theta);
        let s = sin(theta);
        let t1 = canonical.t1 * c + canonical.t2 * s;
        return tangentBasisFromPreferredT1(n, t1);
      }

      fn tangentBasisAngleFromT1(n: vec3f, t1: vec3f) -> f32 {
        let canonical = canonicalTangentBasis(n);
        var theta = atan2(dot(t1, canonical.t2), dot(t1, canonical.t1));
        if (theta < 0.0) {
          theta += TANGENT_BASIS_TWO_PI;
        }
        return theta;
      }
`);

export class ContactGenerationStage {
  private readonly device: GPUDevice;
  private pairKernel: any;
  private pairKernelDebug: any;
  private clearPairBodyCountsKernel: any;
  private clearDebugCountersKernel: any;
  private clearActiveCandidateSlotsKernel: any;
  private buildActiveCandidateSlotsKernel: any;
  private buildActiveCandidateSlotsCcdKernel?: any;
  private clearCcdCountersKernel?: any;
  private speculativeContactKernel?: any;
  private countPersistentCacheMissesKernel: any;
  private clearPersistentCacheKernel: any;
  private resolvePersistentManifoldsKernel: any;
  private buildPairDispatchArgsKernel: any;
  private buildPairBodyListsKernel: any;
  private buildPairBodyListsKernelDebug: any;
  private finalizeDebugCountersKernel: any;
  private finalizeConstraintSafetyKernel: any;
  private initializeCompactPoolKernel?: any;
  private beginCompactEpochKernel?: any;
  private reclaimCompactStaleKernel?: any;
  private buildCompactFinalizeArgsKernel?: any;
  private finalizeCompactFreeRowsKernel?: any;
  private candidateDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private pairDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private persistentProbeIndirectAttr: IndirectStorageBufferAttribute;
  private persistentResetIndirectAttr: IndirectStorageBufferAttribute;
  private compactReclaimIndirectAttr?: IndirectStorageBufferAttribute;
  private compactFinalizeIndirectAttr?: IndirectStorageBufferAttribute;
  private persistentMissCountAttr: StorageBufferAttribute;
  private readonly activeManifoldsAttr: StorageBufferAttribute;
  private readonly debugCountersAttr: StorageBufferAttribute;
  private readonly persistentManifoldMapAttr: StorageBufferAttribute;
  private readonly pairManifoldSlots: number;
  private readonly ccdEnabled: boolean;
  private readonly ccdDiagnosticsEnabled: boolean;
  private readonly compactMode: boolean;
  private readonly activeManifoldStreamEnabled: boolean;
  private readonly compactLayout?: CompactContactPoolLayout;
  private readonly compactContactPoolCapacity: number;
  private readonly compactInitializeDispatch?: readonly [number, number, number];
  private readonly maxPersistentManifolds: number;
  private readonly submissionLedger?: PhysicsSubmissionLedger;
  private readonly ccdCountersAttr?: StorageBufferAttribute;
  /** Sticky production correctness latch: ORs the unresolved CcdResult status. */
  private readonly ccdFatalStateAttr: StorageBufferAttribute;
  private readonly constraintSafetyGateAttr: StorageBufferAttribute;
  private readonly fatalBodyDispatchAttr: IndirectStorageBufferAttribute;
  private readonly safetyReadbackTimeoutMs: number;
  private floorDebugBody = 0xffffffff;
  private debugEnabled = false;
  private debugReadbackInFlight = false;
  private pendingDetailReadback: PendingContactDetailReadback | null = null;
  private lastDebugLogFrame = -1;
  private debugEveryNFrames = 30;
  private capacityOverflowDetected = false;
  /** Sticky contact/CCD bits observed from either safety-readback path. */
  private latchedConstraintSafetyBits = 0;
  /** Failure of this stage's standalone gate map, not a contact overflow. */
  private standaloneSafetyReadbackInfrastructureFailure = false;
  private safetyReadbackFailureWarned = false;
  private safetyReadbackFailureForTesting: 'sync' | 'reject' | 'timeout' | null = null;
  private disposed = false;
  private manifoldEpoch = 0;
  private resetGeneration = 0;
  private compactInitialized = false;
  private compactFinalizedEpoch = 0;
  private ccdFatalStateSubmitted = false;

  constructor(
    device: GPUDevice,
    positions: StorageBufferAttribute,
    quaternions: StorageBufferAttribute,
    shapes: StorageBufferAttribute,
    pairContacts: StorageBufferAttribute,
    pairActivity: StorageBufferAttribute,
    pairBodyContactCounts: StorageBufferAttribute,
    pairBodyContactIndices: StorageBufferAttribute,
    maxBodies: number,
    maxPairContacts: number,
    pairManifoldSlots: number,
    maxPairContactsPerBody: number,
    maxActivePairContacts: number,
    pairCandidateIndicesOffset: number,
    pairActiveCandidateSlotsOffset: number,
    pairActiveContactsOffset: number,
    ignoredPairHash: StorageBufferAttribute,
    ignoredPairHashCapacity: number,
    persistentManifoldMap: StorageBufferAttribute,
    persistentManifoldHashCapacity: number,
    maxPersistentManifolds: number,
    pairActivityWordCount: number,
    initialPose: StorageBufferAttribute,
    inertialPose: StorageBufferAttribute,
    enableCcd = true,
    options: ContactGenerationOptions = {},
  ) {
    this.device = device;
    this.pairManifoldSlots = pairManifoldSlots;
    this.persistentManifoldMapAttr = persistentManifoldMap;
    this.ccdEnabled = enableCcd;
    this.ccdDiagnosticsEnabled = enableCcd && options.enableCcdDiagnostics === true;
    this.compactMode = options.persistentMode === 'compact';
    this.activeManifoldStreamEnabled = this.compactMode
      && options.enableActiveManifoldStream === true;
    this.compactContactPoolCapacity = this.compactMode
      ? Math.floor(options.compactContactPoolCapacity ?? maxPairContacts)
      : maxPairContacts;
    this.maxPersistentManifolds = maxPersistentManifolds;
    this.submissionLedger = options.submissionLedger;
    this.safetyReadbackTimeoutMs = normalizeConstraintSafetyReadbackTimeoutMs(
      options.safetyReadbackTimeoutMs,
    );
    this.constraintSafetyGateAttr = options.constraintSafetyGate
      ?? new StorageBufferAttribute(new Uint32Array(2), 1);
    this.constraintSafetyGateAttr.name = 'AVBD Constraint Safety Gate';
    this.fatalBodyDispatchAttr = options.fatalBodyDispatch
      ?? new IndirectStorageBufferAttribute(new Uint32Array([0, 1, 1]), 1);
    this.fatalBodyDispatchAttr.name = 'AVBD Fatal Body Rollback Dispatch Indirect';
    const maxPairBodyContacts = maxBodies * maxPairContactsPerBody;
    // pairBodyContactIndices is a hybrid adjacency store:
    //   fixed[maxBodies * fastDegree], spillCounter,
    //   spillHead[maxBodies], spillNodes[capacity] = { contact, next }.
    // The fixed region remains the common fast path while rare high-degree
    // bodies borrow from one compact global pool.
    const pairBodyOverflowCounterOffset = maxPairBodyContacts;
    const pairBodyOverflowHeadOffset = pairBodyOverflowCounterOffset + 1;
    const pairBodyOverflowNodeOffset = pairBodyOverflowHeadOffset + maxBodies;
    const pairBodyOverflowCapacity = Math.floor(
      (pairBodyContactIndices.count - pairBodyOverflowNodeOffset) / 2,
    );
    if (pairBodyOverflowCapacity < 1) {
      throw new Error('Pair-body contact spill pool is missing.');
    }
    if (pairBodyContactIndices.count >= PAIR_BODY_SPILL_DESTINATION_BIT) {
      throw new RangeError('Tagged pair-body destinations require adjacency storage below 2^31 words.');
    }
    const maxPairDispatchPairs = this.compactMode
      ? maxPersistentManifolds
      : Math.floor(maxPairContacts / pairManifoldSlots);
    if (
      maxPersistentManifolds >= RETAINED_SLEEPING_PAIR_BIT
      || maxPairDispatchPairs >= RETAINED_SLEEPING_PAIR_BIT
    ) {
      throw new RangeError('Retained sleeping-pair marker requires contact capacities below 2^31.');
    }
    const canonicalCompactLayout = this.compactMode
      ? planCompactContactPoolLayout({
        persistentManifoldHashCapacity,
        maxPersistentManifolds,
        pairManifoldSlots,
        compactContactPoolCapacity: this.compactContactPoolCapacity,
      })
      : undefined;
    if (this.compactMode && maxPairContacts !== this.compactContactPoolCapacity) {
      throw new Error(
        `Compact contact storage rows (${maxPairContacts}) must equal compactContactPoolCapacity (${this.compactContactPoolCapacity}).`,
      );
    }
    if (this.compactMode && maxActivePairContacts !== this.compactContactPoolCapacity) {
      throw new Error(
        `Compact active-contact capacity (${maxActivePairContacts}) must equal compactContactPoolCapacity (${this.compactContactPoolCapacity}).`,
      );
    }
    if (this.compactMode && options.compactContactPoolLayout) {
      const rangeKeys = [
        'header',
        'hashKey',
        'hashIndex',
        'poolKey',
        'poolLastSeen',
        'poolResumeEpoch',
        'slotRow',
        'outputMask',
        'freeManifolds',
        'freeRows0',
        'freeRows1',
      ] as const;
      const layoutMatches = options.compactContactPoolLayout.totalWords
        === canonicalCompactLayout!.totalWords
        && rangeKeys.every((key) => (
          options.compactContactPoolLayout![key].offset === canonicalCompactLayout![key].offset
          && options.compactContactPoolLayout![key].length === canonicalCompactLayout![key].length
        ));
      if (!layoutMatches) {
        throw new Error('compactContactPoolLayout does not match the canonical capacity/layout inputs.');
      }
    }
    this.compactLayout = options.compactContactPoolLayout ?? canonicalCompactLayout;
    if (this.compactMode) {
      const totalInitializeWorkgroups = Math.ceil(
        this.compactLayout!.totalWords / WORKGROUP_SIZE,
      );
      const workgroupLimit = Math.max(
        1,
        Math.floor(Number(device.limits.maxComputeWorkgroupsPerDimension ?? 65_535)),
      );
      const workgroupsX = Math.min(totalInitializeWorkgroups, workgroupLimit);
      const workgroupsY = Math.ceil(totalInitializeWorkgroups / workgroupsX);
      if (workgroupsY > workgroupLimit) {
        throw new Error(
          `Compact persistent-map initialization requires ${workgroupsX}x${workgroupsY} `
          + `workgroups, exceeding maxComputeWorkgroupsPerDimension=${workgroupLimit}.`,
        );
      }
      this.compactInitializeDispatch = Object.freeze([
        workgroupsX,
        workgroupsY,
        1,
      ] as const);
    }
    const persistentHashKeyOffset = this.compactMode
      ? this.compactLayout!.hashKey.offset
      : PERSISTENT_MANIFOLD_HEADER_WORDS;
    const persistentHashIndexOffset = persistentHashKeyOffset + persistentManifoldHashCapacity;
    const persistentPoolKeyOffset = this.compactMode
      ? this.compactLayout!.poolKey.offset
      : persistentHashIndexOffset + persistentManifoldHashCapacity;
    const persistentPoolLastSeenOffset = this.compactMode
      ? this.compactLayout!.poolLastSeen.offset
      : persistentPoolKeyOffset + maxPersistentManifolds;
    const persistentPoolHashSlotOffset = this.compactMode
      ? 0
      : persistentPoolLastSeenOffset + maxPersistentManifolds;
    const persistentPoolResumeEpochOffset = this.compactMode
      ? this.compactLayout!.poolResumeEpoch.offset
      : persistentPoolHashSlotOffset + maxPersistentManifolds;
    const persistentSlotRowOffset = this.compactMode ? this.compactLayout!.slotRow.offset : 0;
    const persistentOutputMaskOffset = this.compactMode ? this.compactLayout!.outputMask.offset : 0;
    const compactFreeManifoldsOffset = this.compactMode ? this.compactLayout!.freeManifolds.offset : 0;
    const compactFreeRows0Offset = this.compactMode ? this.compactLayout!.freeRows0.offset : 0;
    const compactFreeRows1Offset = this.compactMode ? this.compactLayout!.freeRows1.offset : 0;
    const requestedCompactStaleEpochs = options.compactStaleEpochs ?? 1;
    if (!Number.isSafeInteger(requestedCompactStaleEpochs) || requestedCompactStaleEpochs < 1) {
      throw new Error('compactStaleEpochs must be a positive safe integer.');
    }
    const compactStaleEpochs = requestedCompactStaleEpochs;
    const expectedPersistentMapWords = this.compactMode
      ? this.compactLayout!.totalWords
      : PERSISTENT_MANIFOLD_HEADER_WORDS
        + persistentManifoldHashCapacity * 2
        + maxPersistentManifolds * PERSISTENT_MANIFOLD_POOL_ARRAY_COUNT;
    if (persistentManifoldMap.count < expectedPersistentMapWords) {
      throw new Error(
        `Persistent manifold map is too small (${persistentManifoldMap.count} < ${expectedPersistentMapWords}).`,
      );
    }
    this.candidateDispatchIndirectAttr = new IndirectStorageBufferAttribute(new Uint32Array([0, 1, 1]), 1);
    this.candidateDispatchIndirectAttr.name = 'Contact Candidate Prefilter Dispatch Indirect';
    this.pairDispatchIndirectAttr = new IndirectStorageBufferAttribute(new Uint32Array([0, 1, 1]), 1);
    this.pairDispatchIndirectAttr.name = 'Contact Pair Dispatch Indirect';
    this.persistentProbeIndirectAttr = new IndirectStorageBufferAttribute(new Uint32Array([0, 1, 1]), 1);
    this.persistentProbeIndirectAttr.name = 'Contact Persistent Pressure Probe Indirect';
    this.persistentResetIndirectAttr = new IndirectStorageBufferAttribute(new Uint32Array([0, 1, 1]), 1);
    this.persistentResetIndirectAttr.name = 'Contact Persistent Cache Reset Indirect';
    if (this.compactMode) {
      this.compactReclaimIndirectAttr = new IndirectStorageBufferAttribute(
        new Uint32Array([0, 1, 1]),
        1,
      );
      this.compactReclaimIndirectAttr.name = 'Contact Compact Sparse Reclaim Indirect';
      this.compactFinalizeIndirectAttr = new IndirectStorageBufferAttribute(
        new Uint32Array([0, 1, 1, 0]),
        1,
      );
      this.compactFinalizeIndirectAttr.name = 'Contact Compact Free Carry Indirect';
    }
    this.persistentMissCountAttr = new StorageBufferAttribute(new Uint32Array(1), 1);
    this.persistentMissCountAttr.name = 'Contact Persistent Cache Miss Count';
    // Compact contact generation already visits one invocation per persistent
    // manifold while building body adjacency. Publish that exact live set once
    // so the fixed AVBD dual capture can work per manifold instead of reloading
    // both endpoint poses independently for every physical contact row.
    this.activeManifoldsAttr = new StorageBufferAttribute(
      new Uint32Array((this.activeManifoldStreamEnabled ? maxPersistentManifolds : 0) + 1),
      1,
    );
    this.activeManifoldsAttr.name = 'Contact Active Manifolds';
    if (this.ccdDiagnosticsEnabled) {
      this.ccdCountersAttr = new StorageBufferAttribute(new Uint32Array(CCD_DIAGNOSTIC_WORDS), 1);
      this.ccdCountersAttr.name = 'Contact CCD Diagnostics';
    }
    this.ccdFatalStateAttr = new StorageBufferAttribute(new Uint32Array(1), 1);
    this.ccdFatalStateAttr.name = 'Contact CCD Fatal State';
    // debugCounters layout:
    // 0 = warmstartResets (slot identity mismatch)
    // 1 = activeManifolds
    // 2 = activeCandidateSlots
    // 3 = stalePrunedSlots
    // 4 = separatingKeptSlots
    // 5 = stickingAnchorReuses
    // 6 = sharedTangentSignFlips
    // 7 = floorPairCandidates
    // 8 = floorPairWarmstartResets
    // 9 = floorPairKeepRejected
    // 10 = floorPairDegenerateFallbacks
    // 11 = floorPairWriteZero
    // 12 = floorPairActive
    // 13 = floorPairSeparatedRejects
    // 14 = floorPairInactiveThresholdRejects
    // 15 = floorPairHysteresisRejects
    // 16..18 = floorPairSeparatedFaceA{0,1,2}
    // 19..21 = floorPairSeparatedFaceB{0,1,2}
    // 22 = floorPairSeparatedEdge
    // 23 = floorPairSeparatedOther
    // 24 = pairBodyListOverflowWrites
    // 25 = pairBodyListDroppedContacts
    // 26 = activeContactListOverflowWrites
    // 27 = persistentManifoldPoolOverflowWrites (sticky correctness latch)
    // 28 = persistentManifoldPoolReclaims (generation resets; sticky diagnostic)
    // 29 = persistentManifoldHashProtocolFailures (sticky diagnostic)
    this.debugCountersAttr = new StorageBufferAttribute(new Uint32Array(30), 1);

    if (this.compactMode) {
      const layout = this.compactLayout!;
      const capacity = this.compactContactPoolCapacity;
      // Keep one complete incremental-reclaim stripe period as allocation
      // headroom before escalating to an O(high-water) full sweep. The old
      // fixed 75% trigger made the stable 64K production scene (77.7% rows,
      // essentially zero churn) scan every descriptor every timestep even
      // though the normal eight-phase schedule had more than 200K free rows.
      // At 7/8 occupancy the full sweep still begins with one stripe-period
      // of reserve, while steady pools stay on bounded H/8 maintenance.
      const pressureRowThreshold = Math.ceil(
        capacity * (COMPACT_CONTACT_POOL_RECLAIM_PERIOD - 1)
          / COMPACT_CONTACT_POOL_RECLAIM_PERIOD,
      );
      const pressureManifoldThreshold = Math.ceil(
        maxPersistentManifolds * (COMPACT_CONTACT_POOL_RECLAIM_PERIOD - 1)
          / COMPACT_CONTACT_POOL_RECLAIM_PERIOD,
      );
      const initializeCompactPoolShader = wgslFn(/* wgsl */`
        fn compute(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          workgroupId: vec3u,
          localId: vec3u,
        ) -> void {
          let linearWorkgroup = workgroupId.y * ${this.compactInitializeDispatch![0]}u
            + workgroupId.x;
          let gid = linearWorkgroup * ${WORKGROUP_SIZE}u + localId.x;
          if (gid >= ${layout.totalWords}u) { return; }
          var value = 0u;
          if (gid >= ${layout.slotRow.offset}u && gid < ${layout.slotRow.end}u) {
            value = ${capacity}u;
          } else if (gid == ${CompactContactPoolHeaderOffset.generationMode}u) {
            value = ${COMPACT_CONTACT_POOL_GENERATION_MODE}u;
          }
          atomicStore(&persistentManifoldMap[gid], value);
        }
      `);
      this.initializeCompactPoolKernel = initializeCompactPoolShader({
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        workgroupId,
        localId,
      }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Compact Initialize Pool');
      assertStorageBufferBudget('Contact Compact Initialize Pool', 1);

      const beginCompactEpochShader = wgslFn(/* wgsl */`
        fn compute(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          reclaimIndirect: ptr<storage, array<u32>, read_write>,
          currentEpoch: u32,
          epochWrapped: u32,
        ) -> void {
          // Toggle the physical free-list generation every true step. This is
          // intentionally independent of the non-zero epoch value: when u32
          // epoch zero is skipped at wrap, currentEpoch parity repeats once.
          let consumeParity = (
            atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u])
            ^ 1u
          ) & 1u;
          let consumeCountAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            consumeParity != 0u,
          );
          let consumeCursorAddress = select(
            ${CompactContactPoolHeaderOffset.freeCursor0}u,
            ${CompactContactPoolHeaderOffset.freeCursor1}u,
            consumeParity != 0u,
          );
          let produceCountAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            consumeParity != 0u,
          );
          let produceCursorAddress = select(
            ${CompactContactPoolHeaderOffset.freeCursor1}u,
            ${CompactContactPoolHeaderOffset.freeCursor0}u,
            consumeParity != 0u,
          );
          let live = atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u]);
          let highWater = atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowHighWater}u]);
          let freeCount = atomicLoad(&persistentManifoldMap[consumeCountAddress]);
          if (live > ${capacity}u || highWater > ${capacity}u || freeCount > ${capacity}u || live + freeCount != highWater) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
          let manifoldLive = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.allocatedManifolds}u],
          );
          let manifoldHighWater = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldHighWater}u],
          );
          let manifoldFreeHead = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeHead}u],
          );
          let manifoldFreeTail = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeTail}u],
          );
          let manifoldFreeCount = select(
            0u,
            manifoldFreeTail - manifoldFreeHead,
            manifoldFreeTail >= manifoldFreeHead,
          );
          if (
            manifoldLive > ${maxPersistentManifolds}u
            || manifoldHighWater > ${maxPersistentManifolds}u
            || manifoldFreeTail < manifoldFreeHead
            || manifoldFreeCount > ${maxPersistentManifolds}u
            || manifoldLive + manifoldFreeCount != manifoldHighWater
          ) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u], consumeParity);
          atomicStore(&persistentManifoldMap[consumeCursorAddress], 0u);
          atomicStore(&persistentManifoldMap[produceCountAddress], 0u);
          atomicStore(&persistentManifoldMap[produceCursorAddress], 0u);
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.newRowsThisStep}u], 0u);
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.retiredRowsThisStep}u], 0u);
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimedManifoldsThisStep}u], 0u);
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimedRowsThisStep}u], 0u);
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.newManifoldsThisStep}u], 0u);
          atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.pressureFullSweepsThisStep}u], 0u);
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.preReclaimAvailableManifolds}u],
            ${maxPersistentManifolds}u - min(manifoldLive, ${maxPersistentManifolds}u),
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.preReclaimAvailableRows}u],
            ${capacity}u - min(live, ${capacity}u),
          );

          // Normal maintenance visits exactly one descriptor residue per
          // epoch. Across N=8 epochs every index below the stable high-water
          // is examined once, without publishing or scanning an O(live) list.
          let reclaimPhase = currentEpoch & ${COMPACT_CONTACT_POOL_RECLAIM_PERIOD - 1}u;
          var stripeThreads = 0u;
          if (manifoldHighWater > reclaimPhase) {
            stripeThreads = (
              manifoldHighWater - reclaimPhase + ${COMPACT_CONTACT_POOL_RECLAIM_PERIOD - 1}u
            ) / ${COMPACT_CONTACT_POOL_RECLAIM_PERIOD}u;
          }
          let pressureFull = live >= ${pressureRowThreshold}u
            || manifoldLive >= ${pressureManifoldThreshold}u;
          let wrapFull = epochWrapped != 0u;
          var reclaimMode = select(
            ${COMPACT_CONTACT_POOL_RECLAIM_MODE_STRIPE}u,
            ${COMPACT_CONTACT_POOL_RECLAIM_MODE_PRESSURE_FULL}u,
            pressureFull,
          );
          if (wrapFull) {
            reclaimMode = ${COMPACT_CONTACT_POOL_RECLAIM_MODE_WRAP_FULL}u;
          }
          // A pressure epoch runs one full stable-H dispatch in place of the
          // stripe; epoch wrap likewise substitutes one full sweep so skipping
          // reserved epoch zero cannot strand residue zero for another period.
          let fullSweep = pressureFull || wrapFull;
          let reclaimThreads = select(stripeThreads, manifoldHighWater, fullSweep);
          let reclaimWorkgroups = (
            reclaimThreads + ${WORKGROUP_SIZE - 1}u
          ) / ${WORKGROUP_SIZE}u;
          reclaimIndirect[0u] = reclaimWorkgroups;
          reclaimIndirect[1u] = 1u;
          reclaimIndirect[2u] = 1u;
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimCandidateThreads}u],
            reclaimThreads,
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimWorkgroups}u],
            reclaimWorkgroups,
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimMode}u],
            reclaimMode,
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimPhase}u],
            reclaimPhase,
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimPeriod}u],
            ${COMPACT_CONTACT_POOL_RECLAIM_PERIOD}u,
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.pressureFullSweepsThisStep}u],
            select(0u, 1u, pressureFull),
          );
        }

        fn recordCompactFatal(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          bit: u32,
          currentEpoch: u32,
        ) {
          atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
              0u,
              currentEpoch,
            );
            if (result.exchanged || result.old_value != 0u) { break; }
          }
        }
      `);
      this.beginCompactEpochKernel = beginCompactEpochShader({
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        reclaimIndirect: storage(this.compactReclaimIndirectAttr!, 'uint', 3),
        currentEpoch: uniform(0),
        epochWrapped: uniform(0),
      }).computeKernel([1, 1, 1]).setName('Contact Compact Begin Epoch');
      assertStorageBufferBudget('Contact Compact Begin Epoch', 2);

      const reclaimCompactStaleShader = wgslFn(/* wgsl */`
        fn compute(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
          currentEpoch: u32,
          workgroupId: vec3u,
          localId: vec3u,
        ) -> void {
          let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
          let reclaimMode = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimMode}u],
          );
          let reclaimPhase = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimPhase}u],
          );
          let poolIndex = select(
            reclaimPhase + gid * ${COMPACT_CONTACT_POOL_RECLAIM_PERIOD}u,
            gid,
            reclaimMode != ${COMPACT_CONTACT_POOL_RECLAIM_MODE_STRIPE}u,
          );
          let stableHighWater = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldHighWater}u],
          );
          if (poolIndex >= stableHighWater || poolIndex >= ${maxPersistentManifolds}u) { return; }
          let keyAddress = ${persistentPoolKeyOffset}u + poolIndex;
          let packedPair = atomicLoad(&persistentManifoldMap[keyAddress]);
          if (packedPair == 0u || packedPair == 0xffffffffu) { return; }
          let lastSeenAddress = ${persistentPoolLastSeenOffset}u + poolIndex;
          let lastSeen = atomicLoad(&persistentManifoldMap[lastSeenAddress]);
          if (
            lastSeen == 0u
            || compactEpochAge(currentEpoch, lastSeen) <= ${compactStaleEpochs}u
          ) { return; }
          // A stripe/full sweep owns each pool index at most once, and resolve
          // runs in a later dispatch. Atomic exchange therefore claims without
          // weak-CAS spurious failure or a lost stale candidate.
          let claimedLastSeen = atomicExchange(
            &persistentManifoldMap[lastSeenAddress],
            0u,
          );
          if (claimedLastSeen != lastSeen) {
            if (claimedLastSeen != 0u) {
              atomicStore(&persistentManifoldMap[lastSeenAddress], claimedLastSeen);
              recordCompactFatal(
                persistentManifoldMap,
                ${CompactContactPoolFatalBit.persistentHashProtocolFailure}u,
                currentEpoch,
              );
              atomicAdd(&debugCounters[29u], 1u);
            }
            return;
          }

          let mask = ${persistentManifoldHashCapacity - 1}u;
          var hashSlot = hashPairKey(packedPair) & mask;
          var removed = false;
          for (var probe = 0u; probe < ${PERSISTENT_HASH_MAX_PROBES}u; probe++) {
            let hashKeyAddress = ${persistentHashKeyOffset}u + hashSlot;
            let hashKey = atomicLoad(&persistentManifoldMap[hashKeyAddress]);
            if (hashKey == packedPair) {
              let indexAddress = ${persistentHashIndexOffset}u + hashSlot;
              if (atomicLoad(&persistentManifoldMap[indexAddress]) == poolIndex + 1u) {
                atomicStore(&persistentManifoldMap[indexAddress], 0u);
                atomicStore(&persistentManifoldMap[hashKeyAddress], 0xffffffffu);
                removed = true;
              }
              break;
            }
            if (hashKey == 0u) { break; }
            hashSlot = (hashSlot + 1u) & mask;
          }
          if (!removed) {
            atomicStore(&persistentManifoldMap[lastSeenAddress], lastSeen);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.persistentHashProtocolFailure}u,
              currentEpoch,
            );
            atomicAdd(&debugCounters[29u], 1u);
            return;
          }

          atomicStore(&persistentManifoldMap[${persistentOutputMaskOffset}u + poolIndex], 0u);
          let logicalBase = poolIndex * ${pairManifoldSlots}u;
          for (var slot = 0u; slot < ${pairManifoldSlots}u; slot++) {
            let logicalAddress = ${persistentSlotRowOffset}u + logicalBase + slot;
            let row = atomicExchange(&persistentManifoldMap[logicalAddress], ${capacity}u);
            if (row < ${capacity}u) {
              retireCompactRow(persistentManifoldMap, row, currentEpoch);
            }
          }
          atomicStore(&persistentManifoldMap[keyAddress], 0u);
          atomicStore(&persistentManifoldMap[${persistentPoolResumeEpochOffset}u + poolIndex], 0u);
          let oldAllocated = atomicSub(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.allocatedManifolds}u],
            1u,
          );
          if (oldAllocated == 0u) {
            atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.allocatedManifolds}u], 0u);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
              currentEpoch,
            );
          }

          // Reclaim is a producer-only dispatch and resolve is the later
          // consumer-only dispatch. A monotonic ring tail therefore needs no
          // contended CAS and makes reclaimed descriptors reusable this epoch.
          let freeHead = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeHead}u],
          );
          let freeTail = atomicAdd(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeTail}u],
            1u,
          );
          if (
            freeTail >= freeHead
            && freeTail - freeHead < ${maxPersistentManifolds}u
          ) {
            atomicStore(
              &persistentManifoldMap[${compactFreeManifoldsOffset}u + freeTail % ${maxPersistentManifolds}u],
              poolIndex,
            );
          } else {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
          atomicAdd(&debugCounters[28u], 1u);
          atomicAdd(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimedManifoldsThisStep}u],
            1u,
          );
        }

        fn retireCompactRow(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          row: u32,
          currentEpoch: u32,
        ) {
          let consumeParity = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u],
          );
          let countAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            consumeParity != 0u,
          );
          let freeBase = select(${compactFreeRows1Offset}u, ${compactFreeRows0Offset}u, consumeParity != 0u);
          let writeIndex = atomicAdd(&persistentManifoldMap[countAddress], 1u);
          if (writeIndex < ${capacity}u) {
            atomicStore(&persistentManifoldMap[freeBase + writeIndex], row);
          } else {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
          let oldLive = atomicSub(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u], 1u);
          if (oldLive == 0u) {
            atomicStore(&persistentManifoldMap[${CompactContactPoolHeaderOffset.rowLive}u], 0u);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
              currentEpoch,
            );
          }
          atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.retiredRowsThisStep}u], 1u);
          atomicAdd(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.reclaimedRowsThisStep}u],
            1u,
          );
        }

        fn recordCompactFatal(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          bit: u32,
          currentEpoch: u32,
        ) {
          atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
              0u,
              currentEpoch,
            );
            if (result.exchanged || result.old_value != 0u) { break; }
          }
        }

        fn hashPairKey(key: u32) -> u32 {
          var value = key;
          value ^= value >> 16u;
          value *= 0x7feb352du;
          value ^= value >> 15u;
          value *= 0x846ca68bu;
          return value ^ (value >> 16u);
        }

        fn compactEpochAge(currentEpoch: u32, previousEpoch: u32) -> u32 {
          return select(
            (${COMPACT_CONTACT_POOL_EPOCH_MAX}u - previousEpoch) + currentEpoch,
            currentEpoch - previousEpoch,
            currentEpoch >= previousEpoch,
          );
        }
      `);
      this.reclaimCompactStaleKernel = reclaimCompactStaleShader({
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
        currentEpoch: uniform(0),
        workgroupId,
        localId,
      }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Compact Reclaim Stale');
      assertStorageBufferBudget('Contact Compact Reclaim Stale', 2);

      const buildCompactFinalizeArgsShader = wgslFn(/* wgsl */`
        fn compute(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          finalizeIndirect: ptr<storage, array<u32>, read_write>,
          currentEpoch: u32,
        ) -> void {
          let consumeParity = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u],
          );
          let consumeCountAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            consumeParity != 0u,
          );
          let consumeCursorAddress = select(
            ${CompactContactPoolHeaderOffset.freeCursor0}u,
            ${CompactContactPoolHeaderOffset.freeCursor1}u,
            consumeParity != 0u,
          );
          let rawCount = atomicLoad(&persistentManifoldMap[consumeCountAddress]);
          if (rawCount > ${capacity}u) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
          let count = min(rawCount, ${capacity}u);
          let consumed = min(
            atomicLoad(&persistentManifoldMap[consumeCursorAddress]),
            count,
          );
          let remaining = count - consumed;
          let produceCountAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            consumeParity != 0u,
          );
          // Reserve the complete carry suffix once. The previous finalizer
          // issued one contended atomicAdd per free row (often >200K in the
          // stable 64K scene) even though the row set is immutable here.
          let carryBase = atomicAdd(
            &persistentManifoldMap[produceCountAddress],
            remaining,
          );
          if (
            carryBase > ${capacity}u
            || remaining > ${capacity}u - min(carryBase, ${capacity}u)
          ) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }

          // Resolve intentionally lets failed ring reservations overshoot the
          // stable tail. Clamp that speculative head here, then normalize both
          // monotonic counters without changing their modulo-M slot mapping.
          let rawManifoldHead = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeHead}u],
          );
          let rawManifoldTail = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeTail}u],
          );
          let manifoldHead = min(rawManifoldHead, rawManifoldTail);
          let manifoldFreeCount = rawManifoldTail - manifoldHead;
          let manifoldLive = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.allocatedManifolds}u],
          );
          let manifoldHighWater = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldHighWater}u],
          );
          if (
            manifoldFreeCount > ${maxPersistentManifolds}u
            || manifoldLive > ${maxPersistentManifolds}u
            || manifoldHighWater > ${maxPersistentManifolds}u
            || manifoldLive + manifoldFreeCount != manifoldHighWater
          ) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
          if (manifoldHead == rawManifoldTail) {
            atomicStore(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeHead}u],
              0u,
            );
            atomicStore(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeTail}u],
              0u,
            );
          } else {
            let manifoldBase = (manifoldHead / ${maxPersistentManifolds}u)
              * ${maxPersistentManifolds}u;
            atomicStore(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeHead}u],
              manifoldHead - manifoldBase,
            );
            atomicStore(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeTail}u],
              rawManifoldTail - manifoldBase,
            );
          }
          let workgroups = (remaining + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
          finalizeIndirect[0u] = workgroups;
          finalizeIndirect[1u] = 1u;
          finalizeIndirect[2u] = 1u;
          finalizeIndirect[3u] = carryBase;
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.finalizeCarryThreads}u],
            remaining,
          );
          atomicStore(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.finalizeWorkgroups}u],
            workgroups,
          );
        }

        fn recordCompactFatal(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          bit: u32,
          currentEpoch: u32,
        ) {
          atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
              0u,
              currentEpoch,
            );
            if (result.exchanged || result.old_value != 0u) { break; }
          }
        }
      `);
      this.buildCompactFinalizeArgsKernel = buildCompactFinalizeArgsShader({
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        finalizeIndirect: storage(this.compactFinalizeIndirectAttr!, 'uint', 4),
        currentEpoch: uniform(0),
      }).computeKernel([1, 1, 1]).setName('Contact Compact Build Free Carry Args');
      assertStorageBufferBudget('Contact Compact Build Free Carry Args', 2);

      const finalizeCompactFreeRowsShader = wgslFn(/* wgsl */`
        fn compute(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          finalizeIndirect: ptr<storage, array<u32>, read>,
          currentEpoch: u32,
          workgroupId: vec3u,
          localId: vec3u,
        ) -> void {
          let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
          let consumeParity = atomicLoad(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.epochParity}u],
          );
          let consumeCountAddress = select(
            ${CompactContactPoolHeaderOffset.freeCount0}u,
            ${CompactContactPoolHeaderOffset.freeCount1}u,
            consumeParity != 0u,
          );
          let consumeCursorAddress = select(
            ${CompactContactPoolHeaderOffset.freeCursor0}u,
            ${CompactContactPoolHeaderOffset.freeCursor1}u,
            consumeParity != 0u,
          );
          let consumeBase = select(${compactFreeRows0Offset}u, ${compactFreeRows1Offset}u, consumeParity != 0u);
          let produceBase = select(${compactFreeRows1Offset}u, ${compactFreeRows0Offset}u, consumeParity != 0u);
          let count = min(atomicLoad(&persistentManifoldMap[consumeCountAddress]), ${capacity}u);
          let consumed = min(atomicLoad(&persistentManifoldMap[consumeCursorAddress]), count);
          let remaining = count - consumed;
          if (gid >= remaining) { return; }
          let row = atomicLoad(&persistentManifoldMap[consumeBase + consumed + gid]);
          let writeIndex = finalizeIndirect[3u] + gid;
          if (writeIndex < ${capacity}u && row < ${capacity}u) {
            atomicStore(&persistentManifoldMap[produceBase + writeIndex], row);
          } else {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.freeListCarryOverflow}u,
              currentEpoch,
            );
          }
        }

        fn recordCompactFatal(
          persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
          bit: u32,
          currentEpoch: u32,
        ) {
          atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
              0u,
              currentEpoch,
            );
            if (result.exchanged || result.old_value != 0u) { break; }
          }
        }
      `);
      this.finalizeCompactFreeRowsKernel = finalizeCompactFreeRowsShader({
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        finalizeIndirect: storage(
          this.compactFinalizeIndirectAttr!,
          'uint',
          4,
        ).toReadOnly(),
        currentEpoch: uniform(0),
        workgroupId,
        localId,
      }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Compact Finalize Free Rows');
      assertStorageBufferBudget('Contact Compact Finalize Free Rows', 2);
    }

    const legacyPairShaderSource = /* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        shapes: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read_write>,
        ignoredPairHash: ptr<storage, array<u32>, read>,
        persistentManifoldMap: ptr<storage, array<u32>, read>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        debugEnabled: u32,
        bodyCount: u32,
        pairCount: u32,
        pairDispatchCount: u32,
        useCandidatePairs: u32,
        manifoldEpoch: u32,
        floorDebugBody: u32,
        contactSlop: f32,
        collisionMargin: f32,
        frictionStatic: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= pairDispatchCount) { return; }
        var manifold = gid;
        if (useCandidatePairs > 0u) {
          let activeCandidateCount = min(pairActivity[${pairActiveCandidateSlotsOffset}u], pairDispatchCount);
          if (gid >= activeCandidateCount) { return; }
          manifold = pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u];
          if (manifold >= ${maxPersistentManifolds}u) { return; }
        }
        if (manifold >= ${maxPairDispatchPairs}u) { return; }
        let pairBase = manifold * ${pairManifoldSlots}u;

        var i = 0u;
        var j = 0u;
        if (useCandidatePairs > 0u) {
          let packedPair = persistentManifoldMap[${persistentPoolKeyOffset}u + manifold];
          i = packedPair & 0xFFFFu;
          j = packedPair >> 16u;
          if (i >= j) {
            // Empty fixed candidate slots only need to be marked inactive.
            deactivateManifold(pairContacts, pairBase, 0.0, 0.0);
            return;
          }
          // Do not carry AVBD dual/penalty state across a real candidate-stream
          // interruption (or across reuse of a reclaimed pool slot).
          if (persistentManifoldMap[${persistentPoolResumeEpochOffset}u + manifold] == manifoldEpoch) {
            deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
          }
        } else {
          var pairIndex = 0u;
          if (manifold >= pairCount || manifold >= ${maxPairDispatchPairs}u) { return; }
          pairIndex = manifold;
          if (pairIndex >= pairCount) { return; }

          // Brute-force fallback path (small-N only): decode triangular pair index.
          j = u32(floor((1.0 + sqrt(1.0 + 8.0 * f32(pairIndex))) * 0.5));
          if (j == 0u) { return; }
          var triangular = (j * (j - 1u)) / 2u;
          if (triangular > pairIndex) {
            j -= 1u;
            triangular = (j * (j - 1u)) / 2u;
          }
          i = pairIndex - triangular;
          if (i >= j) { return; }
        }

        let isFloorPair = floorDebugBody != 0xffffffffu && (i == floorDebugBody || j == floorDebugBody);
        if (isFloorPair && debugEnabled > 0u) {
          atomicAdd(&debugCounters[7], 1u);
        }

        if (isIgnoredPair(ignoredPairHash, i, j)) {
          deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
          return;
        }

        let firstInfo = loadContactMeta(pairContacts, pairBase);
        let firstI = u32(firstInfo.x + 0.5);
        let firstJ = u32(firstInfo.y + 0.5);
        if (firstI != i || firstJ != j) {
          if (debugEnabled > 0u) {
            atomicAdd(&debugCounters[0], 1u);
            if (isFloorPair) {
              atomicAdd(&debugCounters[8], 1u);
            }
          }
          deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
        }

        var wasActive = false;
        var prevPairFeature: i32 = -1;
        var prevPairNormal = vec3f(0.0);
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let slot = pairBase + s;
          let slotInfo = loadContactMeta(pairContacts, slot);
          let slotEncodedInfo = bitcast<u32>(slotInfo.w);
          // A swept row is a one-step guard, not a persistent discrete
          // manifold. Never let it enable DCD hysteresis or seed DCD state on
          // the following substep.
          if (slotInfo.z > 0.5 && !contactInfoIsSpeculative(slotEncodedInfo)) {
            if (!wasActive) {
              prevPairFeature = i32(decodePackedBaseFeature(slotInfo.w));
              prevPairNormal = loadContactNormalPen(pairContacts, slot).xyz;
            }
            wasActive = true;
          }
        }

        let hysteresis = 0.35 * contactSlop;
        // The reference AVBD manifold is discrete: separation is allowed as a
        // robust SAT/clipping tolerance, but a separated anchor is rejected
        // before it can become a solver row. Do not turn the collision margin
        // into a speculative force shell.
        let sepSlop = contactSlop + select(0.0, hysteresis, wasActive);

        if (i >= bodyCount || j >= bodyCount) {
          deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
          return;
        }

        let invMassA = positions[i].w;
        let invMassB = positions[j].w;
        if (invMassA + invMassB == 0.0) {
          deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
          return;
        }

        let posA = positions[i].xyz;
        let posB = positions[j].xyz;
        let qA = quaternions[i];
        let qB = quaternions[j];
        let shpA = shapes[i];
        let shpB = shapes[j];
        if (!shapesCanCollide(shpA, shpB)) {
          deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
          return;
        }
        let frictionScale = sqrt(max(decodeShapeFriction(shpA.x), 0.0) * max(decodeShapeFriction(shpB.x), 0.0));
        let shapeTypeA = decodeShapeType(shpA.x);
        let shapeTypeB = decodeShapeType(shpB.x);
        let isSphereA = shapeTypeA == SHAPE_TYPE_SPHERE;
        let isSphereB = shapeTypeB == SHAPE_TYPE_SPHERE;
        let specialPair = isSphereA || isSphereB;
        // A sphere has only one DCD row against a static target. Seed that
        // row with the same mass-scaled normal stiffness as swept CCD so a
        // heavy oblique projectile cannot spend its first discrete frame
        // penetrating the target while k grows from the canonical floor.
        //
        // Keep this narrow: box manifolds have up to four support rows and
        // dynamic/dynamic impacts can correct both participants, so they keep
        // the reference k=1 cold start.
        let staticSphereTargetPair = specialPair
          && (invMassA <= 0.0 || invMassB <= 0.0);
        let staticSphereTargetPenaltyN = select(
          1.0,
          min(
            100000000.0,
            416666.67 / max(invMassA + invMassB, 1.0e-6),
          ),
          staticSphereTargetPair,
        );
        let halfA = vec3f(shpA.y, shpA.z, shpA.w);
        let halfB = vec3f(shpB.y, shpB.z, shpB.w);
        let radiusA = max(halfA.x, 0.0);
        let radiusB = max(halfB.x, 0.0);
        let e0 = vec3f(1.0, 0.0, 0.0);
        let e1 = vec3f(0.0, 1.0, 0.0);
        let e2 = vec3f(0.0, 0.0, 1.0);
        let axA0 = qrot(qA, e0);
        let axA1 = qrot(qA, e1);
        let axA2 = qrot(qA, e2);
        let axB0 = qrot(qB, e0);
        let axB1 = qrot(qB, e1);
        let axB2 = qrot(qB, e2);

        var penetration = 0.0;
        var keepContact = false;
        var specialPointA = vec3f(0.0);
        var specialPointB = vec3f(0.0);
        var specialRAWorld = vec3f(0.0);
        var specialRBWorld = vec3f(0.0);
        var specialContactKey = 0u;
        var specialRefIsA = false;
        var specialRefFeature = 0u;
        var specialIncFeature = 0u;
        var specialManifoldPreferredT1 = vec3f(0.0);
        var specialManifoldHasPreferredT1 = false;
        var bestNormal = vec3f(0.0);
        var bestFeature: i32 = -1;
        var keepRejectClass = 0u;
        var separatedFeature: i32 = -1;
        if (!specialPair) {
        let d = posB - posA;
        let eps = 1e-6;

        let R00 = dot(axA0, axB0); let R01 = dot(axA0, axB1); let R02 = dot(axA0, axB2);
        let R10 = dot(axA1, axB0); let R11 = dot(axA1, axB1); let R12 = dot(axA1, axB2);
        let R20 = dot(axA2, axB0); let R21 = dot(axA2, axB1); let R22 = dot(axA2, axB2);

        let aR00 = abs(R00) + eps; let aR01 = abs(R01) + eps; let aR02 = abs(R02) + eps;
        let aR10 = abs(R10) + eps; let aR11 = abs(R11) + eps; let aR12 = abs(R12) + eps;
        let aR20 = abs(R20) + eps; let aR21 = abs(R21) + eps; let aR22 = abs(R22) + eps;

        let tA = vec3f(dot(d, axA0), dot(d, axA1), dot(d, axA2));
        var minPen = 1e30;
        var faceMinPen = 1e30;
        var faceBestNormal = vec3f(0.0);
        var faceBestFeature: i32 = -1;
        var separated = false;

        // Face normals of A
        {
          let ra = halfA.x;
          let rb = aR00 * halfB.x + aR01 * halfB.y + aR02 * halfB.z;
          let s = abs(tA.x);
          let pen = ra + rb - s;
          if (pen < -sepSlop) { separated = true; separatedFeature = 0; }
          if (!separated && pen < faceMinPen) {
            faceMinPen = pen;
            faceBestNormal = select(axA0, -axA0, tA.x < 0.0);
            faceBestFeature = 0;
          }
          if (!separated && pen < minPen) {
            minPen = pen;
            bestNormal = select(axA0, -axA0, tA.x < 0.0);
            bestFeature = 0;
          }
        }
        if (!separated) {
          let ra = halfA.y;
          let rb = aR10 * halfB.x + aR11 * halfB.y + aR12 * halfB.z;
          let s = abs(tA.y);
          let pen = ra + rb - s;
          if (pen < -sepSlop) { separated = true; separatedFeature = 1; }
          if (!separated && pen < faceMinPen) {
            faceMinPen = pen;
            faceBestNormal = select(axA1, -axA1, tA.y < 0.0);
            faceBestFeature = 1;
          }
          if (!separated && pen < minPen) {
            minPen = pen;
            bestNormal = select(axA1, -axA1, tA.y < 0.0);
            bestFeature = 1;
          }
        }
        if (!separated) {
          let ra = halfA.z;
          let rb = aR20 * halfB.x + aR21 * halfB.y + aR22 * halfB.z;
          let s = abs(tA.z);
          let pen = ra + rb - s;
          if (pen < -sepSlop) { separated = true; separatedFeature = 2; }
          if (!separated && pen < faceMinPen) {
            faceMinPen = pen;
            faceBestNormal = select(axA2, -axA2, tA.z < 0.0);
            faceBestFeature = 2;
          }
          if (!separated && pen < minPen) {
            minPen = pen;
            bestNormal = select(axA2, -axA2, tA.z < 0.0);
            bestFeature = 2;
          }
        }

        // Face normals of B
        let tB = vec3f(dot(d, axB0), dot(d, axB1), dot(d, axB2));
        if (!separated) {
          let ra = aR00 * halfA.x + aR10 * halfA.y + aR20 * halfA.z;
          let rb = halfB.x;
          let s = abs(tB.x);
          let pen = ra + rb - s;
          if (pen < -sepSlop) { separated = true; separatedFeature = 3; }
          if (!separated && pen < faceMinPen) {
            faceMinPen = pen;
            faceBestNormal = select(axB0, -axB0, tB.x < 0.0);
            faceBestFeature = 3;
          }
          if (!separated && pen < minPen) {
            minPen = pen;
            bestNormal = select(axB0, -axB0, tB.x < 0.0);
            bestFeature = 3;
          }
        }
        if (!separated) {
          let ra = aR01 * halfA.x + aR11 * halfA.y + aR21 * halfA.z;
          let rb = halfB.y;
          let s = abs(tB.y);
          let pen = ra + rb - s;
          if (pen < -sepSlop) { separated = true; separatedFeature = 4; }
          if (!separated && pen < faceMinPen) {
            faceMinPen = pen;
            faceBestNormal = select(axB1, -axB1, tB.y < 0.0);
            faceBestFeature = 4;
          }
          if (!separated && pen < minPen) {
            minPen = pen;
            bestNormal = select(axB1, -axB1, tB.y < 0.0);
            bestFeature = 4;
          }
        }
        if (!separated) {
          let ra = aR02 * halfA.x + aR12 * halfA.y + aR22 * halfA.z;
          let rb = halfB.z;
          let s = abs(tB.z);
          let pen = ra + rb - s;
          if (pen < -sepSlop) { separated = true; separatedFeature = 5; }
          if (!separated && pen < faceMinPen) {
            faceMinPen = pen;
            faceBestNormal = select(axB2, -axB2, tB.z < 0.0);
            faceBestFeature = 5;
          }
          if (!separated && pen < minPen) {
            minPen = pen;
            bestNormal = select(axB2, -axB2, tB.z < 0.0);
            bestFeature = 5;
          }
        }

        // Edge-edge axes (9)
        if (!separated) {
          let axis = cross(axA0, axB0);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.y * aR20 + halfA.z * aR10;
            let rb = halfB.y * aR02 + halfB.z * aR01;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 6; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 6;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA0, axB1);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.y * aR21 + halfA.z * aR11;
            let rb = halfB.x * aR02 + halfB.z * aR00;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 7; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 7;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA0, axB2);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.y * aR22 + halfA.z * aR12;
            let rb = halfB.x * aR01 + halfB.y * aR00;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 8; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 8;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA1, axB0);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.x * aR20 + halfA.z * aR00;
            let rb = halfB.y * aR12 + halfB.z * aR11;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 9; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 9;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA1, axB1);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.x * aR21 + halfA.z * aR01;
            let rb = halfB.x * aR12 + halfB.z * aR10;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 10; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 10;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA1, axB2);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.x * aR22 + halfA.z * aR02;
            let rb = halfB.x * aR11 + halfB.y * aR10;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 11; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 11;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA2, axB0);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.x * aR10 + halfA.y * aR00;
            let rb = halfB.y * aR22 + halfB.z * aR21;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 12; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 12;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA2, axB1);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.x * aR11 + halfA.y * aR01;
            let rb = halfB.x * aR22 + halfB.z * aR20;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 13; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 13;
            }
          }
        }
        if (!separated) {
          let axis = cross(axA2, axB2);
          let len2 = dot(axis, axis);
          if (len2 > 1e-6) {
            let invLen = inverseSqrt(len2);
            let n = axis * invLen;
            let ra = halfA.x * aR12 + halfA.y * aR02;
            let rb = halfB.x * aR21 + halfB.y * aR20;
            let s = abs(dot(d, n));
            let pen = (ra + rb) * invLen - s;
            if (pen < -sepSlop) { separated = true; separatedFeature = 14; }
            if (!separated && pen < minPen) {
              minPen = pen;
              bestNormal = select(n, -n, dot(d, n) < 0.0);
              bestFeature = 14;
            }
          }
        }

        // SAT winner hysteresis: if the previous active feature is still close in
        // penetration, keep it to avoid face/edge normal flicker on rotating boxes.
        if (!separated && wasActive && prevPairFeature >= 0) {
          let axisHysteresis = 0.5 * contactSlop;
          var prevPen = 1e30;
          var prevNormalEval = bestNormal;
          var prevValid = false;

          if (prevPairFeature == 0) {
            prevPen = halfA.x + (aR00 * halfB.x + aR01 * halfB.y + aR02 * halfB.z) - abs(tA.x);
            prevNormalEval = select(axA0, -axA0, tA.x < 0.0);
            prevValid = true;
          } else if (prevPairFeature == 1) {
            prevPen = halfA.y + (aR10 * halfB.x + aR11 * halfB.y + aR12 * halfB.z) - abs(tA.y);
            prevNormalEval = select(axA1, -axA1, tA.y < 0.0);
            prevValid = true;
          } else if (prevPairFeature == 2) {
            prevPen = halfA.z + (aR20 * halfB.x + aR21 * halfB.y + aR22 * halfB.z) - abs(tA.z);
            prevNormalEval = select(axA2, -axA2, tA.z < 0.0);
            prevValid = true;
          } else if (prevPairFeature == 3) {
            prevPen = (aR00 * halfA.x + aR10 * halfA.y + aR20 * halfA.z) + halfB.x - abs(tB.x);
            prevNormalEval = select(axB0, -axB0, tB.x < 0.0);
            prevValid = true;
          } else if (prevPairFeature == 4) {
            prevPen = (aR01 * halfA.x + aR11 * halfA.y + aR21 * halfA.z) + halfB.y - abs(tB.y);
            prevNormalEval = select(axB1, -axB1, tB.y < 0.0);
            prevValid = true;
          } else if (prevPairFeature == 5) {
            prevPen = (aR02 * halfA.x + aR12 * halfA.y + aR22 * halfA.z) + halfB.z - abs(tB.z);
            prevNormalEval = select(axB2, -axB2, tB.z < 0.0);
            prevValid = true;
          } else if (prevPairFeature == 6) {
            let axis = cross(axA0, axB0);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.y * aR20 + halfA.z * aR10;
              let rb = halfB.y * aR02 + halfB.z * aR01;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 7) {
            let axis = cross(axA0, axB1);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.y * aR21 + halfA.z * aR11;
              let rb = halfB.x * aR02 + halfB.z * aR00;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 8) {
            let axis = cross(axA0, axB2);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.y * aR22 + halfA.z * aR12;
              let rb = halfB.x * aR01 + halfB.y * aR00;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 9) {
            let axis = cross(axA1, axB0);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.x * aR20 + halfA.z * aR00;
              let rb = halfB.y * aR12 + halfB.z * aR11;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 10) {
            let axis = cross(axA1, axB1);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.x * aR21 + halfA.z * aR01;
              let rb = halfB.x * aR12 + halfB.z * aR10;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 11) {
            let axis = cross(axA1, axB2);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.x * aR22 + halfA.z * aR02;
              let rb = halfB.x * aR11 + halfB.y * aR10;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 12) {
            let axis = cross(axA2, axB0);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.x * aR10 + halfA.y * aR00;
              let rb = halfB.y * aR22 + halfB.z * aR21;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 13) {
            let axis = cross(axA2, axB1);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.x * aR11 + halfA.y * aR01;
              let rb = halfB.x * aR22 + halfB.z * aR20;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          } else if (prevPairFeature == 14) {
            let axis = cross(axA2, axB2);
            let len2 = dot(axis, axis);
            if (len2 > 1e-6) {
              let invLen = inverseSqrt(len2);
              let n = axis * invLen;
              let ra = halfA.x * aR12 + halfA.y * aR02;
              let rb = halfB.x * aR21 + halfB.y * aR20;
              prevPen = (ra + rb) * invLen - abs(dot(d, n));
              prevNormalEval = select(n, -n, dot(d, n) < 0.0);
              prevValid = true;
            }
          }

          if (prevValid && prevPen > -sepSlop && prevPen <= minPen + axisHysteresis && dot(prevPairNormal, prevNormalEval) > 0.85) {
            minPen = prevPen;
            bestNormal = prevNormalEval;
            bestFeature = prevPairFeature;
          }
        }

        // Edge-edge axes are noisy near face-edge ties on rotating boxes.
        // Match the reference's conservative face preference: an edge axis
        // must win both relatively and by a meaningful absolute amount.  The
        // old 0.5*slop threshold changed a wide face manifold into a single
        // noisy edge row under tiny f32 perturbations, which is especially
        // visible on tall, thin dominoes.
        if (!separated && bestFeature >= 6 && faceBestFeature >= 0) {
          let edgeClearlyBetter = minPen < faceMinPen * 0.95 - 0.01;
          if (!edgeClearlyBetter) {
            minPen = faceMinPen;
            bestNormal = faceBestNormal;
            bestFeature = faceBestFeature;
          }
        }

        penetration = minPen;
        keepContact = !separated && penetration > -sepSlop;
        if (!keepContact) {
          keepRejectClass = select(select(14u, 15u, wasActive), 13u, separated);
        }
        } else {
          if (isSphereA && isSphereB) {
            let centerDelta = posB - posA;
            let centerDist2 = dot(centerDelta, centerDelta);
            let rSum = radiusA + radiusB;
            if (centerDist2 > 1e-12) {
              let centerDist = sqrt(centerDist2);
              bestNormal = centerDelta / centerDist;
              penetration = rSum - centerDist;
            } else {
              bestNormal = select(prevPairNormal, vec3f(0.0, 1.0, 0.0), dot(prevPairNormal, prevPairNormal) < 1e-12);
              penetration = rSum;
            }
            bestFeature = 6;
            keepContact = penetration > -sepSlop;
            if (!keepContact) {
              keepRejectClass = select(14u, 15u, wasActive);
            }
            specialPointA = posA + bestNormal * radiusA;
            specialPointB = posB - bestNormal * radiusB;
            specialRAWorld = specialPointA - posA;
            specialRBWorld = specialPointB - posB;
            specialContactKey = packEdgeContactFeature(0u, 0u);
          } else if (isSphereA != isSphereB) {
            let sphereIsA = isSphereA;
            let sphereCenter = select(posB, posA, sphereIsA);
            let boxCenter = select(posA, posB, sphereIsA);
            let sphereRadius = select(radiusB, radiusA, sphereIsA);
            let halfBox = select(halfA, halfB, sphereIsA);
            let boxAx0 = select(axA0, axB0, sphereIsA);
            let boxAx1 = select(axA1, axB1, sphereIsA);
            let boxAx2 = select(axA2, axB2, sphereIsA);

            let rel = sphereCenter - boxCenter;
            let sphereLocal = vec3f(dot(rel, boxAx0), dot(rel, boxAx1), dot(rel, boxAx2));
            let clampedLocal = clamp(sphereLocal, -halfBox, halfBox);
            let deltaLocal = sphereLocal - clampedLocal;
            var boxPointLocal = clampedLocal;
            var faceAxis = 0u;
            var faceSign = 1.0;
            var boxOutwardNormal = boxAx0;
            let outsideDist2 = dot(deltaLocal, deltaLocal);

            if (outsideDist2 > 1e-12) {
              faceAxis = dominantAbsAxis3(deltaLocal);
              faceSign = sgnnz(axisValue3(deltaLocal, faceAxis));
              boxOutwardNormal = axisVector3(faceAxis, boxAx0, boxAx1, boxAx2) * faceSign;
              penetration = sphereRadius - sqrt(outsideDist2);
            } else {
              let faceGap = halfBox - abs(sphereLocal);
              faceAxis = leastAxis3(faceGap);
              faceSign = sgnnz(axisValue3(sphereLocal, faceAxis));
              if (faceAxis == 0u) {
                boxPointLocal = vec3f(faceSign * halfBox.x, sphereLocal.y, sphereLocal.z);
              } else if (faceAxis == 1u) {
                boxPointLocal = vec3f(sphereLocal.x, faceSign * halfBox.y, sphereLocal.z);
              } else {
                boxPointLocal = vec3f(sphereLocal.x, sphereLocal.y, faceSign * halfBox.z);
              }
              boxOutwardNormal = axisVector3(faceAxis, boxAx0, boxAx1, boxAx2) * faceSign;
              penetration = sphereRadius + axisValue3(faceGap, faceAxis);
            }

            keepContact = penetration > -sepSlop;
            if (!keepContact) {
              keepRejectClass = select(14u, 15u, wasActive);
            }
            let boxPointWorld = boxCenter
              + boxAx0 * boxPointLocal.x
              + boxAx1 * boxPointLocal.y
              + boxAx2 * boxPointLocal.z;
            let spherePointWorld = sphereCenter - boxOutwardNormal * sphereRadius;
            bestNormal = select(boxOutwardNormal, -boxOutwardNormal, sphereIsA);
            bestFeature = i32(faceAxis) + select(0, 3, sphereIsA);
            specialRefIsA = !sphereIsA;
            specialRefFeature = faceAxis;
            specialIncFeature = 0u;
            specialContactKey = packFaceContactFeature(!sphereIsA, faceAxis, 0u, 0u);
            specialPointA = select(boxPointWorld, spherePointWorld, sphereIsA);
            specialPointB = select(spherePointWorld, boxPointWorld, sphereIsA);
            specialRAWorld = specialPointA - posA;
            specialRBWorld = specialPointB - posB;
            specialManifoldHasPreferredT1 = true;
            specialManifoldPreferredT1 = select(
              axisVector3(0u, boxAx0, boxAx1, boxAx2),
              axisVector3(1u, boxAx0, boxAx1, boxAx2),
              faceAxis == 0u,
            );
            if (faceAxis == 1u) {
              specialManifoldPreferredT1 = boxAx2;
            } else if (faceAxis == 2u) {
              specialManifoldPreferredT1 = boxAx0;
            }
          }
        }
        if (!keepContact) {
          if (debugEnabled > 0u && isFloorPair) {
            atomicAdd(&debugCounters[9], 1u);
            if (keepRejectClass == 13u) {
              atomicAdd(&debugCounters[13], 1u);
              if (separatedFeature >= 0 && separatedFeature <= 2) {
                atomicAdd(&debugCounters[16u + u32(separatedFeature)], 1u);
              } else if (separatedFeature >= 3 && separatedFeature <= 5) {
                atomicAdd(&debugCounters[16u + u32(separatedFeature)], 1u);
              } else if (separatedFeature >= 6) {
                atomicAdd(&debugCounters[22], 1u);
              } else {
                atomicAdd(&debugCounters[23], 1u);
              }
            } else if (keepRejectClass == 15u) {
              atomicAdd(&debugCounters[15], 1u);
            } else {
              atomicAdd(&debugCounters[14], 1u);
            }
          }
          deactivateManifold(pairContacts, pairBase, f32(i), f32(j));
          return;
        }
        let storedPenetration = max(penetration, 0.0);
        // Keep more temporary face candidates, then reduce to manifold slots by
        // spread (not only depth) for better torque stability.
        var candidatePointWorld: array<vec3f, 8>;
        var candidateRAWorld: array<vec3f, 8>;
        var candidateRBWorld: array<vec3f, 8>;
        var candidatePenetration: array<f32, 8>;
        var candidateContactKey: array<u32, 8>;
        var candidateCount = 0u;
        var manifoldPreferredT1 = specialManifoldPreferredT1;
        var manifoldHasPreferredT1 = specialManifoldHasPreferredT1;
        var refIsA = specialRefIsA;
        var refFeature = specialRefFeature;
        var incFeature = specialIncFeature;
        if (specialPair) {
          candidatePointWorld[0] = 0.5 * (specialPointA + specialPointB);
          candidateRAWorld[0] = specialRAWorld;
          candidateRBWorld[0] = specialRBWorld;
          candidatePenetration[0] = storedPenetration;
          candidateContactKey[0] = specialContactKey;
          candidateCount = 1u;
        } else if (bestFeature >= 6) {
          // Edge-edge: keep closest-segment single contact.
          let code = u32(bestFeature - 6);
          let k = code / 3u;
          let l = code - k * 3u;

          var uA = vec3f(0.0);
          var sA1 = vec3f(0.0);
          var sA2 = vec3f(0.0);
          var huA = 0.0;
          var hA1 = 0.0;
          var hA2 = 0.0;
          if (k == 0u) {
            uA = axA0; sA1 = axA1; sA2 = axA2;
            huA = halfA.x; hA1 = halfA.y; hA2 = halfA.z;
          } else if (k == 1u) {
            uA = axA1; sA1 = axA0; sA2 = axA2;
            huA = halfA.y; hA1 = halfA.x; hA2 = halfA.z;
          } else {
            uA = axA2; sA1 = axA0; sA2 = axA1;
            huA = halfA.z; hA1 = halfA.x; hA2 = halfA.y;
          }

          let sgnA1 = sgnnz(dot(sA1, bestNormal));
          let sgnA2 = sgnnz(dot(sA2, bestNormal));
          let cA = posA + sA1 * (sgnA1 * hA1) + sA2 * (sgnA2 * hA2);
          let pA0 = cA - uA * huA;
          let pA1 = cA + uA * huA;

          var uB = vec3f(0.0);
          var sB1 = vec3f(0.0);
          var sB2 = vec3f(0.0);
          var huB = 0.0;
          var hB1 = 0.0;
          var hB2 = 0.0;
          if (l == 0u) {
            uB = axB0; sB1 = axB1; sB2 = axB2;
            huB = halfB.x; hB1 = halfB.y; hB2 = halfB.z;
          } else if (l == 1u) {
            uB = axB1; sB1 = axB0; sB2 = axB2;
            huB = halfB.y; hB1 = halfB.x; hB2 = halfB.z;
          } else {
            uB = axB2; sB1 = axB0; sB2 = axB1;
            huB = halfB.z; hB1 = halfB.x; hB2 = halfB.y;
          }

          let sgnB1 = sgnnz(-dot(sB1, bestNormal));
          let sgnB2 = sgnnz(-dot(sB2, bestNormal));
          let cB = posB + sB1 * (sgnB1 * hB1) + sB2 * (sgnB2 * hB2);
          let pB0 = cB - uB * huB;
          let pB1 = cB + uB * huB;

          let seg = closestPointsSegments(pA0, pA1, pB0, pB1);
          let raWorld = seg.p - posA;
          let rbWorld = seg.q - posB;
          candidatePointWorld[0] = 0.5 * (seg.p + seg.q);
          candidateRAWorld[0] = raWorld;
          candidateRBWorld[0] = rbWorld;
          candidatePenetration[0] = storedPenetration;
          candidateContactKey[0] = packEdgeContactFeature(k, l);
          candidateCount = 1u;
          manifoldPreferredT1 = uA;
          manifoldHasPreferredT1 = true;
        } else {
          // Face features: generate up to 8 clipped candidates, then reduce.
          var refPos = vec3f(0.0);
          var refHalf = vec3f(0.0);
          var refX = vec3f(0.0);
          var refY = vec3f(0.0);
          var refZ = vec3f(0.0);
          var incPos = vec3f(0.0);
          var incHalf = vec3f(0.0);
          var incX = vec3f(0.0);
          var incY = vec3f(0.0);
          var incZ = vec3f(0.0);
          if (bestFeature < 3) {
            refPos = posA; refHalf = halfA; refX = axA0; refY = axA1; refZ = axA2;
            incPos = posB; incHalf = halfB; incX = axB0; incY = axB1; incZ = axB2;
            refFeature = u32(bestFeature);
          } else {
            refPos = posB; refHalf = halfB; refX = axB0; refY = axB1; refZ = axB2;
            incPos = posA; incHalf = halfA; incX = axA0; incY = axA1; incZ = axA2;
            refFeature = u32(bestFeature - 3);
          }

          var refAxis = vec3f(0.0);
          var refU = vec3f(0.0);
          var refV = vec3f(0.0);
          var refH = 0.0;
          var refHU = 0.0;
          var refHV = 0.0;
          if (refFeature == 0u) {
            refAxis = refX; refU = refY; refV = refZ;
            refH = refHalf.x; refHU = refHalf.y; refHV = refHalf.z;
          } else if (refFeature == 1u) {
            refAxis = refY; refU = refX; refV = refZ;
            refH = refHalf.y; refHU = refHalf.x; refHV = refHalf.z;
          } else {
            refAxis = refZ; refU = refX; refV = refY;
            refH = refHalf.z; refHU = refHalf.x; refHV = refHalf.y;
          }

          refIsA = bestFeature < 3;
          // Reference-face clipping uses the reference outward normal.
          let nRef = select(-bestNormal, bestNormal, refIsA);
          let refSign = sgnnz(dot(refAxis, nRef));
          let refCenter = refPos + refAxis * (refSign * refH);
          manifoldPreferredT1 = refU;
          manifoldHasPreferredT1 = true;

          let ad0 = abs(dot(incX, nRef));
          let ad1 = abs(dot(incY, nRef));
          let ad2 = abs(dot(incZ, nRef));
          incFeature = 0u;
          if (ad1 > ad0 && ad1 >= ad2) {
            incFeature = 1u;
          } else if (ad2 > ad0 && ad2 > ad1) {
            incFeature = 2u;
          }

          var incAxis = vec3f(0.0);
          var incU = vec3f(0.0);
          var incV = vec3f(0.0);
          var incH = 0.0;
          var incHU = 0.0;
          var incHV = 0.0;
          if (incFeature == 0u) {
            incAxis = incX; incU = incY; incV = incZ;
            incH = incHalf.x; incHU = incHalf.y; incHV = incHalf.z;
          } else if (incFeature == 1u) {
            incAxis = incY; incU = incX; incV = incZ;
            incH = incHalf.y; incHU = incHalf.x; incHV = incHalf.z;
          } else {
            incAxis = incZ; incU = incX; incV = incY;
            incH = incHalf.z; incHU = incHalf.x; incHV = incHalf.y;
          }

          // Incident face must oppose the reference normal.
          let incSign = select(1.0, -1.0, dot(incAxis, nRef) > 0.0);
          let incCenter = incPos + incAxis * (incSign * incH);

          var polyIn: array<vec3f, 8>;
          var polyOut: array<vec3f, 8>;
          polyIn[0] = incCenter + incU * incHU + incV * incHV;
          polyIn[1] = incCenter - incU * incHU + incV * incHV;
          polyIn[2] = incCenter - incU * incHU - incV * incHV;
          polyIn[3] = incCenter + incU * incHU - incV * incHV;
          var polyCount = 4u;

          for (var planeIdx = 0u; planeIdx < 4u; planeIdx++) {
            if (polyCount == 0u) { break; }

            var planeN = vec3f(0.0);
            var planeD = 0.0;
            if (planeIdx == 0u) {
              planeN = refU;
              planeD = dot(refU, refCenter) + refHU;
            } else if (planeIdx == 1u) {
              planeN = -refU;
              planeD = dot(-refU, refCenter) + refHU;
            } else if (planeIdx == 2u) {
              planeN = refV;
              planeD = dot(refV, refCenter) + refHV;
            } else {
              planeN = -refV;
              planeD = dot(-refV, refCenter) + refHV;
            }

            var outCount = 0u;
            let planeEpsilon = 1e-5;
            for (var vi = 0u; vi < polyCount; vi++) {
              let a = polyIn[vi];
              let b = polyIn[(vi + 1u) % polyCount];
              let da = dot(planeN, a) - planeD;
              let db = dot(planeN, b) - planeD;
              let inA = da <= planeEpsilon;
              let inB = db <= planeEpsilon;

              if (inA && inB) {
                if (outCount < 8u) {
                  polyOut[outCount] = b;
                  outCount += 1u;
                }
              } else if (inA && !inB) {
                let denom = da - db;
                if (abs(denom) > 1e-6 && outCount < 8u) {
                  let t = clamp(da / denom, 0.0, 1.0);
                  polyOut[outCount] = a + (b - a) * t;
                  outCount += 1u;
                }
              } else if (!inA && inB) {
                let denom = da - db;
                if (abs(denom) > 1e-6 && outCount < 8u) {
                  let t = clamp(da / denom, 0.0, 1.0);
                  polyOut[outCount] = a + (b - a) * t;
                  outCount += 1u;
                }
                if (outCount < 8u) {
                  polyOut[outCount] = b;
                  outCount += 1u;
                }
              }
            }

            polyCount = outCount;
            for (var vi = 0u; vi < polyCount; vi++) {
              polyIn[vi] = polyOut[vi];
            }
          }

          for (var vi = 0u; vi < polyCount; vi++) {
            let p = polyIn[vi];
            // Signed distance to reference plane (outward normal = nRef).
            let dist = dot(nRef, p - refCenter);
            if (dist > contactSlop) { continue; }

            let pStored = max(-dist, 0.0);
            let pRef = p - nRef * dist;
            let xA = select(p, pRef, refIsA);
            let xB = select(pRef, p, refIsA);
            // Keep the manifold point at the midpoint for dedupe/reduction,
            // but store distinct surface anchors for A/B like the reference.
            let cp = 0.5 * (xA + xB);
            var duplicate = false;
            for (var c = 0u; c < candidateCount; c++) {
              let dd = cp - candidatePointWorld[c];
              if (dot(dd, dd) < 1e-6) {
                duplicate = true;
              }
            }
            if (duplicate) { continue; }

            if (candidateCount < 8u) {
              candidatePointWorld[candidateCount] = cp;
              candidateRAWorld[candidateCount] = xA - posA;
              candidateRBWorld[candidateCount] = xB - posB;
              candidatePenetration[candidateCount] = pStored;
              candidateContactKey[candidateCount] = packFaceContactFeature(refIsA, refFeature, incFeature, min(vi, 7u));
              candidateCount += 1u;
            }
          }
        }

        if (candidateCount > ${pairManifoldSlots}u) {
          // 4-point manifold reduction heuristic:
          // 1) deepest, 2) farthest from deepest,
          // 3) max triangle area, 4) max min-distance from previous 3.
          var selectedIdx: array<u32, ${pairManifoldSlots}>;
          var selCount = 0u;

          var deepestIdx = 0u;
          var deepestPen = -1e30;
          for (var c = 0u; c < candidateCount; c++) {
            if (candidatePenetration[c] > deepestPen) {
              deepestPen = candidatePenetration[c];
              deepestIdx = c;
            }
          }
          selectedIdx[0] = deepestIdx;
          selCount = 1u;

          if (${pairManifoldSlots}u > 1u) {
            var farIdx = deepestIdx;
            var farD2 = -1.0;
            let p0 = candidatePointWorld[deepestIdx];
            for (var c = 0u; c < candidateCount; c++) {
              if (c == deepestIdx) { continue; }
              let dp = candidatePointWorld[c] - p0;
              let d2 = dot(dp, dp);
              if (d2 > farD2) {
                farD2 = d2;
                farIdx = c;
              }
            }
            selectedIdx[1] = farIdx;
            selCount = 2u;
          }

          if (${pairManifoldSlots}u > 2u) {
            var areaIdx = selectedIdx[0];
            var areaBest = -1.0;
            let p0 = candidatePointWorld[selectedIdx[0]];
            let p1 = candidatePointWorld[selectedIdx[1]];
            let base = p1 - p0;
            for (var c = 0u; c < candidateCount; c++) {
              if (c == selectedIdx[0] || c == selectedIdx[1]) { continue; }
              let v = candidatePointWorld[c] - p0;
              let cr = cross(base, v);
              let a2 = dot(cr, cr);
              if (a2 > areaBest) {
                areaBest = a2;
                areaIdx = c;
              }
            }
            selectedIdx[2] = areaIdx;
            selCount = 3u;
          }

          if (${pairManifoldSlots}u > 3u) {
            var spreadIdx = selectedIdx[0];
            var spreadBest = -1.0;
            for (var c = 0u; c < candidateCount; c++) {
              var used = false;
              for (var s = 0u; s < selCount; s++) {
                if (c == selectedIdx[s]) {
                  used = true;
                }
              }
              if (used) { continue; }

              var minD2 = 1e30;
              for (var s = 0u; s < selCount; s++) {
                let dp = candidatePointWorld[c] - candidatePointWorld[selectedIdx[s]];
                let d2 = dot(dp, dp);
                if (d2 < minD2) {
                  minD2 = d2;
                }
              }
              if (minD2 > spreadBest) {
                spreadBest = minD2;
                spreadIdx = c;
              }
            }
            selectedIdx[3] = spreadIdx;
            selCount = 4u;
          }

          // Generalize the farthest-point fill for 5-8 slot manifolds. The
          // first four preserve the reference reduction heuristic; additional
          // slots maximize their minimum distance from every selected point.
          while (selCount < ${pairManifoldSlots}u) {
            var spreadIdx = selectedIdx[0];
            var spreadBest = -1.0;
            for (var c = 0u; c < candidateCount; c++) {
              var used = false;
              for (var s = 0u; s < selCount; s++) {
                if (c == selectedIdx[s]) {
                  used = true;
                }
              }
              if (used) { continue; }

              var minD2 = 1e30;
              for (var s = 0u; s < selCount; s++) {
                let dp = candidatePointWorld[c] - candidatePointWorld[selectedIdx[s]];
                minD2 = min(minD2, dot(dp, dp));
              }
              if (minD2 > spreadBest) {
                spreadBest = minD2;
                spreadIdx = c;
              }
            }
            selectedIdx[selCount] = spreadIdx;
            selCount += 1u;
          }

          var reducedPointWorld: array<vec3f, ${pairManifoldSlots}>;
          var reducedRAWorld: array<vec3f, ${pairManifoldSlots}>;
          var reducedRBWorld: array<vec3f, ${pairManifoldSlots}>;
          var reducedPenetration: array<f32, ${pairManifoldSlots}>;
          var reducedContactKey: array<u32, ${pairManifoldSlots}>;
          for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
            let idx = selectedIdx[s];
            reducedPointWorld[s] = candidatePointWorld[idx];
            reducedRAWorld[s] = candidateRAWorld[idx];
            reducedRBWorld[s] = candidateRBWorld[idx];
            reducedPenetration[s] = candidatePenetration[idx];
            reducedContactKey[s] = candidateContactKey[idx];
          }
          for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
            candidatePointWorld[s] = reducedPointWorld[s];
            candidateRAWorld[s] = reducedRAWorld[s];
            candidateRBWorld[s] = reducedRBWorld[s];
            candidatePenetration[s] = reducedPenetration[s];
            candidateContactKey[s] = reducedContactKey[s];
          }
          candidateCount = ${pairManifoldSlots}u;
        }

        if (candidateCount == 0u) {
          if (debugEnabled > 0u && isFloorPair) {
            atomicAdd(&debugCounters[10], 1u);
          }
          // Degenerate face clipping fallback.
          let pA = obbSupport(posA, qA, halfA, bestNormal);
          let pB = obbSupport(posB, qB, halfB, -bestNormal);
          candidatePointWorld[0] = 0.5 * (pA + pB);
          candidateRAWorld[0] = pA - posA;
          candidateRBWorld[0] = pB - posB;
          candidatePenetration[0] = storedPenetration;
          candidateContactKey[0] = packFaceContactFeature(refIsA, refFeature, incFeature, 0u);
          candidateCount = 1u;
        }

        // Match the reference demo's compact manifold behavior more closely by
        // removing duplicate contact samples before persistent slot assignment.
        // The fixed-slot GPU backing store still keeps up to pairManifoldSlots
        // contacts, but each physical contact should appear at most once.
        if (candidateCount > 1u) {
          var compactPointWorld: array<vec3f, 8>;
          var compactRAWorld: array<vec3f, 8>;
          var compactRBWorld: array<vec3f, 8>;
          var compactPenetration: array<f32, 8>;
          var compactContactKey: array<u32, 8>;
          var compactCount = 0u;

          for (var c = 0u; c < candidateCount; c++) {
            let candidatePoint = candidatePointWorld[c];
            let candidateRA = candidateRAWorld[c];
            let candidateRB = candidateRBWorld[c];
            let candidatePen = candidatePenetration[c];
            let candidateKey = candidateContactKey[c];

            var duplicateIdx = 0xffffffffu;
            for (var k = 0u; k < compactCount; k++) {
              let dp = candidatePoint - compactPointWorld[k];
              let d2 = dot(dp, dp);
              if (d2 < 1e-6) {
                duplicateIdx = k;
                break;
              }
            }

            if (duplicateIdx != 0xffffffffu) {
              // Match the reference addContact behavior: keep the first
              // clipped-polygon contact and ignore later near-duplicate rows
              // instead of replacing them with a deeper sample.
              continue;
            }

            compactPointWorld[compactCount] = candidatePoint;
            compactRAWorld[compactCount] = candidateRA;
            compactRBWorld[compactCount] = candidateRB;
            compactPenetration[compactCount] = candidatePen;
            compactContactKey[compactCount] = candidateKey;
            compactCount += 1u;
          }

          for (var c = 0u; c < compactCount; c++) {
            candidatePointWorld[c] = compactPointWorld[c];
            candidateRAWorld[c] = compactRAWorld[c];
            candidateRBWorld[c] = compactRBWorld[c];
            candidatePenetration[c] = compactPenetration[c];
            candidateContactKey[c] = compactContactKey[c];
          }
          candidateCount = compactCount;
        }

        if (candidateCount > 1u) {
          // Preserve a deterministic in-manifold ordinal closer to the reference
          // contact list semantics instead of the reduction-selection order.
          for (var passIdx = 0u; passIdx < 8u; passIdx++) {
            if (passIdx + 1u >= candidateCount) { break; }
            let last = candidateCount - 1u - passIdx;
            for (var idx = 0u; idx < 8u; idx++) {
              if (idx >= last) { break; }
              let next = idx + 1u;
              let leftKey = candidateContactKey[idx];
              let rightKey = candidateContactKey[next];
              var shouldSwap = leftKey > rightKey;
              if (!shouldSwap && leftKey == rightKey) {
                shouldSwap = candidatePenetration[idx] < candidatePenetration[next];
              }
              if (!shouldSwap) { continue; }

              let swapPoint = candidatePointWorld[idx];
              candidatePointWorld[idx] = candidatePointWorld[next];
              candidatePointWorld[next] = swapPoint;

              let swapRA = candidateRAWorld[idx];
              candidateRAWorld[idx] = candidateRAWorld[next];
              candidateRAWorld[next] = swapRA;

              let swapRB = candidateRBWorld[idx];
              candidateRBWorld[idx] = candidateRBWorld[next];
              candidateRBWorld[next] = swapRB;

              let swapPen = candidatePenetration[idx];
              candidatePenetration[idx] = candidatePenetration[next];
              candidatePenetration[next] = swapPen;

              let swapKey = candidateContactKey[idx];
              candidateContactKey[idx] = candidateContactKey[next];
              candidateContactKey[next] = swapKey;
            }
          }
        }

        let staleBand = contactSlop + hysteresis;
        let separatingKeepBand = 0.25 * staleBand;
        let separatingKeepMinNormal = 2.0;
        // Tangential drift pruning removes ghost contacts from rotating pairs.
        // Lower thresholds are safer but can look softer (more manifold churn).
        let maxDrift = max(3.5 * contactSlop, 0.016);
        let maxDrift2 = maxDrift * maxDrift;
        let normalThreshold = 0.95;
        let invalidSlot = 0xffffffffu;
        let warmstartReasonNone = 0u;
        let warmstartReasonExactFeature = 1u;
        let warmstartReasonNormalGate = 3u;
        let warmstartReasonInactiveSlot = 4u;
        let manifoldStickCooldown = 0u;
        var manifoldTangentBasis = canonicalTangentBasis(bestNormal);
        if (manifoldHasPreferredT1) {
          manifoldTangentBasis = tangentBasisFromPreferredT1(bestNormal, manifoldPreferredT1);
        }
        var manifoldTangentAngle = tangentBasisAngleFromT1(bestNormal, manifoldTangentBasis.t1);

        // Snapshot the previous manifold state so the new compact manifold can
        // exact-match against last frame without depending on slot continuity.
        var prevSlotActive: array<u32, ${pairManifoldSlots}>;
        var prevSlotMeta: array<vec4f, ${pairManifoldSlots}>;
        var prevSlotArmA: array<vec4f, ${pairManifoldSlots}>;
        var prevSlotArmB: array<vec4f, ${pairManifoldSlots}>;
        var priorSpeculativePenaltyN = 1.0;
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let slot = pairBase + s;
          let slotInfo = loadContactMeta(pairContacts, slot);
          let slotEncodedInfo = bitcast<u32>(slotInfo.w);
          if (slotInfo.z >= 0.5 && contactInfoIsSpeculative(slotEncodedInfo)) {
            priorSpeculativePenaltyN = max(
              priorSpeculativePenaltyN,
              loadContactPenalty(pairContacts, slot).x,
            );
          }
          let slotActive = select(
            0u,
            1u,
            slotInfo.z >= 0.5 && !contactInfoIsSpeculative(slotEncodedInfo),
          );
          prevSlotActive[s] = slotActive;
          prevSlotMeta[s] = slotInfo;
          if (slotActive > 0u) {
            prevSlotArmA[s] = loadContactArmA(pairContacts, slot);
            prevSlotArmB[s] = loadContactArmB(pairContacts, slot);
          }
        }

        var prevSlotMatched: array<u32, ${pairManifoldSlots}>;
        var candidatePrevSlot: array<u32, ${pairManifoldSlots}>;
        var candidateWarmstartReason: array<u32, ${pairManifoldSlots}>;
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          prevSlotMatched[s] = 0u;
          candidatePrevSlot[s] = invalidSlot;
          candidateWarmstartReason[s] = warmstartReasonNone;
        }

        for (var c = 0u; c < candidateCount; c++) {
          let candidatePoint = candidatePointWorld[c];
          let candidateFeatureKey = candidateContactKey[c];
          var bestSlot = invalidSlot;
          var bestD2 = 1e30;
          for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
            if (prevSlotActive[s] == 0u || prevSlotMatched[s] != 0u) { continue; }
            if (decodePackedContactFeatureKey(prevSlotMeta[s].w) != candidateFeatureKey) { continue; }
            let oldRAStored = prevSlotArmA[s].xyz;
            let oldRBStored = prevSlotArmB[s].xyz;
            // A sphere has no material-space surface orientation. Its normal
            // arm is stored directly in world space so body spin cannot turn a
            // central normal contact into a false torque arm.
            let oldRA = select(qrot(qA, oldRAStored), oldRAStored, isSphereA);
            let oldRB = select(qrot(qB, oldRBStored), oldRBStored, isSphereB);
            let oldPoint = 0.5 * ((posA + oldRA) + (posB + oldRB));
            let dp = candidatePoint - oldPoint;
            let d2 = dot(dp, dp);
            // Feature identity alone is insufficient after a clipped vertex
            // disappears.  Reject a geometrically remote old row instead of
            // transplanting its lambda/penalty/stick anchor to another corner.
            if (d2 <= maxDrift2 && d2 < bestD2) {
              bestD2 = d2;
              bestSlot = s;
            }
          }
          if (bestSlot != invalidSlot) {
            prevSlotMatched[bestSlot] = 1u;
            candidatePrevSlot[c] = bestSlot;
            candidateWarmstartReason[c] = warmstartReasonExactFeature;
          }
        }

        var exactMatchPreferredT1 = vec3f(0.0);
        var exactMatchPreferredCount = 0u;
        for (var c = 0u; c < candidateCount; c++) {
          let matchedPrevSlot = candidatePrevSlot[c];
          if (matchedPrevSlot == invalidSlot) { continue; }
          if (candidateWarmstartReason[c] != warmstartReasonExactFeature) { continue; }
          let matchedPrevContact = pairBase + matchedPrevSlot;
          let prevNormal = loadContactNormalPen(pairContacts, matchedPrevContact).xyz;
          let prevTangentAngle = prevSlotArmA[matchedPrevSlot].w;
          let prevBasis = tangentBasisFromAngle(prevNormal, prevTangentAngle);
          exactMatchPreferredT1 += prevBasis.t1;
          exactMatchPreferredCount += 1u;
        }
        // Preserve manifold-level tangent sign continuity for exact matched
        // manifolds without abandoning the shared-basis convention.
        if (
          exactMatchPreferredCount > 0u
          && dot(exactMatchPreferredT1, manifoldTangentBasis.t1) < 0.0
        ) {
          manifoldTangentBasis = TangentBasis(-manifoldTangentBasis.t1, -manifoldTangentBasis.t2);
          manifoldTangentAngle = tangentBasisAngleFromT1(bestNormal, manifoldTangentBasis.t1);
          if (debugEnabled > 0u) {
            atomicAdd(&debugCounters[6], 1u);
          }
        }

        // Preserve exact matches in their previous slot. Unmatched contacts
        // can only occupy slots not reserved by another exact match. This
        // gives the fixed GPU manifold the same no-alias property as the
        // reference's temporary newContacts[8] merge without per-thread copies.
        var outputSlotMask = 0u;
        var writeCount = 0u;
        for (var c = 0u; c < candidateCount; c++) {
          let candidatePoint = candidatePointWorld[c];
          let candidateFeatureKey = candidateContactKey[c];
          let raWorld = candidateRAWorld[c];
          let rbWorld = candidateRBWorld[c];
          var contactRAWorld = raWorld;
          var contactRBWorld = rbWorld;
          var candidateStoredPen = candidatePenetration[c];
          let matchedPrevSlot = candidatePrevSlot[c];
          let matchedPrevContact = pairBase + matchedPrevSlot;
          let preserveWarmstart = matchedPrevSlot != invalidSlot;
          var warmstartDebugReason = candidateWarmstartReason[c];
          var tangentBasis = manifoldTangentBasis;
          var useStickingPoint = false;
          var canWarmstart = false;
          var exactFeatureWarmstart = false;
          var carryStick = 0u;
          var carryStickAnchorReuse = 0u;
          var lambdaNKeep = 0.0;
          var lambdaT = vec2f(0.0, 0.0);
          var carriedShadow = vec4f(0.0);
          var carriedDual = vec4f(0.0);
          var carriedPenalty = vec4f(1.0, 1.0, 1.0, frictionScale);

          // The fresh collision manifold, rather than a restored sticky
          // anchor, decides whether this is a real discrete contact. This
          // mirrors the reference's collide-then-merge ordering.
          let freshPointA = posA + raWorld;
          let freshPointB = posB + rbWorld;
          let freshRawPen = -dot(freshPointB - freshPointA, bestNormal);
          if (freshRawPen < 0.0) {
            continue;
          }

          if (preserveWarmstart) {
            let prevInfo = prevSlotMeta[matchedPrevSlot];
            let prevNormalPen = loadContactNormalPen(pairContacts, matchedPrevContact);
            let prevNormal = prevNormalPen.xyz;
            let prevFeature = i32(decodePackedBaseFeature(prevInfo.w));
            let prevFeatureKey = decodePackedContactFeatureKey(prevInfo.w);
            let prevStick = decodePackedStick(prevInfo.w);
            let prevTangentAngle = prevSlotArmA[matchedPrevSlot].w;
            let sameFeature = prevFeatureKey == candidateFeatureKey;
            exactFeatureWarmstart = sameFeature;
            var matchNormalDot = dot(prevNormal, bestNormal);
            if ((prevFeature >= 6) != (bestFeature >= 6)) {
              matchNormalDot = -1.0;
            }

            // Exact-key matches reuse prior state like the reference compact
            // manifold merge, while still requiring normal agreement.
            canWarmstart = preserveWarmstart && select(
              matchNormalDot > normalThreshold,
              true,
              sameFeature && !specialPair,
            );
            if (!canWarmstart) {
              warmstartDebugReason = warmstartReasonNormalGate;
            }

            if (canWarmstart) {
              let oldRAStored = prevSlotArmA[matchedPrevSlot].xyz;
              let oldRBStored = prevSlotArmB[matchedPrevSlot].xyz;
              let oldRA = select(qrot(qA, oldRAStored), oldRAStored, isSphereA);
              let oldRB = select(qrot(qB, oldRBStored), oldRBStored, isSphereB);
              let oldPointA = posA + oldRA;
              let oldPointB = posB + oldRB;
              useStickingPoint = prevStick > 0u && sameFeature && !specialPair;
              if (useStickingPoint) {
                if (debugEnabled > 0u) {
                  atomicAdd(&debugCounters[5], 1u);
                }
                // avbd-demo3d parity: exact sticky matches keep the old
                // anchors; otherwise the matched contact keeps the new anchors
                // and C0 is recomputed from whichever anchors were chosen.
                contactRAWorld = oldRA;
                contactRBWorld = oldRB;
                candidateStoredPen = max(
                  -dot((posB + contactRBWorld) - (posA + contactRAWorld), bestNormal),
                  0.0,
                );
              }
              carryStick = select(0u, prevStick, sameFeature && !specialPair);
              carryStickAnchorReuse = select(0u, 1u, useStickingPoint);
              let prevShadow = loadContactShadow(pairContacts, matchedPrevContact);
              let prevDual = loadContactDual(pairContacts, matchedPrevContact);
              lambdaNKeep = max(prevShadow.x, 0.0);
              carriedShadow = prevShadow;
              carriedDual = prevDual;
              carriedPenalty = loadContactPenalty(pairContacts, matchedPrevContact);

              let warmFriction = frictionStatic * frictionScale;
              let prevNLen2 = dot(prevNormal, prevNormal);
              let writeTangentBasis = tangentBasisFromAngle(bestNormal, manifoldTangentAngle);
              if (prevNLen2 > 1e-10) {
                let prevBasis = tangentBasisFromAngle(prevNormal, prevTangentAngle);
                let jtWorld = prevBasis.t1 * prevShadow.y + prevBasis.t2 * prevShadow.z;
                lambdaT = vec2f(
                  dot(jtWorld, writeTangentBasis.t1),
                  dot(jtWorld, writeTangentBasis.t2),
                );
                let maxT = warmFriction * lambdaNKeep;
                let maxT2 = maxT * maxT;
                let lt2 = dot(lambdaT, lambdaT);
                if (lt2 > maxT2 && lt2 > 1e-12) {
                  lambdaT *= maxT * inverseSqrt(lt2);
                }
                carriedShadow = vec4f(lambdaNKeep, lambdaT.x, lambdaT.y, prevShadow.w);
                let prevDualTB = prevDual.yz;
                let dualTWorld = prevBasis.t1 * prevDualTB.x + prevBasis.t2 * prevDualTB.y;
                let rotatedDualTB = vec2f(
                  dot(dualTWorld, writeTangentBasis.t1),
                    dot(dualTWorld, writeTangentBasis.t2),
                );
                carriedDual = vec4f(prevDual.x, rotatedDualTB, 0.0);
              }
            }
          } else {
            warmstartDebugReason = warmstartReasonInactiveSlot;
          }

          let finalPointA = posA + contactRAWorld;
          let finalPointB = posB + contactRBWorld;
          let finalRawPen = -dot(finalPointB - finalPointA, bestNormal);
          // Match the reference discrete manifold: SAT/clipping may use a
          // tolerance, but ordinary AVBD contacts exist only at overlap/touch.
          if (!useStickingPoint && finalRawPen < 0.0) {
            continue;
          }
          candidateStoredPen = max(finalRawPen, 0.0);

          let writeTangentBasis = tangentBasisFromAngle(bestNormal, manifoldTangentAngle);
          let tangentAngle = manifoldTangentAngle;
          let normalContactMargin = collisionMargin;
          let cachedC0Vec = finalPointA - finalPointB;
          // Match the public AVBD reference: the small margin is penetration
          // slop, so C = gap + margin with C >= 0 as the feasible half-space.
          let cachedC0N = -dot(cachedC0Vec, bestNormal) + normalContactMargin;
          // The reference recomputes the complete C0 vector every timestep,
          // even when a sticking feature keeps its previous local anchors.
          // Carrying last frame's tangential C0 here accumulates stale error
          // and can inject lateral energy into resting stacks.
          // Our stored tangent basis uses J_A=-t and J_B=+t, so its C0 must
          // use dot(xB-xA,t). This is the same constraint as avbd-demo3d in our
          // basis convention and avoids static-friction motoring.
          let cachedC0T1 = -dot(cachedC0Vec, writeTangentBasis.t1);
          let cachedC0T2 = -dot(cachedC0Vec, writeTangentBasis.t2);
          let storedRA = select(qrot(qconj(qA), contactRAWorld), contactRAWorld, isSphereA);
          let storedRB = select(qrot(qconj(qB), contactRBWorld), contactRBWorld, isSphereB);
          let sphereArmFlags = select(0u, 1u, isSphereA)
            | select(0u, 2u, isSphereB);
          var targetSlot = matchedPrevSlot;
          if (targetSlot == invalidSlot) {
            for (var slotCandidate = 0u; slotCandidate < ${pairManifoldSlots}u; slotCandidate++) {
              let slotBit = 1u << slotCandidate;
              if (prevSlotMatched[slotCandidate] == 0u && (outputSlotMask & slotBit) == 0u) {
                targetSlot = slotCandidate;
                break;
              }
            }
          }
          if (targetSlot == invalidSlot) { continue; }
          outputSlotMask |= 1u << targetSlot;
          let targetIndex = pairBase + targetSlot;
          // Preserve the actual pair/manifold slot order seen by the contact
          // pipeline instead of reconstructing a synthetic rank from (i, j).
          let manifoldSequence = manifold;
          storeContactMeta(pairContacts, targetIndex, vec4f(
            f32(i),
            f32(j),
            f32(manifoldSequence + 1u),
            packContactInfo(
              candidateFeatureKey,
              manifoldStickCooldown,
              select(0u, 1u, canWarmstart),
              carryStick,
              carryStickAnchorReuse,
              warmstartDebugReason,
              targetSlot,
            ),
          ));
          storeContactNormalPen(pairContacts, targetIndex, vec4f(bestNormal, candidateStoredPen));
          storeContactArmA(pairContacts, targetIndex, vec4f(storedRA, tangentAngle));
          storeContactArmB(pairContacts, targetIndex, vec4f(storedRB, f32(sphereArmFlags)));
          storeContactConstraintC0(pairContacts, targetIndex, vec4f(cachedC0N, cachedC0T1, cachedC0T2, 0.0));
          if (canWarmstart && exactFeatureWarmstart && matchedPrevSlot != invalidSlot) {
            // avbd-demo3d parity: exact matches carry the previous contact
            // object into the new slot first, then geometry/C0 are refreshed
            // from the chosen anchors above.
            storeContactShadow(pairContacts, targetIndex, carriedShadow);
            storeContactDual(pairContacts, targetIndex, carriedDual);
            storeContactPenalty(pairContacts, targetIndex, carriedPenalty);
            storeContactCache(pairContacts, targetIndex, loadContactCache(pairContacts, matchedPrevContact));
          } else {
            // Unmatched rows start fresh; prepareState owns any
            // subsequent warmstart decision.
            storeContactShadow(pairContacts, targetIndex, select(
              vec4f(0.0, 0.0, 0.0, frictionScale),
              vec4f(lambdaNKeep, lambdaT.x, lambdaT.y, frictionScale),
              canWarmstart,
            ));
            storeContactDual(pairContacts, targetIndex, vec4f(0.0));
            // A DCD row immediately following a swept TOI row is geometrically
            // fresh but not physically cold. Retain only the prior normal
            // stiffness so the impact cannot lose support on the next frame;
            // dual and tangential state still start from zero.
            //
            // A fresh sphere/static DCD row needs the same mass-scaled seed
            // even if the compact pool did not retain its swept row. This is
            // the exact dense-scene path used by runtime-thrown projectiles.
            storeContactPenalty(
              pairContacts,
              targetIndex,
              vec4f(
                max(priorSpeculativePenaltyN, staticSphereTargetPenaltyN),
                1.0,
                1.0,
                frictionScale,
              ),
            );
            storeContactCacheWord(pairContacts, targetIndex, 0u);
          }
          // The basis was already constructed above for C0.  Cache its first
          // tangent in the otherwise-unused cache payload so every nonlinear
          // solver sweep does not repeat the canonical basis, sin/cos and
          // projection work.  prepareState only updates cache.x and therefore
          // preserves these three words.
          storeContactTangentT1(pairContacts, targetIndex, writeTangentBasis.t1);
          writeCount += 1u;
        }

        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          if ((outputSlotMask & (1u << s)) == 0u) {
            deactivateContactSlot(pairContacts, pairBase + s, f32(i), f32(j));
          }
        }

        if (debugEnabled > 0u && isFloorPair) {
          if (writeCount == 0u) {
            atomicAdd(&debugCounters[11], 1u);
          } else {
            atomicAdd(&debugCounters[12], 1u);
          }
        }
      }

      fn hashIgnoredPairKey(key: u32) -> u32 {
        var value = key;
        value ^= value >> 16u;
        value *= 0x7feb352du;
        value ^= value >> 15u;
        value *= 0x846ca68bu;
        return value ^ (value >> 16u);
      }

      fn isIgnoredPair(
        ignoredPairHash: ptr<storage, array<u32>, read>,
        i: u32,
        j: u32,
      ) -> bool {
        let key = (i & 0xffffu) | ((j & 0xffffu) << 16u);
        let mask = ${ignoredPairHashCapacity - 1}u;
        var slot = hashIgnoredPairKey(key) & mask;
        var probe = 0u;
        loop {
          if (probe >= ${ignoredPairHashCapacity}u) { return false; }
          let base = slot * 2u;
          let state = ignoredPairHash[base + 1u];
          if (state == ${IGNORED_PAIR_HASH_STATE_EMPTY}u) { return false; }
          if (state == ${IGNORED_PAIR_HASH_STATE_OCCUPIED}u && ignoredPairHash[base] == key) {
            return true;
          }
          slot = (slot + 1u) & mask;
          probe += 1u;
        }
      }

      fn deactivateContactSlot(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        slot: u32,
        pairI: f32,
        pairJ: f32,
      ) {
        storeContactMeta(pairContacts, slot, vec4f(pairI, pairJ, 0.0, 0.0));
        storeContactCacheWord(pairContacts, slot, 0u);
      }

      fn deactivateManifold(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairBase: u32,
        pairI: f32,
        pairJ: f32,
      ) {
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          deactivateContactSlot(pairContacts, pairBase + s, pairI, pairJ);
        }
      }

      fn packFaceContactFeature(referenceIsA: bool, referenceAxis: u32, incidentAxis: u32, ordinal: u32) -> u32 {
        let featureType = select(1u, 0u, referenceIsA);
        return (ordinal & 0x7u)
          | ((incidentAxis & 0x3u) << 3u)
          | ((referenceAxis & 0x3u) << 5u)
          | ((featureType & 0x3u) << 7u);
      }

      fn packEdgeContactFeature(axisA: u32, axisB: u32) -> u32 {
        return ((axisB & 0x3u) << 3u)
          | ((axisA & 0x3u) << 5u)
          | (2u << 7u);
      }

      fn axisValue3(v: vec3f, axis: u32) -> f32 {
        if (axis == 0u) { return v.x; }
        if (axis == 1u) { return v.y; }
        return v.z;
      }

      fn axisVector3(axis: u32, axis0: vec3f, axis1: vec3f, axis2: vec3f) -> vec3f {
        if (axis == 0u) { return axis0; }
        if (axis == 1u) { return axis1; }
        return axis2;
      }

      fn dominantAbsAxis3(v: vec3f) -> u32 {
        let av = abs(v);
        if (av.y > av.x && av.y >= av.z) { return 1u; }
        if (av.z > av.x && av.z > av.y) { return 2u; }
        return 0u;
      }

      fn leastAxis3(v: vec3f) -> u32 {
        if (v.y < v.x && v.y <= v.z) { return 1u; }
        if (v.z < v.x && v.z < v.y) { return 2u; }
        return 0u;
      }

      fn decodePackedContactFeatureKey(encoded: f32) -> u32 {
        return bitcast<u32>(encoded) & 0x1FFu;
      }

      fn decodePackedContactFeatureTypeKey(featureKey: u32) -> u32 {
        return (featureKey >> 7u) & 0x3u;
      }

      fn decodePackedBaseFeature(encoded: f32) -> u32 {
        let featureKey = decodePackedContactFeatureKey(encoded);
        let featureType = decodePackedContactFeatureTypeKey(featureKey);
        let referenceAxis = (featureKey >> 5u) & 0x3u;
        let incidentAxis = (featureKey >> 3u) & 0x3u;
        if (featureType == 0u) {
          return referenceAxis;
        }
        if (featureType == 1u) {
          return 3u + referenceAxis;
        }
        return 6u + referenceAxis * 3u + incidentAxis;
      }

      fn decodePackedCooldown(encoded: f32) -> u32 {
        return (bitcast<u32>(encoded) >> 9u) & 0x7Fu;
      }

      fn decodePackedStick(encoded: f32) -> u32 {
        return (bitcast<u32>(encoded) >> 17u) & 0x1u;
      }

	      fn decodePackedStickAnchorReuse(encoded: f32) -> u32 {
	        return (bitcast<u32>(encoded) >> 18u) & 0x1u;
	      }

	      fn decodePackedWarmstartDebugReason(encoded: f32) -> u32 {
	        return (bitcast<u32>(encoded) >> 21u) & 0x7u;
	      }

      fn packContactInfo(
        featureKey: u32,
        cooldown: u32,
        preserveWarmstart: u32,
        stick: u32,
        stickAnchorReuse: u32,
        warmstartDebugReason: u32,
        logicalSlot: u32,
      ) -> f32 {
        return bitcast<f32>(contactInfoWithLogicalSlot(
          (featureKey & 0x1FFu)
          | ((cooldown & 0x7Fu) << 9u)
          | ((preserveWarmstart & 0x1u) << 16u)
          | ((stick & 0x1u) << 17u)
          | ((stickAnchorReuse & 0x1u) << 18u)
          | ((warmstartDebugReason & 0x7u) << 21u),
          logicalSlot,
        ));
      }

      struct SegResult {
        p: vec3f,
        q: vec3f,
      };

      fn sgnnz(x: f32) -> f32 {
        return select(1.0, -1.0, x < 0.0);
      }

      fn closestPointsSegments(p0: vec3f, p1: vec3f, q0: vec3f, q1: vec3f) -> SegResult {
        let u = p1 - p0;
        let v = q1 - q0;
        let w0 = p0 - q0;

        let a = dot(u, u);
        let b = dot(u, v);
        let c = dot(v, v);
        let d = dot(u, w0);
        let e = dot(v, w0);

        let D = a * c - b * b;
        let EPS = 1e-6;

        var sN: f32;
        var sD = D;
        var tN: f32;
        var tD = D;

        if (D < EPS) {
          sN = 0.0;
          sD = 1.0;
          tN = e;
          tD = c;
        } else {
          sN = b * e - c * d;
          tN = a * e - b * d;

          if (sN < 0.0) {
            sN = 0.0;
            tN = e;
            tD = c;
          } else if (sN > sD) {
            sN = sD;
            tN = e + b;
            tD = c;
          }
        }

        if (tN < 0.0) {
          tN = 0.0;
          if (-d < 0.0) {
            sN = 0.0;
          } else if (-d > a) {
            sN = sD;
          } else {
            sN = -d;
            sD = a;
          }
        } else if (tN > tD) {
          tN = tD;
          if (-d + b < 0.0) {
            sN = 0.0;
          } else if (-d + b > a) {
            sN = sD;
          } else {
            sN = -d + b;
            sD = a;
          }
        }

        let sc = select(0.0, sN / sD, abs(sN) > EPS);
        let tc = select(0.0, tN / tD, abs(tN) > EPS);

        let p = p0 + sc * u;
        let q = q0 + tc * v;
        return SegResult(p, q);
      }
    `;

    const pairShaderSource = this.compactMode
      ? buildCompactPairShaderSource(legacyPairShaderSource, {
        activeCandidateSlotsOffset: pairActiveCandidateSlotsOffset,
        slotRowOffset: persistentSlotRowOffset,
        outputMaskOffset: persistentOutputMaskOffset,
        freeRows0Offset: compactFreeRows0Offset,
        freeRows1Offset: compactFreeRows1Offset,
        poolKeyOffset: persistentPoolKeyOffset,
        poolResumeEpochOffset: persistentPoolResumeEpochOffset,
        pairManifoldSlots,
        contactPoolCapacity: this.compactContactPoolCapacity,
      })
      : legacyPairShaderSource;
    const pairShader = wgslFn(
      pairShaderSource,
      [qrot, qconj, obbSupport, tangentBasisHelpers, contactRecordHelpers, shapeEncodingHelpers],
    );

    const pairKernelStorageBuffers = 8;
    assertStorageBufferBudget('Contact Pair Generate', pairKernelStorageBuffers);

    this.pairKernel = pairShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', maxBodies).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      ignoredPairHash: storage(ignoredPairHash, 'uint', ignoredPairHash.count).toReadOnly(),
      persistentManifoldMap: this.compactMode
        ? storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic()
        : storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toReadOnly(),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      debugEnabled: uniform(0),
      bodyCount: uniform(0),
      pairCount: uniform(0),
      pairDispatchCount: uniform(0),
      useCandidatePairs: uniform(0),
      manifoldEpoch: uniform(0),
      floorDebugBody: uniform(0xffffffff),
      contactSlop: uniform(0.005),
      collisionMargin: uniform(AVBD_COLLISION_MARGIN),
      frictionStatic: uniform(AVBD_FRICTION_STATIC),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Pair Generate');

    this.pairKernelDebug = pairShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', maxBodies).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      ignoredPairHash: storage(ignoredPairHash, 'uint', ignoredPairHash.count).toReadOnly(),
      persistentManifoldMap: this.compactMode
        ? storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic()
        : storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toReadOnly(),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      debugEnabled: uniform(0),
      bodyCount: uniform(0),
      pairCount: uniform(0),
      pairDispatchCount: uniform(0),
      useCandidatePairs: uniform(0),
      manifoldEpoch: uniform(0),
      floorDebugBody: uniform(0xffffffff),
      contactSlop: uniform(0.005),
      collisionMargin: uniform(AVBD_COLLISION_MARGIN),
      frictionStatic: uniform(AVBD_FRICTION_STATIC),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Pair Generate');

    const clearPairBodyCountsShader = wgslFn(/* wgsl */`
      fn compute(
        pairBodyContactCounts: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        activeManifolds: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid == 0u) {
          atomicStore(&pairActivity[${pairActiveContactsOffset}u], 0u);
          atomicStore(&activeManifolds[0], 0u);
          atomicStore(&pairBodyContactIndices[${pairBodyOverflowCounterOffset}u], 0u);
        }
        if (gid >= bodyCount) { return; }
        atomicStore(&pairBodyContactCounts[gid], 0u);
        atomicStore(
          &pairBodyContactIndices[${pairBodyOverflowHeadOffset}u + gid],
          0xffffffffu,
        );
      }
    `);

    this.clearPairBodyCountsKernel = clearPairBodyCountsShader({
      pairBodyContactCounts: storage(pairBodyContactCounts, 'uint', maxBodies).toAtomic(),
      pairBodyContactIndices: storage(pairBodyContactIndices, 'uint', pairBodyContactIndices.count).toAtomic(),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      activeManifolds: storage(
        this.activeManifoldsAttr,
        'uint',
        this.activeManifoldsAttr.count,
      ).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Clear Body Counts');

    const clearDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        debugEnabled: u32,
      ) -> void {
        if (debugEnabled == 0u) { return; }
        // 24..29 are monotonic correctness/allocator diagnostics. Never clear
        // them per frame: sparse readback must still observe a loss from any
        // prior frame. Scene reset uploads zeros explicitly.
        for (var i = 0u; i < 24u; i++) {
          atomicStore(&debugCounters[i], 0u);
        }
      }
    `);

    this.clearDebugCountersKernel = clearDebugCountersShader({
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      debugEnabled: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Contact Clear Debug Counters');

    const clearActiveCandidateSlotsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        constraintSafetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        candidateDispatchIndirect: ptr<storage, array<u32>, read_write>,
        pairDispatchCount: u32,
        useCandidatePairs: u32,
      ) -> void {
        var exactCandidateCount = pairDispatchCount;
        if (useCandidatePairs > 0u) {
          exactCandidateCount = min(
            atomicLoad(&pairActivity[${pairCandidateIndicesOffset}u]),
            pairDispatchCount,
          );
        }
        // Broadphase writes the retained descriptor poolIndex sidecar into
        // this active-slot segment using the same deterministic prefix index
        // as the canonical candidate list. Preserve that prefix and let only
        // ordinary candidates append after it; no retained invocation needs
        // the contended active-list atomic.
        let retainedPrefix = select(
          0u,
          min(atomicLoad(&constraintSafetyGate[1u]), exactCandidateCount),
          useCandidatePairs > 0u,
        );
        atomicStore(
          &pairActivity[${pairActiveCandidateSlotsOffset}u],
          retainedPrefix,
        );
        candidateDispatchIndirect[0] =
          (exactCandidateCount + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        candidateDispatchIndirect[1] = 1u;
        candidateDispatchIndirect[2] = 1u;
      }
    `);

    this.clearActiveCandidateSlotsKernel = clearActiveCandidateSlotsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      constraintSafetyGate: storage(this.constraintSafetyGateAttr, 'uint', 2).toAtomic(),
      candidateDispatchIndirect: storage(this.candidateDispatchIndirectAttr, 'uint', 3),
      pairDispatchCount: uniform(0),
      useCandidatePairs: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Contact Clear Active Candidate Slots');
    assertStorageBufferBudget('Contact Clear Active Candidate Slots', 3);

    const buildActiveCandidateSlotsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        constraintSafetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        shapes: ptr<storage, array<vec4f>, read>,
        pairCount: u32,
        pairDispatchCount: u32,
        useCandidatePairs: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= pairDispatchCount) { return; }

        var manifold = gid;
        var retainedSleepingPair = false;
        if (useCandidatePairs > 0u) {
          let compactCandidateCount = atomicLoad(
            &pairActivity[${pairCandidateIndicesOffset}u]
          );
          if (gid >= compactCandidateCount) { return; }
          retainedSleepingPair = gid < min(
            atomicLoad(&constraintSafetyGate[1u]),
            compactCandidateCount,
          );
          let packedPair = atomicLoad(&pairActivity[${pairCandidateIndicesOffset}u + gid + 1u]);
          let i = packedPair & 0xFFFFu;
          let j = packedPair >> 16u;
          if (retainedSleepingPair) {
            // The aligned scratch row still contains this candidate's compact
            // descriptor poolIndex. Tag it in place; retained prefix rows are
            // never compacted or reordered, so resolve can use gid for the
            // canonical pair and the tagged payload for direct descriptor IO.
            manifold = atomicLoad(
              &pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u]
            );
            atomicStore(
              &pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u],
              manifold | ${RETAINED_SLEEPING_PAIR_BIT}u,
            );
            return;
          }
          if (i >= j) { return; }
          if (!candidateShapesOverlap(
            positions[i].xyz,
            quaternions[i],
            shapes[i],
            positions[j].xyz,
            quaternions[j],
            shapes[j],
          )) { return; }
        } else {
          if (gid >= pairCount) { return; }
          if (gid >= ${maxPairDispatchPairs}u) { return; }
        }

        let outIndex = atomicAdd(&pairActivity[${pairActiveCandidateSlotsOffset}u], 1u);
        let activeCapacity = select(
          ${maxPairDispatchPairs}u,
          ${pairActiveCandidateSlotsOffset - pairCandidateIndicesOffset - 1}u,
          useCandidatePairs > 0u,
        );
        if (outIndex < activeCapacity) {
          atomicStore(
            &pairActivity[${pairActiveCandidateSlotsOffset}u + outIndex + 1u],
            manifold,
          );
        }
      }

      fn candidateShapesOverlap(
        posA: vec3f,
        qA: vec4f,
        shapeA: vec4f,
        posB: vec3f,
        qB: vec4f,
        shapeB: vec4f,
      ) -> bool {
        let halfA = max(shapeA.yzw, vec3f(0.0));
        let halfB = max(shapeB.yzw, vec3f(0.0));
        let sphereA = decodeShapeType(shapeA.x) == SHAPE_TYPE_SPHERE;
        let sphereB = decodeShapeType(shapeB.x) == SHAPE_TYPE_SPHERE;
        let slop = 0.005;
        // Most swept-AABB candidates are not close enough for their shapes to
        // overlap.  Reject them before constructing OBB axes and running the
        // 15-axis SAT.  This sphere test is conservative (box radius is the
        // half-extents vector length), so it cannot remove a real contact.
        let boundA = select(length(halfA), halfA.x, sphereA);
        let boundB = select(length(halfB), halfB.x, sphereB);
        let centerDelta = posB - posA;
        let boundRadius = boundA + boundB + slop;
        if (dot(centerDelta, centerDelta) > boundRadius * boundRadius) {
          return false;
        }
        if (sphereA && sphereB) {
          let radius = halfA.x + halfB.x + slop;
          return dot(centerDelta, centerDelta) <= radius * radius;
        }

        let a0 = qrot(qA, vec3f(1.0, 0.0, 0.0));
        let a1 = qrot(qA, vec3f(0.0, 1.0, 0.0));
        let a2 = qrot(qA, vec3f(0.0, 0.0, 1.0));
        let b0 = qrot(qB, vec3f(1.0, 0.0, 0.0));
        let b1 = qrot(qB, vec3f(0.0, 1.0, 0.0));
        let b2 = qrot(qB, vec3f(0.0, 0.0, 1.0));

        if (sphereA) {
          return sphereBoxOverlap(posA, halfA.x, posB, halfB, b0, b1, b2, slop);
        }
        if (sphereB) {
          return sphereBoxOverlap(posB, halfB.x, posA, halfA, a0, a1, a2, slop);
        }

        let d = centerDelta;
        if (axisSeparates(d, a0, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, a1, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, a2, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, b0, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, b1, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, b2, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a0, b0), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a0, b1), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a0, b2), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a1, b0), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a1, b1), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a1, b2), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a2, b0), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a2, b1), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        if (axisSeparates(d, cross(a2, b2), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
        return true;
      }

      fn sphereBoxOverlap(
        spherePos: vec3f,
        sphereRadius: f32,
        boxPos: vec3f,
        boxHalf: vec3f,
        b0: vec3f,
        b1: vec3f,
        b2: vec3f,
        slop: f32,
      ) -> bool {
        let d = spherePos - boxPos;
        let local = vec3f(dot(d, b0), dot(d, b1), dot(d, b2));
        let closest = clamp(local, -boxHalf, boxHalf);
        let delta = local - closest;
        let radius = sphereRadius + slop;
        return dot(delta, delta) <= radius * radius;
      }

      fn axisSeparates(
        d: vec3f,
        axis: vec3f,
        halfA: vec3f,
        a0: vec3f,
        a1: vec3f,
        a2: vec3f,
        halfB: vec3f,
        b0: vec3f,
        b1: vec3f,
        b2: vec3f,
        slop: f32,
      ) -> bool {
        let len2 = dot(axis, axis);
        if (len2 <= 1e-12) { return false; }
        let radiusA = halfA.x * abs(dot(axis, a0))
          + halfA.y * abs(dot(axis, a1))
          + halfA.z * abs(dot(axis, a2));
        let radiusB = halfB.x * abs(dot(axis, b0))
          + halfB.y * abs(dot(axis, b1))
          + halfB.z * abs(dot(axis, b2));
        let positiveSeparation = abs(dot(d, axis)) - radiusA - radiusB;
        return positiveSeparation > 0.0
          && positiveSeparation * positiveSeparation > slop * slop * len2;
      }
    `, [qrot, shapeEncodingHelpers]);

    this.buildActiveCandidateSlotsKernel = buildActiveCandidateSlotsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      constraintSafetyGate: storage(this.constraintSafetyGateAttr, 'uint', 2).toAtomic(),
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      shapes: storage(shapes, 'vec4f', maxBodies).toReadOnly(),
      pairCount: uniform(0),
      pairDispatchCount: uniform(0),
      useCandidatePairs: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Build Active Candidate Slots');
    assertStorageBufferBudget('Contact Build Active Candidate Slots', 5);

    // CCD is deliberately a separate, opt-in shader graph.  When disabled we
    // construct and dispatch only the original four-binding DCD prefilter
    // above, so its generated WGSL and arithmetic remain byte-for-byte
    // independent of swept collision detection.
    if (this.ccdEnabled) {
      if (this.ccdDiagnosticsEnabled) {
        const clearCcdCountersShader = wgslFn(/* wgsl */`
          fn compute(
            ccdCounters: ptr<storage, array<atomic<u32>>, read_write>,
          ) -> void {
            for (var index = 0u; index < ${CCD_DIAGNOSTIC_WORDS}u; index++) {
              atomicStore(&ccdCounters[index], 0u);
            }
          }
        `);
        this.clearCcdCountersKernel = clearCcdCountersShader({
          ccdCounters: storage(this.ccdCountersAttr!, 'uint', CCD_DIAGNOSTIC_WORDS).toAtomic(),
        }).computeKernel([1, 1, 1]).setName('Contact CCD Clear Diagnostics');
        assertStorageBufferBudget('Contact CCD Clear Diagnostics', 1);
      }

      const buildActiveCandidateSlotsCcdShaderInstrumentedSource = /* wgsl */`
        fn compute(
          pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
          constraintSafetyGate: ptr<storage, array<atomic<u32>>, read_write>,
          positions: ptr<storage, array<vec4f>, read>,
          quaternions: ptr<storage, array<vec4f>, read>,
          shapes: ptr<storage, array<vec4f>, read>,
          inertialPose: ptr<storage, array<vec4f>, read>,
          persistentManifoldMap: ptr<storage, array<u32>, read>,
          ccdCounters: ptr<storage, array<atomic<u32>>, read_write>,
          pairCount: u32,
          pairDispatchCount: u32,
          useCandidatePairs: u32,
          workgroupId: vec3u,
          localId: vec3u,
        ) -> void {
          let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
          if (gid >= pairDispatchCount) { return; }

          var manifold = gid;
          var retainedSleepingPair = false;
          if (useCandidatePairs > 0u) {
            let compactCandidateCount = atomicLoad(
              &pairActivity[${pairCandidateIndicesOffset}u]
            );
            if (gid >= compactCandidateCount) { return; }
            retainedSleepingPair = gid < min(
              atomicLoad(&constraintSafetyGate[1u]),
              compactCandidateCount,
            );
            let packedPair = atomicLoad(
              &pairActivity[${pairCandidateIndicesOffset}u + gid + 1u]
            );
            let i = packedPair & 0xffffu;
            let j = packedPair >> 16u;
            if (retainedSleepingPair) {
              // Match the DCD prefilter exactly: the retained prefix is
              // already certified current-overlap work, and its aligned
              // active scratch row carries the compact descriptor poolIndex.
              manifold = atomicLoad(
                &pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u]
              );
              atomicStore(
                &pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u],
                manifold | ${RETAINED_SLEEPING_PAIR_BIT}u,
              );
              return;
            }
            if (i >= j) { return; }

            let currentOverlap = ccdCurrentCandidateShapesOverlap(
              positions[i].xyz,
              quaternions[i],
              shapes[i],
              positions[j].xyz,
              quaternions[j],
              shapes[j],
            );
            if (!currentOverlap) {
              let targetBaseA = i * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
              let targetBaseB = j * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
              if (!ccdShouldRunSpeculativeSweep(
                positions[i].xyz,
                quaternions[i],
                inertialPose[targetBaseA].xyz,
                inertialPose[targetBaseA + 1u],
                shapes[i],
                positions[j].xyz,
                quaternions[j],
                inertialPose[targetBaseB].xyz,
                inertialPose[targetBaseB + 1u],
                shapes[j],
              )) {
                atomicAdd(&ccdCounters[21u], 1u);
                // A brand-new quiet near pair can use the ordinary DCD
                // rejection immediately. A persistent pair must stay in the
                // stream so DCD rewrites/deactivates any prior one-step swept
                // row before the speculative kernel takes the same skip.
                if (!ccdPersistentPairExists(persistentManifoldMap, packedPair)) {
                  return;
                }
              } else {
                atomicAdd(&ccdCounters[0u], 1u);
                let swept = ccdSweepShapePair(
                  positions[i].xyz,
                  quaternions[i],
                  inertialPose[targetBaseA].xyz,
                  inertialPose[targetBaseA + 1u],
                  shapes[i],
                  positions[j].xyz,
                  quaternions[j],
                  inertialPose[targetBaseB].xyz,
                  inertialPose[targetBaseB + 1u],
                  shapes[j],
                  0.0,
                );
                ccdRecordPrefilterStatus(ccdCounters, swept.status, swept.iterations);
                // FAIL_OPEN must preserve the broadphase candidate so ordinary
                // DCD still receives it. Dropping it here turns any unsupported,
                // invalid or iteration-limited sweep into a collision false
                // negative. Only a certified finite CCD miss may reject it.
                if ((swept.status & CCD_STATUS_FAIL_OPEN) == 0u) {
                  if ((swept.status & CCD_STATUS_INITIAL_OVERLAP) != 0u) {
                    atomicAdd(&ccdCounters[5u], 1u);
                  } else {
                    if (swept.hit == 0u) { return; }
                    atomicAdd(&ccdCounters[1u], 1u);
                  }
                }
              }
            }
          } else {
            // Preserve the original brute-force fallback exactly: every
            // triangular pair reaches DCD, and the speculative pass decides
            // independently whether it needs a swept row.
            if (gid >= pairCount || gid >= ${maxPairDispatchPairs}u) { return; }
          }

          let outIndex = atomicAdd(
            &pairActivity[${pairActiveCandidateSlotsOffset}u],
            1u,
          );
          let activeCapacity = select(
            ${maxPairDispatchPairs}u,
            ${pairActiveCandidateSlotsOffset - pairCandidateIndicesOffset - 1}u,
            useCandidatePairs > 0u,
          );
          if (outIndex < activeCapacity) {
            atomicStore(
              &pairActivity[${pairActiveCandidateSlotsOffset}u + outIndex + 1u],
              manifold,
            );
          }
        }

        fn ccdRecordPrefilterStatus(
          ccdCounters: ptr<storage, array<atomic<u32>>, read_write>,
          status: u32,
          iterations: u32,
        ) {
          atomicAdd(&ccdCounters[14u], iterations);
          if ((status & CCD_STATUS_CHEAP_MISS) != 0u) {
            atomicAdd(&ccdCounters[15u], 1u);
          }
          if ((status & CCD_STATUS_CONSERVATIVE_ADVANCEMENT) != 0u) {
            atomicAdd(&ccdCounters[17u], 1u);
            atomicAdd(&ccdCounters[18u], iterations);
          }
          if ((status & CCD_STATUS_FAIL_OPEN) != 0u) {
            atomicAdd(&ccdCounters[4u], 1u);
          }
          if ((status & CCD_STATUS_UNSUPPORTED) != 0u) {
            atomicAdd(&ccdCounters[8u], 1u);
          }
          if ((status & CCD_STATUS_NON_FINITE) != 0u) {
            atomicAdd(&ccdCounters[9u], 1u);
          }
          if ((status & CCD_STATUS_ITERATION_LIMIT) != 0u) {
            atomicAdd(&ccdCounters[10u], 1u);
          }
          if ((status & CCD_STATUS_INVALID_INPUT) != 0u) {
            atomicAdd(&ccdCounters[11u], 1u);
          }
          if ((status & CCD_STATUS_NO_CLOSING) != 0u) {
            atomicAdd(&ccdCounters[13u], 1u);
          }
          if ((status & CCD_STATUS_CONSERVATIVE_FALLBACK) != 0u) {
            atomicAdd(&ccdCounters[23u], 1u);
          }
        }

        fn ccdPersistentPairExists(
          persistentManifoldMap: ptr<storage, array<u32>, read>,
          packedPair: u32,
        ) -> bool {
          let mask = ${persistentManifoldHashCapacity - 1}u;
          var hashSlot = ccdPersistentPairHash(packedPair) & mask;
          for (var probe = 0u; probe < ${PERSISTENT_HASH_MAX_PROBES}u; probe++) {
            let key = persistentManifoldMap[${persistentHashKeyOffset}u + hashSlot];
            if (key == packedPair) {
              // Even a temporarily unpublished/invalid index must fail open
              // into DCD cleanup. The later resolver owns protocol diagnosis.
              return true;
            }
            if (key == 0u) { return false; }
            hashSlot = (hashSlot + 1u) & mask;
          }
          // A probe-budget exhaustion is not permission to skip cleanup.
          return true;
        }

        fn ccdPersistentPairHash(key: u32) -> u32 {
          var value = key;
          value ^= value >> 16u;
          value *= 0x7feb352du;
          value ^= value >> 15u;
          value *= 0x846ca68bu;
          return value ^ (value >> 16u);
        }

        // This is the same conservative broad rejection + exact DCD overlap
        // predicate used by the original prefilter.  It is duplicated only in
        // the CCD pipeline so enabling/disabling CCD cannot rewrite the DCD
        // shader module.
        fn ccdCurrentCandidateShapesOverlap(
          posA: vec3f,
          qA: vec4f,
          shapeA: vec4f,
          posB: vec3f,
          qB: vec4f,
          shapeB: vec4f,
        ) -> bool {
          let halfA = max(shapeA.yzw, vec3f(0.0));
          let halfB = max(shapeB.yzw, vec3f(0.0));
          let sphereA = decodeShapeType(shapeA.x) == SHAPE_TYPE_SPHERE;
          let sphereB = decodeShapeType(shapeB.x) == SHAPE_TYPE_SPHERE;
          let slop = 0.005;
          let boundA = select(length(halfA), halfA.x, sphereA);
          let boundB = select(length(halfB), halfB.x, sphereB);
          let centerDelta = posB - posA;
          let boundRadius = boundA + boundB + slop;
          if (dot(centerDelta, centerDelta) > boundRadius * boundRadius) {
            return false;
          }
          if (sphereA && sphereB) {
            let radius = halfA.x + halfB.x + slop;
            return dot(centerDelta, centerDelta) <= radius * radius;
          }

          let a0 = qrot(qA, vec3f(1.0, 0.0, 0.0));
          let a1 = qrot(qA, vec3f(0.0, 1.0, 0.0));
          let a2 = qrot(qA, vec3f(0.0, 0.0, 1.0));
          let b0 = qrot(qB, vec3f(1.0, 0.0, 0.0));
          let b1 = qrot(qB, vec3f(0.0, 1.0, 0.0));
          let b2 = qrot(qB, vec3f(0.0, 0.0, 1.0));

          if (sphereA) {
            return ccdSphereBoxOverlap(posA, halfA.x, posB, halfB, b0, b1, b2, slop);
          }
          if (sphereB) {
            return ccdSphereBoxOverlap(posB, halfB.x, posA, halfA, a0, a1, a2, slop);
          }

          let d = centerDelta;
          if (ccdAxisSeparates(d, a0, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, a1, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, a2, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, b0, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, b1, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, b2, halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a0, b0), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a0, b1), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a0, b2), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a1, b0), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a1, b1), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a1, b2), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a2, b0), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a2, b1), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          if (ccdAxisSeparates(d, cross(a2, b2), halfA, a0, a1, a2, halfB, b0, b1, b2, slop)) { return false; }
          return true;
        }

        fn ccdSphereBoxOverlap(
          spherePos: vec3f,
          sphereRadius: f32,
          boxPos: vec3f,
          boxHalf: vec3f,
          b0: vec3f,
          b1: vec3f,
          b2: vec3f,
          slop: f32,
        ) -> bool {
          let d = spherePos - boxPos;
          let local = vec3f(dot(d, b0), dot(d, b1), dot(d, b2));
          let closest = clamp(local, -boxHalf, boxHalf);
          let delta = local - closest;
          let radius = sphereRadius + slop;
          return dot(delta, delta) <= radius * radius;
        }

        fn ccdAxisSeparates(
          d: vec3f,
          axis: vec3f,
          halfA: vec3f,
          a0: vec3f,
          a1: vec3f,
          a2: vec3f,
          halfB: vec3f,
          b0: vec3f,
          b1: vec3f,
          b2: vec3f,
          slop: f32,
        ) -> bool {
          let len2 = dot(axis, axis);
          if (len2 <= 1e-12) { return false; }
          let radiusA = halfA.x * abs(dot(axis, a0))
            + halfA.y * abs(dot(axis, a1))
            + halfA.z * abs(dot(axis, a2));
          let radiusB = halfB.x * abs(dot(axis, b0))
            + halfB.y * abs(dot(axis, b1))
            + halfB.z * abs(dot(axis, b2));
          let positiveSeparation = abs(dot(d, axis)) - radiusA - radiusB;
          return positiveSeparation > 0.0
            && positiveSeparation * positiveSeparation > slop * slop * len2;
        }
      `;
      const buildActiveCandidateSlotsCcdShader = wgslFn(
        this.ccdDiagnosticsEnabled
          ? buildActiveCandidateSlotsCcdShaderInstrumentedSource
          : stripCcdDiagnosticInstrumentation(buildActiveCandidateSlotsCcdShaderInstrumentedSource),
        [qrot, shapeEncodingHelpers, ccdHelpers],
      );

      this.buildActiveCandidateSlotsCcdKernel = buildActiveCandidateSlotsCcdShader({
        pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
        constraintSafetyGate: storage(this.constraintSafetyGateAttr, 'uint', 2).toAtomic(),
        positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
        quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
        shapes: storage(shapes, 'vec4f', maxBodies).toReadOnly(),
        inertialPose: storage(
          inertialPose,
          'vec4f',
          maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
        ).toReadOnly(),
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toReadOnly(),
        ...(this.ccdDiagnosticsEnabled
          ? { ccdCounters: storage(this.ccdCountersAttr!, 'uint', CCD_DIAGNOSTIC_WORDS).toAtomic() }
          : {}),
        pairCount: uniform(0),
        pairDispatchCount: uniform(0),
        useCandidatePairs: uniform(0),
        workgroupId,
        localId,
      }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact CCD Build Active Candidate Slots');
      assertStorageBufferBudget(
        'Contact CCD Build Active Candidate Slots',
        this.ccdDiagnosticsEnabled ? 8 : 7,
      );

      const legacySpeculativeContactShaderSource = /* wgsl */`
        fn compute(
          initialPose: ptr<storage, array<vec4f>, read>,
          inertialPose: ptr<storage, array<vec4f>, read>,
          shapes: ptr<storage, array<vec4f>, read>,
          pairContacts: ptr<storage, array<vec4f>, read_write>,
          pairActivity: ptr<storage, array<u32>, read>,
          ignoredPairHash: ptr<storage, array<u32>, read>,
          persistentManifoldMap: ptr<storage, array<u32>, read>,
          ccdFatalState: ptr<storage, array<atomic<u32>>, read_write>,
          ccdCounters: ptr<storage, array<atomic<u32>>, read_write>,
          bodyCount: u32,
          pairCount: u32,
          pairDispatchCount: u32,
          useCandidatePairs: u32,
          collisionMargin: f32,
          workgroupId: vec3u,
          localId: vec3u,
        ) -> void {
          let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
          if (gid >= pairDispatchCount) { return; }

          var manifold = gid;
          var i = 0u;
          var j = 0u;
          if (useCandidatePairs > 0u) {
            let activeCount = min(
              pairActivity[${pairActiveCandidateSlotsOffset}u],
              pairDispatchCount,
            );
            if (gid >= activeCount) { return; }
            manifold = pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u];
            if (manifold >= ${maxPersistentManifolds}u) { return; }
            let packedPair = persistentManifoldMap[${persistentPoolKeyOffset}u + manifold];
            i = packedPair & 0xffffu;
            j = packedPair >> 16u;
          } else {
            if (manifold >= pairCount || manifold >= ${maxPairDispatchPairs}u) { return; }
            j = u32(floor((1.0 + sqrt(1.0 + 8.0 * f32(manifold))) * 0.5));
            if (j == 0u) { return; }
            var triangular = (j * (j - 1u)) / 2u;
            if (triangular > manifold) {
              j -= 1u;
              triangular = (j * (j - 1u)) / 2u;
            }
            i = manifold - triangular;
          }
          if (i >= j || j >= bodyCount || manifold >= ${maxPairDispatchPairs}u) { return; }
          if (ccdSpecIgnoredPair(ignoredPairHash, i, j)) { return; }

          let initialBaseA = i * 2u;
          let initialBaseB = j * 2u;
          if (initialPose[initialBaseA].w + initialPose[initialBaseB].w == 0.0) { return; }
          let shapeA = shapes[i];
          let shapeB = shapes[j];
          if (!shapesCanCollide(shapeA, shapeB)) { return; }

          let pairBase = manifold * ${pairManifoldSlots}u;
          var dcdOwnsPair = false;
          for (var slotIndex = 0u; slotIndex < ${pairManifoldSlots}u; slotIndex++) {
            if (loadContactMeta(pairContacts, pairBase + slotIndex).z >= 0.5) {
              dcdOwnsPair = true;
              atomicAdd(&ccdCounters[12u], 1u);
              break;
            }
          }
          let spherePair =
            decodeShapeType(shapeA.x) == SHAPE_TYPE_SPHERE
            || decodeShapeType(shapeB.x) == SHAPE_TYPE_SPHERE;
          // DCD remains authoritative for ordinary overlap. A fast sphere
          // that began separated but ended overlapped needs the earlier swept
          // TOI row instead; otherwise one visual frame can bury the sphere
          // deeply before the next DCD solve recovers it.
          if (dcdOwnsPair && !spherePair) { return; }

          let targetBaseA = i * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
          let targetBaseB = j * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
          let startPosA = initialPose[initialBaseA].xyz;
          let startPosB = initialPose[initialBaseB].xyz;
          let startQA = initialPose[initialBaseA + 1u];
          let startQB = initialPose[initialBaseB + 1u];
          let targetPosA = inertialPose[targetBaseA].xyz;
          let targetPosB = inertialPose[targetBaseB].xyz;
          let targetQA = inertialPose[targetBaseA + 1u];
          let targetQB = inertialPose[targetBaseB + 1u];
          // DCD has already rewritten or deactivated every row for this active
          // pair. Returning here therefore cannot carry an old speculative
          // row into the solver stream.
          if (!ccdShouldRunSpeculativeSweep(
            startPosA,
            startQA,
            targetPosA,
            targetQA,
            shapeA,
            startPosB,
            startQB,
            targetPosB,
            targetQB,
            shapeB,
          )) {
            atomicAdd(&ccdCounters[22u], 1u);
            return;
          }
          atomicAdd(&ccdCounters[2u], 1u);
          let swept = ccdSweepShapePair(
            startPosA,
            startQA,
            targetPosA,
            targetQA,
            shapeA,
            startPosB,
            startQB,
            targetPosB,
            targetQB,
            shapeB,
            0.0,
          );
          ccdRecordSpeculativeStatus(ccdCounters, swept.status, swept.iterations);
          if ((swept.status & CCD_STATUS_FAIL_OPEN) != 0u) {
            // No finite separating sample survived from which a conservative
            // row could be manufactured. The first atomic is the always-on,
            // sticky production latch that pauses PhysicsEngine after its
            // asynchronous capacity/correctness poll. The second is optional
            // detail telemetry and is stripped from production shaders.
            atomicOr(&ccdFatalState[0u], swept.status);
            atomicAdd(&ccdCounters[24u], 1u);
            return;
          }
          if ((swept.status & CCD_STATUS_INITIAL_OVERLAP) != 0u) {
            atomicAdd(&ccdCounters[5u], 1u);
            return;
          }
          if (swept.hit == 0u) { return; }

          let normalLengthSquared = dot(swept.normal, swept.normal);
          if (!ccdScalarFinite(normalLengthSquared) || normalLengthSquared <= 1.0e-20) {
            atomicOr(
              &ccdFatalState[0u],
              CCD_STATUS_FAIL_OPEN | CCD_STATUS_NON_FINITE,
            );
            atomicAdd(&ccdCounters[4u], 1u);
            atomicAdd(&ccdCounters[9u], 1u);
            return;
          }
          let normal = swept.normal * inverseSqrt(normalLengthSquared);
          let shapeTypeA = decodeShapeType(shapeA.x);
          let shapeTypeB = decodeShapeType(shapeB.x);
          let sphereA = shapeTypeA == SHAPE_TYPE_SPHERE;
          let sphereB = shapeTypeB == SHAPE_TYPE_SPHERE;

          let toiPosA = mix(startPosA, targetPosA, swept.toi);
          let toiPosB = mix(startPosB, targetPosB, swept.toi);
          let toiQA = ccdInterpolateQuat(startQA, targetQA, swept.toi);
          let toiQB = ccdInterpolateQuat(startQB, targetQB, swept.toi);
          var anchors = CcdSpecAnchorPair(
            swept.witnessA,
            0.0,
            swept.witnessB,
            0.0,
          );
          if (!sphereA && !sphereB) {
            anchors = ccdStableObbAnchors(
              ccdMakeObbFrame(toiPosA, toiQA, shapeA.yzw),
              ccdMakeObbFrame(toiPosB, toiQB, shapeB.yzw),
              normal,
              swept.feature,
            );
          }

          let toiArmAWorld = anchors.pointA - toiPosA;
          let toiArmBWorld = anchors.pointB - toiPosB;
          let storedArmA = select(qrot(qconj(toiQA), toiArmAWorld), toiArmAWorld, sphereA);
          let storedArmB = select(qrot(qconj(toiQB), toiArmBWorld), toiArmBWorld, sphereB);
          let initialArmAWorld = select(qrot(startQA, storedArmA), storedArmA, sphereA);
          let initialArmBWorld = select(qrot(startQB, storedArmB), storedArmB, sphereB);
          let initialAnchorDelta = (startPosA + initialArmAWorld)
            - (startPosB + initialArmBWorld);
          let c0Normal = -dot(initialAnchorDelta, normal) + collisionMargin;
          if (!ccdScalarFinite(c0Normal)) {
            atomicOr(
              &ccdFatalState[0u],
              CCD_STATUS_FAIL_OPEN | CCD_STATUS_NON_FINITE,
            );
            atomicAdd(&ccdCounters[4u], 1u);
            atomicAdd(&ccdCounters[9u], 1u);
            return;
          }

          let contactIndex = pairBase;
          let sphereArmFlags = select(0u, 1u, sphereA)
            | select(0u, 2u, sphereB);
          let featureInfo = contactInfoWithLogicalSlot(
            ${CONTACT_INFO_SPECULATIVE_BIT}u
            | ${CONTACT_INFO_SPECULATIVE_FEATURE_BASE}u
            | (swept.feature & 0x1fu),
            0u,
          );
          storeContactMeta(pairContacts, contactIndex, vec4f(
            f32(i),
            f32(j),
            f32(manifold + 1u),
            bitcast<f32>(featureInfo),
          ));
          storeContactNormalPen(pairContacts, contactIndex, vec4f(normal, 0.0));
          storeContactArmA(pairContacts, contactIndex, vec4f(storedArmA, 0.0));
          storeContactArmB(pairContacts, contactIndex, vec4f(storedArmB, f32(sphereArmFlags)));
          storeContactConstraintC0(pairContacts, contactIndex, vec4f(c0Normal, 0.0, 0.0, 0.0));
          storeContactShadow(pairContacts, contactIndex, vec4f(0.0));
          storeContactDual(pairContacts, contactIndex, vec4f(0.0));
          // A speculative row gets only the remaining nonlinear rounds of
          // this substep. A dynamic body against an immovable target needs a
          // mass-scaled normal seed to reach TOI in the same rendered frame.
          // Dynamic/dynamic impacts deliberately keep the canonical k=1 seed:
          // their relative correction can move the lighter participant, while
          // a floor-scale seed would over-condition dense brick impacts.
          let speculativeInvMassA = initialPose[initialBaseA].w;
          let speculativeInvMassB = initialPose[initialBaseB].w;
          let speculativeInvMass = speculativeInvMassA + speculativeInvMassB;
          let staticTargetPair = speculativeInvMassA <= 0.0
            || speculativeInvMassB <= 0.0;
          let staticTargetPenaltyN = min(
            100000000.0,
            416666.67 / max(speculativeInvMass, 1.0e-6),
          );
          let speculativePenaltyN = select(
            1.0,
            staticTargetPenaltyN,
            staticTargetPair,
          );
          storeContactPenalty(
            pairContacts,
            contactIndex,
            vec4f(speculativePenaltyN, 1.0, 1.0, 0.0),
          );
          storeContactCache(pairContacts, contactIndex, vec4u(0u));
          storeContactTangentT1(
            pairContacts,
            contactIndex,
            canonicalTangentBasis(normal).t1,
          );
          for (var slotIndex = 1u; slotIndex < ${pairManifoldSlots}u; slotIndex++) {
            let contact = pairBase + slotIndex;
            storeContactMeta(pairContacts, contact, vec4f(f32(i), f32(j), 0.0, 0.0));
            storeContactCacheWord(pairContacts, contact, 0u);
          }
          atomicAdd(&ccdCounters[3u], 1u);
          if ((swept.status & CCD_STATUS_ANALYTIC) != 0u) {
            atomicAdd(&ccdCounters[6u], 1u);
          }
          if ((swept.status & CCD_STATUS_CONSERVATIVE_ADVANCEMENT) != 0u) {
            atomicAdd(&ccdCounters[7u], 1u);
          }
        }

        struct CcdSpecAnchorPair {
          pointA: vec3f,
          pointAPadding: f32,
          pointB: vec3f,
          pointBPadding: f32,
        };

        struct CcdSpecSegment {
          start: vec3f,
          startPadding: f32,
          end: vec3f,
          endPadding: f32,
        };

        fn ccdRecordSpeculativeStatus(
          ccdCounters: ptr<storage, array<atomic<u32>>, read_write>,
          status: u32,
          iterations: u32,
        ) {
          atomicAdd(&ccdCounters[14u], iterations);
          if ((status & CCD_STATUS_CHEAP_MISS) != 0u) {
            atomicAdd(&ccdCounters[16u], 1u);
          }
          if ((status & CCD_STATUS_CONSERVATIVE_ADVANCEMENT) != 0u) {
            atomicAdd(&ccdCounters[19u], 1u);
            atomicAdd(&ccdCounters[20u], iterations);
          }
          if ((status & CCD_STATUS_FAIL_OPEN) != 0u) {
            atomicAdd(&ccdCounters[4u], 1u);
          }
          if ((status & CCD_STATUS_UNSUPPORTED) != 0u) {
            atomicAdd(&ccdCounters[8u], 1u);
          }
          if ((status & CCD_STATUS_NON_FINITE) != 0u) {
            atomicAdd(&ccdCounters[9u], 1u);
          }
          if ((status & CCD_STATUS_ITERATION_LIMIT) != 0u) {
            atomicAdd(&ccdCounters[10u], 1u);
          }
          if ((status & CCD_STATUS_INVALID_INPUT) != 0u) {
            atomicAdd(&ccdCounters[11u], 1u);
          }
          if ((status & CCD_STATUS_NO_CLOSING) != 0u) {
            atomicAdd(&ccdCounters[13u], 1u);
          }
          if ((status & CCD_STATUS_CONSERVATIVE_FALLBACK) != 0u) {
            atomicAdd(&ccdCounters[23u], 1u);
          }
        }

        fn ccdSpecHashIgnoredPairKey(key: u32) -> u32 {
          var value = key;
          value ^= value >> 16u;
          value *= 0x7feb352du;
          value ^= value >> 15u;
          value *= 0x846ca68bu;
          return value ^ (value >> 16u);
        }

        fn ccdSpecIgnoredPair(
          ignoredPairHash: ptr<storage, array<u32>, read>,
          i: u32,
          j: u32,
        ) -> bool {
          let key = (i & 0xffffu) | ((j & 0xffffu) << 16u);
          let mask = ${ignoredPairHashCapacity - 1}u;
          var slot = ccdSpecHashIgnoredPairKey(key) & mask;
          var probe = 0u;
          loop {
            if (probe >= ${ignoredPairHashCapacity}u) { return false; }
            let base = slot * 2u;
            let state = ignoredPairHash[base + 1u];
            if (state == ${IGNORED_PAIR_HASH_STATE_EMPTY}u) { return false; }
            if (state == ${IGNORED_PAIR_HASH_STATE_OCCUPIED}u
              && ignoredPairHash[base] == key) {
              return true;
            }
            slot = (slot + 1u) & mask;
            probe += 1u;
          }
        }

        fn ccdClosestPointOnObb(point: vec3f, frame: CcdObbFrame) -> vec3f {
          let delta = point - frame.center;
          return frame.center
            + frame.axis0 * clamp(dot(delta, frame.axis0), -frame.half.x, frame.half.x)
            + frame.axis1 * clamp(dot(delta, frame.axis1), -frame.half.y, frame.half.y)
            + frame.axis2 * clamp(dot(delta, frame.axis2), -frame.half.z, frame.half.z);
        }

        fn ccdStableFacePoint(
          frame: CcdObbFrame,
          faceAxis: u32,
          outwardDirection: vec3f,
          otherCenter: vec3f,
        ) -> vec3f {
          let axis = ccdObbAxis(frame, faceAxis);
          let signValue = select(-1.0, 1.0, dot(axis, outwardDirection) >= 0.0);
          var point = frame.center + axis * ccdAxisValue(frame.half, faceAxis) * signValue;
          let centerDelta = otherCenter - frame.center;
          for (var tangentAxis = 0u; tangentAxis < 3u; tangentAxis++) {
            if (tangentAxis != faceAxis) {
              let tangent = ccdObbAxis(frame, tangentAxis);
              point += tangent * clamp(
                dot(centerDelta, tangent),
                -ccdAxisValue(frame.half, tangentAxis),
                ccdAxisValue(frame.half, tangentAxis),
              );
            }
          }
          return point;
        }

        fn ccdSupportEdge(
          frame: CcdObbFrame,
          edgeAxis: u32,
          supportDirection: vec3f,
        ) -> CcdSpecSegment {
          var center = frame.center;
          for (var axisIndex = 0u; axisIndex < 3u; axisIndex++) {
            if (axisIndex != edgeAxis) {
              let axis = ccdObbAxis(frame, axisIndex);
              let signValue = select(-1.0, 1.0, dot(axis, supportDirection) >= 0.0);
              center += axis * ccdAxisValue(frame.half, axisIndex) * signValue;
            }
          }
          let direction = ccdObbAxis(frame, edgeAxis)
            * ccdAxisValue(frame.half, edgeAxis);
          return CcdSpecSegment(center - direction, 0.0, center + direction, 0.0);
        }

        fn ccdClosestSegments(
          segmentA: CcdSpecSegment,
          segmentB: CcdSpecSegment,
        ) -> CcdSpecAnchorPair {
          let u = segmentA.end - segmentA.start;
          let v = segmentB.end - segmentB.start;
          let w = segmentA.start - segmentB.start;
          let a = dot(u, u);
          let b = dot(u, v);
          let c = dot(v, v);
          let d = dot(u, w);
          let e = dot(v, w);
          let denominator = a * c - b * b;
          var s = 0.0;
          var t = 0.0;
          if (a > 1.0e-12 && c > 1.0e-12) {
            if (denominator > 1.0e-12) {
              s = clamp((b * e - c * d) / denominator, 0.0, 1.0);
            }
            t = clamp((b * s + e) / c, 0.0, 1.0);
            s = clamp((b * t - d) / a, 0.0, 1.0);
          } else if (a > 1.0e-12) {
            s = clamp(-d / a, 0.0, 1.0);
          } else if (c > 1.0e-12) {
            t = clamp(e / c, 0.0, 1.0);
          }
          return CcdSpecAnchorPair(
            segmentA.start + u * s,
            0.0,
            segmentB.start + v * t,
            0.0,
          );
        }

        fn ccdStableObbAnchors(
          frameA: CcdObbFrame,
          frameB: CcdObbFrame,
          normal: vec3f,
          feature: u32,
        ) -> CcdSpecAnchorPair {
          if (feature < 3u) {
            let pointA = ccdStableFacePoint(frameA, feature, normal, frameB.center);
            return CcdSpecAnchorPair(
              pointA,
              0.0,
              ccdClosestPointOnObb(pointA, frameB),
              0.0,
            );
          }
          if (feature < 6u) {
            let pointB = ccdStableFacePoint(frameB, feature - 3u, -normal, frameA.center);
            return CcdSpecAnchorPair(
              ccdClosestPointOnObb(pointB, frameA),
              0.0,
              pointB,
              0.0,
            );
          }
          if (feature < 15u) {
            let edgeFeature = feature - 6u;
            return ccdClosestSegments(
              ccdSupportEdge(frameA, edgeFeature / 3u, normal),
              ccdSupportEdge(frameB, edgeFeature % 3u, -normal),
            );
          }
          return CcdSpecAnchorPair(
            ccdObbSupport(frameA, normal),
            0.0,
            ccdObbSupport(frameB, -normal),
            0.0,
          );
        }
      `;
      const speculativeContactShaderInstrumentedSource = this.compactMode
        ? buildCompactCcdShaderSource(legacySpeculativeContactShaderSource, {
          activeCandidateSlotsOffset: pairActiveCandidateSlotsOffset,
          slotRowOffset: persistentSlotRowOffset,
          outputMaskOffset: persistentOutputMaskOffset,
          freeRows0Offset: compactFreeRows0Offset,
          freeRows1Offset: compactFreeRows1Offset,
          poolKeyOffset: persistentPoolKeyOffset,
          poolResumeEpochOffset: persistentPoolResumeEpochOffset,
          pairManifoldSlots,
          contactPoolCapacity: this.compactContactPoolCapacity,
        })
        : legacySpeculativeContactShaderSource;
      const speculativeContactShaderSource = this.ccdDiagnosticsEnabled
        ? speculativeContactShaderInstrumentedSource
        : stripCcdDiagnosticInstrumentation(speculativeContactShaderInstrumentedSource);
      const speculativeContactShader = wgslFn(
        speculativeContactShaderSource,
        [qrot, qconj, tangentBasisHelpers, contactRecordHelpers, shapeEncodingHelpers, ccdHelpers],
      );

      this.speculativeContactKernel = speculativeContactShader({
        initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
        inertialPose: storage(
          inertialPose,
          'vec4f',
          maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
        ).toReadOnly(),
        shapes: storage(shapes, 'vec4f', maxBodies).toReadOnly(),
        pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
        pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toReadOnly(),
        ignoredPairHash: storage(ignoredPairHash, 'uint', ignoredPairHash.count).toReadOnly(),
        persistentManifoldMap: this.compactMode
          ? storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic()
          : storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toReadOnly(),
        ccdFatalState: storage(this.ccdFatalStateAttr, 'uint', 1).toAtomic(),
        ...(this.ccdDiagnosticsEnabled
          ? { ccdCounters: storage(this.ccdCountersAttr!, 'uint', CCD_DIAGNOSTIC_WORDS).toAtomic() }
          : {}),
        bodyCount: uniform(0),
        pairCount: uniform(0),
        pairDispatchCount: uniform(0),
        useCandidatePairs: uniform(0),
        ...(this.compactMode ? { currentEpoch: uniform(0) } : {}),
        collisionMargin: uniform(AVBD_COLLISION_MARGIN),
        workgroupId,
        localId,
      }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact CCD Speculative Rows');
      assertStorageBufferBudget(
        'Contact CCD Speculative Rows',
        this.ccdDiagnosticsEnabled ? 9 : 8,
      );
    }

    const countPersistentCacheMissesShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        persistentMissCount: ptr<storage, array<atomic<u32>>, read_write>,
        persistentResetIndirect: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          atomicLoad(&pairActivity[${pairActiveCandidateSlotsOffset}u]),
          pairDispatchCount,
        );
        if (gid >= activeCount) { return; }

        let activeAddress = ${pairActiveCandidateSlotsOffset}u + gid + 1u;
        let candidateSlot = atomicLoad(&pairActivity[activeAddress]);
        if (candidateSlot >= pairDispatchCount) { return; }
        let packedPair = atomicLoad(
          &pairActivity[${pairCandidateIndicesOffset}u + candidateSlot + 1u],
        );
        let i = packedPair & 0xffffu;
        let j = packedPair >> 16u;
        if (i >= j || packedPair == 0xffffffffu) { return; }

        let mask = ${persistentManifoldHashCapacity - 1}u;
        var hashSlot = hashPairKeyForPressureProbe(packedPair) & mask;
        var probe = 0u;
        var found = false;
        loop {
          if (probe >= ${PERSISTENT_HASH_MAX_PROBES}u) { break; }
          let existingKey = atomicLoad(
            &persistentManifoldMap[${persistentHashKeyOffset}u + hashSlot],
          );
          if (existingKey == packedPair) {
            let poolPlusOne = atomicLoad(
              &persistentManifoldMap[${persistentHashIndexOffset}u + hashSlot],
            );
            if (poolPlusOne > 0u && poolPlusOne <= ${maxPersistentManifolds}u) {
              let poolIndex = poolPlusOne - 1u;
              found = atomicLoad(
                &persistentManifoldMap[${persistentPoolKeyOffset}u + poolIndex],
              ) == packedPair;
            }
            break;
          }
          // EMPTY terminates an open-addressing lookup; TOMBSTONE continues.
          if (existingKey == 0u) { break; }
          hashSlot = (hashSlot + 1u) & mask;
          probe += 1u;
        }
        if (!found) {
          let previousMisses = atomicAdd(&persistentMissCount[0u], 1u);
          let allocatedEntries = min(
            atomicLoad(&persistentManifoldMap[0u]),
            ${maxPersistentManifolds}u,
          );
          let remainingEntries = ${maxPersistentManifolds}u - allocatedEntries;
          // previousMisses + 1 > remainingEntries means this exact set of new
          // pairs cannot fit. Publish the clear dispatch directly; every
          // writer stores the same value, and the atomic makes it race-free.
          // This removes a dedicated finalize dispatch from every AVBD step.
          if (previousMisses >= remainingEntries) {
            atomicStore(
              &persistentResetIndirect[0u],
              ${Math.ceil(persistentManifoldMap.count / WORKGROUP_SIZE)}u,
            );
          }
        }
      }

      fn hashPairKeyForPressureProbe(key: u32) -> u32 {
        var value = key;
        value ^= value >> 16u;
        value *= 0x7feb352du;
        value ^= value >> 15u;
        value *= 0x846ca68bu;
        return value ^ (value >> 16u);
      }
    `);

    this.countPersistentCacheMissesKernel = countPersistentCacheMissesShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
      persistentMissCount: storage(this.persistentMissCountAttr, 'uint', 1).toAtomic(),
      persistentResetIndirect: storage(this.persistentResetIndirectAttr, 'uint', 3).toAtomic(),
      pairDispatchCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Count Persistent Cache Misses');
    assertStorageBufferBudget('Contact Count Persistent Cache Misses', 4);

    const clearPersistentCacheShader = wgslFn(/* wgsl */`
      fn compute(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= ${persistentManifoldMap.count}u) { return; }
        atomicStore(&persistentManifoldMap[gid], 0u);
        if (gid == 0u) {
          atomicAdd(&debugCounters[28u], 1u);
        }
      }
    `);

    this.clearPersistentCacheKernel = clearPersistentCacheShader({
      persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Pressure Reset Persistent Cache');
    assertStorageBufferBudget('Contact Pressure Reset Persistent Cache', 2);

    const resolveCompactPersistentManifoldsShader = this.compactMode
      ? wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        pairContacts: ptr<storage, array<vec4f>, read>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchCount: u32,
        useCandidatePairs: u32,
        currentEpoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        if (useCandidatePairs == 0u) {
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.persistentHashProtocolFailure}u,
            currentEpoch,
          );
          return;
        }
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let rawActiveCount = atomicLoad(
          &pairActivity[${pairActiveCandidateSlotsOffset}u],
        );
        if (
          gid == 0u
          && rawActiveCount > ${pairActiveCandidateSlotsOffset - pairCandidateIndicesOffset - 1}u
        ) {
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.activeCandidateOverflow}u,
            currentEpoch,
          );
        }
        let activeCount = min(
          rawActiveCount,
          pairDispatchCount,
        );
        if (gid >= activeCount) { return; }

        let invalidPool = ${maxPersistentManifolds}u;
        let activeAddress = ${pairActiveCandidateSlotsOffset}u + gid + 1u;
        let rawCandidateSlot = atomicLoad(&pairActivity[activeAddress]);
        let retainedSleepingPair = (rawCandidateSlot & ${RETAINED_SLEEPING_PAIR_BIT}u) != 0u;
        let retainedPoolIndex = rawCandidateSlot & ${ACTIVE_PAIR_VALUE_MASK}u;
        // Retained rows occupy the deterministic candidate prefix, so gid is
        // their canonical candidate slot. The tagged payload is the compact
        // descriptor poolIndex written by broadphase, not another list index.
        let candidateSlot = select(retainedPoolIndex, gid, retainedSleepingPair);
        if (candidateSlot >= pairDispatchCount) {
          atomicStore(&pairActivity[activeAddress], invalidPool);
          return;
        }
        let packedPair = atomicLoad(
          &pairActivity[${pairCandidateIndicesOffset}u + candidateSlot + 1u],
        );
        let i = packedPair & 0xffffu;
        let j = packedPair >> 16u;
        if (i >= j || packedPair == 0xffffffffu) {
          atomicStore(&pairActivity[activeAddress], invalidPool);
          return;
        }

        if (retainedSleepingPair) {
          // This is the complete proof needed to bypass the persistent hash:
          // broadphase retained one descriptor, build-active preserved its
          // aligned poolIndex, and every physical row must still belong to the
          // exact pair/logical slot from the immediately preceding epoch.
          if (retainedPoolIndex >= ${maxPersistentManifolds}u) {
            recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
            return;
          }
          if (
            atomicLoad(
              &persistentManifoldMap[${persistentPoolKeyOffset}u + retainedPoolIndex]
            ) != packedPair
          ) {
            recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
            return;
          }
          let retainedOutputMask = atomicLoad(
            &persistentManifoldMap[${persistentOutputMaskOffset}u + retainedPoolIndex]
          );
          let retainedValidMask = ${(2 ** pairManifoldSlots) - 1}u;
          if (
            retainedOutputMask == 0u
            || (retainedOutputMask & ~retainedValidMask) != 0u
          ) {
            recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
            return;
          }
          let retainedPreviousEpoch = atomicLoad(
            &persistentManifoldMap[
              ${persistentPoolLastSeenOffset}u + retainedPoolIndex
            ]
          );
          if (
            retainedPreviousEpoch == 0u
            || compactEpochAge(currentEpoch, retainedPreviousEpoch) != 1u
          ) {
            recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
            return;
          }
          let retainedLogicalBase = retainedPoolIndex * ${pairManifoldSlots}u;
          for (var retainedSlot = 0u; retainedSlot < ${pairManifoldSlots}u; retainedSlot++) {
            if ((retainedOutputMask & (1u << retainedSlot)) == 0u) { continue; }
            let retainedRow = atomicLoad(
              &persistentManifoldMap[
                ${persistentSlotRowOffset}u + retainedLogicalBase + retainedSlot
              ]
            );
            if (retainedRow >= ${this.compactContactPoolCapacity}u) {
              recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
              return;
            }
            let retainedInfo = pairContacts[
              retainedRow * ${CONTACT_RECORD_VEC4S}u
            ];
            let retainedEncodedInfo = bitcast<u32>(retainedInfo.w);
            if (
              retainedInfo.z < 0.5
              || retainedInfo.x != f32(i)
              || retainedInfo.y != f32(j)
              || retainedInfo.z != f32(retainedPoolIndex + 1u)
              || (retainedEncodedInfo & ${CONTACT_INFO_SPECULATIVE_BIT}u) != 0u
              || (retainedEncodedInfo & 0x40000000u) != 0u
              || ((retainedEncodedInfo & 0x38000000u) >> 27u) != retainedSlot
            ) {
              recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
              return;
            }
          }
          let retainedClaimedEpoch = atomicExchange(
            &persistentManifoldMap[
              ${persistentPoolLastSeenOffset}u + retainedPoolIndex
            ],
            currentEpoch,
          );
          if (retainedClaimedEpoch != retainedPreviousEpoch) {
            recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
            return;
          }
          atomicStore(
            &pairActivity[activeAddress],
            retainedPoolIndex | ${RETAINED_SLEEPING_PAIR_BIT}u,
          );
          return;
        }

        let mask = ${persistentManifoldHashCapacity - 1}u;
        let startSlot = hashPairKey(packedPair) & mask;
        for (var restart = 0u; restart < ${PERSISTENT_HASH_MAX_RESTARTS}u; restart++) {
          var hashSlot = startSlot;
          var firstTombstone = 0xffffffffu;
          var insertionTarget = 0xffffffffu;
          for (var probe = 0u; probe < ${PERSISTENT_HASH_MAX_PROBES}u; probe++) {
            let keyAddress = ${persistentHashKeyOffset}u + hashSlot;
            let existingKey = atomicLoad(&persistentManifoldMap[keyAddress]);
            if (existingKey == packedPair) {
              let poolPlusOne = atomicLoad(
                &persistentManifoldMap[${persistentHashIndexOffset}u + hashSlot],
              );
              if (poolPlusOne == 0u) {
                // One invocation owns publication. A duplicate never spins on it.
                atomicStore(&pairActivity[activeAddress], invalidPool);
                return;
              }
              if (poolPlusOne > ${maxPersistentManifolds}u) {
                recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
                return;
              }
              let poolIndex = poolPlusOne - 1u;
              if (atomicLoad(&persistentManifoldMap[${persistentPoolKeyOffset}u + poolIndex]) != packedPair) {
                recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
                return;
              }
              let previousEpoch = atomicExchange(
                &persistentManifoldMap[${persistentPoolLastSeenOffset}u + poolIndex],
                currentEpoch,
              );
              if (previousEpoch == currentEpoch) {
                atomicStore(&pairActivity[activeAddress], invalidPool);
                return;
              }
              if (
                previousEpoch == 0u
                || compactEpochAge(currentEpoch, previousEpoch) > 1u
              ) {
                atomicStore(
                  &persistentManifoldMap[${persistentPoolResumeEpochOffset}u + poolIndex],
                  currentEpoch,
                );
              }
              let retainedExactHit = retainedSleepingPair
                && previousEpoch != 0u
                && compactEpochAge(currentEpoch, previousEpoch) == 1u;
              atomicStore(
                &pairActivity[activeAddress],
                poolIndex | select(
                  0u,
                  ${RETAINED_SLEEPING_PAIR_BIT}u,
                  retainedExactHit,
                ),
              );
              return;
            }
            if (existingKey == 0xffffffffu) {
              if (firstTombstone == 0xffffffffu) { firstTombstone = hashSlot; }
            } else if (existingKey == 0u) {
              insertionTarget = select(hashSlot, firstTombstone, firstTombstone != 0xffffffffu);
              break;
            }
            hashSlot = (hashSlot + 1u) & mask;
          }
          if (insertionTarget == 0xffffffffu) {
            recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
            return;
          }

          let keyAddress = ${persistentHashKeyOffset}u + insertionTarget;
          let indexAddress = ${persistentHashIndexOffset}u + insertionTarget;
          let expectedKey = atomicLoad(&persistentManifoldMap[keyAddress]);
          if (expectedKey != 0u && expectedKey != 0xffffffffu) { continue; }
          var claimed = false;
          for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
            let result = atomicCompareExchangeWeak(
              &persistentManifoldMap[keyAddress],
              expectedKey,
              packedPair,
            );
            if (result.exchanged) {
              claimed = true;
              break;
            }
            if (result.old_value != expectedKey) { break; }
          }
          if (!claimed) { continue; }

          let poolIndex = allocateDescriptor(persistentManifoldMap, currentEpoch);
          if (poolIndex >= ${maxPersistentManifolds}u) {
            atomicStore(&persistentManifoldMap[indexAddress], 0u);
            atomicStore(&persistentManifoldMap[keyAddress], 0xffffffffu);
            atomicAdd(&debugCounters[27u], 1u);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.persistentManifoldOverflow}u,
              currentEpoch,
            );
            atomicStore(&pairActivity[activeAddress], invalidPool);
            return;
          }

          atomicStore(&persistentManifoldMap[${persistentPoolKeyOffset}u + poolIndex], packedPair);
          atomicStore(&persistentManifoldMap[${persistentPoolLastSeenOffset}u + poolIndex], currentEpoch);
          atomicStore(&persistentManifoldMap[${persistentPoolResumeEpochOffset}u + poolIndex], currentEpoch);
          atomicStore(&persistentManifoldMap[${persistentOutputMaskOffset}u + poolIndex], 0u);
          let logicalBase = poolIndex * ${pairManifoldSlots}u;
          for (var slot = 0u; slot < ${pairManifoldSlots}u; slot++) {
            atomicStore(
              &persistentManifoldMap[${persistentSlotRowOffset}u + logicalBase + slot],
              ${this.compactContactPoolCapacity}u,
            );
          }
          // Hash index publication is the exact-match descriptor commit point.
          atomicStore(&persistentManifoldMap[indexAddress], poolIndex + 1u);
          atomicAdd(&persistentManifoldMap[${CompactContactPoolHeaderOffset.allocatedManifolds}u], 1u);
          atomicAdd(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.newManifoldsThisStep}u],
            1u,
          );
          atomicStore(&pairActivity[activeAddress], poolIndex);
          return;
        }
        recordProtocolFailure(debugCounters, persistentManifoldMap, activeAddress, pairActivity, invalidPool, currentEpoch);
      }

      fn compactEpochAge(currentEpoch: u32, previousEpoch: u32) -> u32 {
        return select(
          (${COMPACT_CONTACT_POOL_EPOCH_MAX}u - previousEpoch) + currentEpoch,
          currentEpoch - previousEpoch,
          currentEpoch >= previousEpoch,
        );
      }

      fn allocateDescriptor(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        currentEpoch: u32,
      ) -> u32 {
        // Reclaim completed in an earlier dispatch, so tail is stable while
        // resolve consumers reserve unique monotonic ring positions.
        let freePosition = atomicAdd(
          &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeHead}u],
          1u,
        );
        let stableTail = atomicLoad(
          &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldFreeTail}u],
        );
        if (freePosition < stableTail) {
          let poolIndex = atomicLoad(
            &persistentManifoldMap[
              ${compactFreeManifoldsOffset}u + freePosition % ${maxPersistentManifolds}u
            ],
          );
          if (poolIndex < ${maxPersistentManifolds}u) { return poolIndex; }
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
            currentEpoch,
          );
          return ${maxPersistentManifolds}u;
        }
        let highWater = atomicAdd(
          &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldHighWater}u],
          1u,
        );
        if (highWater < ${maxPersistentManifolds}u) { return highWater; }
        atomicMin(
          &persistentManifoldMap[${CompactContactPoolHeaderOffset.manifoldHighWater}u],
          ${maxPersistentManifolds}u,
        );
        return ${maxPersistentManifolds}u;
      }

      fn recordProtocolFailure(
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        activeAddress: u32,
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        invalidPool: u32,
        currentEpoch: u32,
      ) {
        atomicAdd(&debugCounters[27u], 1u);
        atomicAdd(&debugCounters[29u], 1u);
        recordCompactFatal(
          persistentManifoldMap,
          ${CompactContactPoolFatalBit.persistentHashProtocolFailure}u,
          currentEpoch,
        );
        atomicStore(&pairActivity[activeAddress], invalidPool);
      }

      fn recordCompactFatal(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        bit: u32,
        currentEpoch: u32,
      ) {
        atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
        for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
          let result = atomicCompareExchangeWeak(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
            0u,
            currentEpoch,
          );
          if (result.exchanged || result.old_value != 0u) { break; }
        }
      }

      fn hashPairKey(key: u32) -> u32 {
        var value = key;
        value ^= value >> 16u;
        value *= 0x7feb352du;
        value ^= value >> 15u;
        value *= 0x846ca68bu;
        return value ^ (value >> 16u);
      }
    `)
      : undefined;

    const resolvePersistentManifoldsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchCount: u32,
        useCandidatePairs: u32,
        currentEpoch: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        if (useCandidatePairs == 0u) { return; }
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCount = min(
          atomicLoad(&pairActivity[${pairActiveCandidateSlotsOffset}u]),
          pairDispatchCount,
        );
        if (gid >= activeCount) { return; }

        let invalidPool = ${maxPersistentManifolds}u;
        let activeAddress = ${pairActiveCandidateSlotsOffset}u + gid + 1u;
        let candidateSlot = atomicLoad(&pairActivity[activeAddress]);
        if (candidateSlot >= pairDispatchCount) {
          atomicStore(&pairActivity[activeAddress], invalidPool);
          return;
        }
        let packedPair = atomicLoad(
          &pairActivity[${pairCandidateIndicesOffset}u + candidateSlot + 1u],
        );
        let i = packedPair & 0xffffu;
        let j = packedPair >> 16u;
        if (i >= j || packedPair == 0xffffffffu) {
          atomicStore(&pairActivity[activeAddress], invalidPool);
          return;
        }

        let mask = ${persistentManifoldHashCapacity - 1}u;
        let startSlot = hashPairKey(packedPair) & mask;
        var restartCount = 0u;
        loop {
          // Never spin for table-capacity iterations. A protocol/capacity
          // failure is recoverable and observable; a non-returning dispatch is
          // not. Normal load at <= 50% reaches a key/empty slot far below these
          // limits.
          if (restartCount >= ${PERSISTENT_HASH_MAX_RESTARTS}u) {
            recordPersistentMapFailure(debugCounters, activeAddress, pairActivity, invalidPool);
            return;
          }

          var hashSlot = startSlot;
          var firstTombstone = 0xffffffffu;
          var insertionTarget = 0xffffffffu;
          var probe = 0u;
          loop {
            if (probe >= ${PERSISTENT_HASH_MAX_PROBES}u) {
              insertionTarget = firstTombstone;
              break;
            }
            let keyAddress = ${persistentHashKeyOffset}u + hashSlot;
            let existingKey = atomicLoad(&persistentManifoldMap[keyAddress]);
            if (existingKey == packedPair) {
              let indexAddress = ${persistentHashIndexOffset}u + hashSlot;
              let poolPlusOne = atomicLoad(&persistentManifoldMap[indexAddress]);
              if (poolPlusOne == 0u) {
                // Another invocation is publishing this same pair.  This
                // candidate is a duplicate; the owner invocation will emit the
                // manifold, so waiting here would only risk GPU starvation.
                atomicStore(&pairActivity[activeAddress], invalidPool);
                return;
              }
              if (poolPlusOne > ${maxPersistentManifolds}u) {
                recordPersistentMapFailure(debugCounters, activeAddress, pairActivity, invalidPool);
                return;
              }
              let poolIndex = poolPlusOne - 1u;
              if (atomicLoad(&persistentManifoldMap[${persistentPoolKeyOffset}u + poolIndex]) != packedPair) {
                recordPersistentMapFailure(debugCounters, activeAddress, pairActivity, invalidPool);
                return;
              }
              let previousEpoch = atomicExchange(
                &persistentManifoldMap[${persistentPoolLastSeenOffset}u + poolIndex],
                currentEpoch,
              );
              if (previousEpoch == currentEpoch) {
                // Deduplicate repeated broadphase entries before the pair
                // kernel; two invocations must never write one manifold slot.
                atomicStore(&pairActivity[activeAddress], invalidPool);
                return;
              }
              if (previousEpoch == 0u || contactEpochAge(currentEpoch, previousEpoch) > 1u) {
                atomicStore(
                  &persistentManifoldMap[${persistentPoolResumeEpochOffset}u + poolIndex],
                  currentEpoch,
                );
              }
              atomicStore(&pairActivity[activeAddress], poolIndex);
              return;
            }
            if (existingKey == 0xffffffffu) {
              if (firstTombstone == 0xffffffffu) { firstTombstone = hashSlot; }
            } else if (existingKey == 0u) {
              insertionTarget = select(hashSlot, firstTombstone, firstTombstone != 0xffffffffu);
              break;
            }
            hashSlot = (hashSlot + 1u) & mask;
            probe += 1u;
          }

          if (insertionTarget == 0xffffffffu) {
            recordPersistentMapFailure(debugCounters, activeAddress, pairActivity, invalidPool);
            return;
          }
          let insertionKeyAddress = ${persistentHashKeyOffset}u + insertionTarget;
          let insertionIndexAddress = ${persistentHashIndexOffset}u + insertionTarget;
          let expectedKey = atomicLoad(&persistentManifoldMap[insertionKeyAddress]);
          if (expectedKey != 0u && expectedKey != 0xffffffffu) {
            restartCount += 1u;
            continue;
          }

          // Avoid claiming a hash entry only to turn it into a tombstone when
          // the pool is already known to be full. Existing keys were resolved
          // during the probe above, so this early exit cannot hide a hit.
          if (
            atomicLoad(&persistentManifoldMap[1u]) == 0u
            && atomicLoad(&persistentManifoldMap[0u]) >= ${maxPersistentManifolds}u
          ) {
            atomicAdd(&debugCounters[27u], 1u);
            atomicStore(&pairActivity[activeAddress], invalidPool);
            return;
          }

          var claimedSlot = false;
          var claimAttempts = 0u;
          loop {
            if (claimAttempts >= ${PERSISTENT_CAS_MAX_ATTEMPTS}u) { break; }
            let claimed = atomicCompareExchangeWeak(
              &persistentManifoldMap[insertionKeyAddress],
              expectedKey,
              packedPair,
            );
            if (claimed.exchanged) {
              claimedSlot = true;
              break;
            }
            if (claimed.old_value != expectedKey) { break; }
            claimAttempts += 1u;
          }
          if (!claimedSlot) {
            restartCount += 1u;
            continue;
          }

          let poolIndex = allocatePersistentPool(persistentManifoldMap);
          if (poolIndex >= ${maxPersistentManifolds}u) {
            // Never leave a published key with index zero.  Revert even a
            // formerly-empty slot to TOMBSTONE: returning it to EMPTY could
            // create a probing hole after concurrent insertions passed it.
            atomicStore(&persistentManifoldMap[insertionIndexAddress], 0u);
            var revertedSlot = false;
            var revertChanged = false;
            var revertAttempts = 0u;
            loop {
              if (revertAttempts >= ${PERSISTENT_CAS_MAX_ATTEMPTS}u) { break; }
              let reverted = atomicCompareExchangeWeak(
                &persistentManifoldMap[insertionKeyAddress],
                packedPair,
                0xffffffffu,
              );
              if (reverted.exchanged) {
                revertedSlot = true;
                break;
              }
              if (reverted.old_value != packedPair) {
                revertChanged = true;
                break;
              }
              revertAttempts += 1u;
            }
            if (!revertedSlot && !revertChanged) {
              // This invocation owns the unpublished key. A bounded weak-CAS
              // pseudo-failure must not leave an immortal key with index zero.
              atomicStore(&persistentManifoldMap[insertionKeyAddress], 0xffffffffu);
              atomicAdd(&debugCounters[29u], 1u);
            }
            atomicAdd(&debugCounters[27u], 1u);
            atomicStore(&pairActivity[activeAddress], invalidPool);
            return;
          }

          atomicStore(&persistentManifoldMap[${persistentPoolKeyOffset}u + poolIndex], packedPair);
          atomicStore(&persistentManifoldMap[${persistentPoolLastSeenOffset}u + poolIndex], currentEpoch);
          atomicStore(&persistentManifoldMap[${persistentPoolHashSlotOffset}u + poolIndex], insertionTarget + 1u);
          atomicStore(&persistentManifoldMap[${persistentPoolResumeEpochOffset}u + poolIndex], currentEpoch);
          // Publish the hash index last.  Readers that encounter index zero
          // know this is an in-flight duplicate and leave it to this owner.
          atomicStore(&persistentManifoldMap[insertionIndexAddress], poolIndex + 1u);
          atomicAdd(&persistentManifoldMap[2u], 1u);
          atomicStore(&pairActivity[activeAddress], poolIndex);
          return;
        }
      }

      fn allocatePersistentPool(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
      ) -> u32 {
        // Fresh slots are an append-only arena within one cache generation.
        // atomicAdd reserves exactly one slot without a contended retry loop;
        // this avoids both GPU starvation and false "pool full" reports when
        // thousands of new pairs arrive in the same frame.
        let highWater = atomicAdd(&persistentManifoldMap[0u], 1u);
        if (highWater < ${maxPersistentManifolds}u) { return highWater; }
        atomicMin(&persistentManifoldMap[0u], ${maxPersistentManifolds}u);
        return ${maxPersistentManifolds}u;
      }

      fn contactEpochAge(currentEpoch: u32, previousEpoch: u32) -> u32 {
        return select(
          (${COMPACT_CONTACT_POOL_EPOCH_MAX}u - previousEpoch) + currentEpoch,
          currentEpoch - previousEpoch,
          currentEpoch >= previousEpoch,
        );
      }

      fn recordPersistentMapFailure(
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        activeAddress: u32,
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        invalidPool: u32,
      ) {
        atomicAdd(&debugCounters[27u], 1u);
        atomicAdd(&debugCounters[29u], 1u);
        atomicStore(&pairActivity[activeAddress], invalidPool);
      }

      fn hashPairKey(key: u32) -> u32 {
        var value = key;
        value ^= value >> 16u;
        value *= 0x7feb352du;
        value ^= value >> 15u;
        value *= 0x846ca68bu;
        return value ^ (value >> 16u);
      }
    `);

    const selectedResolvePersistentManifoldsShader = this.compactMode
      ? resolveCompactPersistentManifoldsShader!
      : resolvePersistentManifoldsShader;
    this.resolvePersistentManifoldsKernel = selectedResolvePersistentManifoldsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      ...(this.compactMode ? {
        pairContacts: storage(
          pairContacts,
          'vec4f',
          maxPairContacts * CONTACT_RECORD_VEC4S,
        ).toReadOnly(),
      } : {}),
      persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      pairDispatchCount: uniform(0),
      useCandidatePairs: uniform(0),
      currentEpoch: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName(
      this.compactMode
        ? 'Contact Compact Resolve Persistent Pair Manifolds'
        : 'Contact Resolve Persistent Pair Manifolds',
    );
    assertStorageBufferBudget('Contact Resolve Persistent Pair Manifolds', this.compactMode ? 4 : 3);

    const buildPairDispatchArgsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchIndirect: ptr<storage, array<u32>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        persistentMissCount: ptr<storage, array<atomic<u32>>, read_write>,
        persistentProbeIndirect: ptr<storage, array<u32>, read_write>,
        persistentResetIndirect: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchCount: u32,
      ) -> void {
        let dispatchThreads = min(
          atomicLoad(&pairActivity[${pairActiveCandidateSlotsOffset}u]),
          pairDispatchCount,
        );

        let workgroups = (dispatchThreads + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        pairDispatchIndirect[0] = workgroups;
        pairDispatchIndirect[1] = 1u;
        pairDispatchIndirect[2] = 1u;

        let allocatedEntries = min(
          atomicLoad(&persistentManifoldMap[0u]),
          ${maxPersistentManifolds}u,
        );
        let activeCandidates = min(dispatchThreads, ${maxPersistentManifolds}u);
        let remainingEntries = ${maxPersistentManifolds}u - allocatedEntries;
        // At low occupancy, activeCandidates is a conservative upper bound on
        // this pass's misses. At >= 75%, perform the exact lookup even for a
        // small pass so a large stable hit set keeps all warm-start state.
        let pressureProbe = allocatedEntries * 4u >= ${maxPersistentManifolds * 3}u
          || activeCandidates > remainingEntries;
        let probeWorkgroups = (activeCandidates + ${WORKGROUP_SIZE - 1}u) / ${WORKGROUP_SIZE}u;
        atomicStore(&persistentMissCount[0u], 0u);
        persistentProbeIndirect[0u] = select(0u, probeWorkgroups, pressureProbe);
        persistentProbeIndirect[1u] = 1u;
        persistentProbeIndirect[2u] = 1u;
        atomicStore(&persistentResetIndirect[0u], 0u);
        atomicStore(&persistentResetIndirect[1u], 1u);
        atomicStore(&persistentResetIndirect[2u], 1u);
      }
    `);

    this.buildPairDispatchArgsKernel = buildPairDispatchArgsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      pairDispatchIndirect: storage(this.pairDispatchIndirectAttr, 'uint', 3),
      persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
      persistentMissCount: storage(this.persistentMissCountAttr, 'uint', 1).toAtomic(),
      persistentProbeIndirect: storage(this.persistentProbeIndirectAttr, 'uint', 3),
      persistentResetIndirect: storage(this.persistentResetIndirectAttr, 'uint', 3).toAtomic(),
      pairDispatchCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Contact Build Dispatch Args');
    assertStorageBufferBudget('Contact Build Dispatch Args', 6);

    const buildPairBodyListsShaderSource = /* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        positions: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactCounts: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        pairDispatchCount: u32,
        debugEnabled: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCandidateCount = min(atomicLoad(&pairActivity[${pairActiveCandidateSlotsOffset}u]), pairDispatchCount);
        if (gid >= activeCandidateCount) { return; }
        let manifold = atomicLoad(&pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u]);
        if (manifold >= ${maxPersistentManifolds}u) { return; }

        let pairBase = manifold * ${pairManifoldSlots}u;
        var manifoldHasActive = false;
        var activeRows: array<u32, ${pairManifoldSlots}>;
        var activeRowCount = 0u;
        var bodyI = 0xffffffffu;
        var bodyJ = 0xffffffffu;
        var dynamicI = false;
        var dynamicJ = false;
        for (var s = 0u; s < ${pairManifoldSlots}u; s++) {
          let p = pairBase + s;
          let pairInfo = loadContactMeta(pairContacts, p);
          if (pairInfo.z < 0.5) { continue; }
          manifoldHasActive = true;

          let i = u32(pairInfo.x);
          let j = u32(pairInfo.y);
          if (i >= bodyCount || j >= bodyCount) { continue; }
          let rowDynamicI = positions[i].w > 0.0;
          let rowDynamicJ = positions[j].w > 0.0;
          if (!rowDynamicI && !rowDynamicJ) { continue; }

          if (activeRowCount == 0u) {
            bodyI = i;
            bodyJ = j;
            dynamicI = rowDynamicI;
            dynamicJ = rowDynamicJ;
          } else if (i != bodyI || j != bodyJ) {
            // A manifold is owned by exactly one canonical pair. Treat a
            // corrupt mixed-endpoint row as dropped instead of attaching its
            // physical row to the wrong bodies.
            atomicAdd(&debugCounters[25], 1u);
            continue;
          }
          activeRows[activeRowCount] = p;
          activeRowCount += 1u;
        }

        var reservationI = emptyPairBodyReservation();
        if (dynamicI && activeRowCount > 0u) {
          reservationI = reservePairBodyContacts(
            bodyI,
            activeRowCount,
            pairBodyContactCounts,
            pairBodyContactIndices,
            debugCounters,
          );
        }
        var reservationJ = emptyPairBodyReservation();
        if (dynamicJ && activeRowCount > 0u) {
          reservationJ = reservePairBodyContacts(
            bodyJ,
            activeRowCount,
            pairBodyContactCounts,
            pairBodyContactIndices,
            debugCounters,
          );
        }

        var keepManifold = activeRowCount > 0u;
        if (dynamicI) { keepManifold = keepManifold && reservationI.wrote; }
        if (dynamicJ) { keepManifold = keepManifold && reservationJ.wrote; }
        if (dynamicI && dynamicJ && !keepManifold) {
          // Dynamic-dynamic contacts require both endpoint reservations. A
          // failed side invalidates every already-reserved ref as one unit.
          atomicAdd(&debugCounters[25], activeRowCount);
        }

        // Reserve the canonical output as one manifold-sized block. The old
        // per-row atomicAdd serialized every live contact on the same global
        // word (more than half a million atomics in the 50K scene). This keeps
        // the identical accepted rows and slot order while issuing one atomic
        // per manifold and makes pair-shared solver reads naturally local.
        var activeBlockStart = 0u;
        if (keepManifold) {
          activeBlockStart = atomicAdd(
            &pairActivity[${pairActiveContactsOffset}u],
            activeRowCount,
          );
        }

        for (var rowOrdinal = 0u; rowOrdinal < activeRowCount; rowOrdinal++) {
          let physicalRow = activeRows[rowOrdinal];
          let storedRow = select(${maxPairContacts}u, physicalRow, keepManifold);
          if (dynamicI && reservationI.wrote) {
            storeReservedPairBodyContact(
              reservationI,
              bodyI,
              rowOrdinal,
              storedRow,
              pairBodyContactIndices,
            );
          }
          if (dynamicJ && reservationJ.wrote) {
            storeReservedPairBodyContact(
              reservationJ,
              bodyJ,
              rowOrdinal,
              storedRow,
              pairBodyContactIndices,
            );
          }
          if (!keepManifold) { continue; }

          // Preserve the immutable canonical active stream: it still contains
          // one raw physical-row ref per accepted contact, in manifold-slot
          // order, and is independent of the adjacency reservation layout.
          let activeWrite = activeBlockStart + rowOrdinal;
          if (activeWrite < ${maxActivePairContacts}u) {
            atomicStore(
              &pairActivity[${pairActiveContactsOffset}u + activeWrite + 1u],
              physicalRow,
            );
          } else {
            atomicAdd(&debugCounters[26], 1u);
          }
        }

        if (manifoldHasActive) {
          if (debugEnabled > 0u) {
            atomicAdd(&debugCounters[1], 1u);
          }
        }
      }

      struct PairBodyReservation {
        wrote: bool,
        slotStart: u32,
        directCount: u32,
        spillStart: u32,
        spillCount: u32,
        previousSpillHead: u32,
      };

      fn emptyPairBodyReservation() -> PairBodyReservation {
        return PairBodyReservation(false, 0u, 0u, 0u, 0u, 0xffffffffu);
      }

      fn reservePairBodyContacts(
        body: u32,
        contactCount: u32,
        pairBodyContactCounts: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
      ) -> PairBodyReservation {
        // One atomic reservation replaces one count atomic per manifold row.
        // The returned interval remains contiguous even when it crosses from
        // the fixed fast path into the sparse spill pool.
        let slotStart = atomicAdd(&pairBodyContactCounts[body], contactCount);
        let boundedStart = min(slotStart, ${maxPairContactsPerBody}u);
        let directCount = min(
          contactCount,
          ${maxPairContactsPerBody}u - boundedStart,
        );
        let spillCount = contactCount - directCount;
        if (spillCount == 0u) {
          return PairBodyReservation(
            true, slotStart, directCount, 0u, 0u, 0xffffffffu,
          );
        }

        // Spill nodes are also reserved as one contiguous block. The linked
        // list is published with one head exchange below, retaining the old
        // newest-row-first traversal order.
        let spillStart = atomicAdd(
          &pairBodyContactIndices[${pairBodyOverflowCounterOffset}u],
          spillCount,
        );
        if (
          spillStart >= ${pairBodyOverflowCapacity}u
          || spillCount > ${pairBodyOverflowCapacity}u - spillStart
        ) {
          var available = 0u;
          if (spillStart < ${pairBodyOverflowCapacity}u) {
            available = ${pairBodyOverflowCapacity}u - spillStart;
          }
          atomicAdd(&debugCounters[24], spillCount - min(spillCount, available));
          return PairBodyReservation(
            false, slotStart, directCount, spillStart, spillCount, 0xffffffffu,
          );
        }

        let newestNode = spillStart + spillCount - 1u;
        let previousHead = atomicExchange(
          &pairBodyContactIndices[${pairBodyOverflowHeadOffset}u + body],
          newestNode,
        );
        return PairBodyReservation(
          true,
          slotStart,
          directCount,
          spillStart,
          spillCount,
          previousHead,
        );
      }

      fn storeReservedPairBodyContact(
        reservation: PairBodyReservation,
        body: u32,
        rowOrdinal: u32,
        contact: u32,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
      ) {
        var taggedDestination = 0u;
        if (rowOrdinal < reservation.directCount) {
          taggedDestination = body * ${maxPairContactsPerBody}u
            + reservation.slotStart + rowOrdinal;
        } else {
          let spillOrdinal = rowOrdinal - reservation.directCount;
          taggedDestination = ${PAIR_BODY_SPILL_DESTINATION_BIT}u
            | (reservation.spillStart + spillOrdinal);
        }

        if ((taggedDestination & ${PAIR_BODY_SPILL_DESTINATION_BIT}u) == 0u) {
          atomicStore(&pairBodyContactIndices[taggedDestination], contact);
          return;
        }
        // Decode the transient destination tag before writing the persistent,
        // untagged physical-row reference consumed by AVBD.
        let spillOrdinal = rowOrdinal - reservation.directCount;
        let spillNode = taggedDestination & ${PAIR_BODY_DESTINATION_VALUE_MASK}u;
        let nodeBase = ${pairBodyOverflowNodeOffset}u + spillNode * 2u;
        var nextNode = reservation.previousSpillHead;
        if (spillOrdinal > 0u) { nextNode = spillNode - 1u; }
        atomicStore(&pairBodyContactIndices[nodeBase], contact);
        atomicStore(&pairBodyContactIndices[nodeBase + 1u], nextNode);
      }
    `;

    const buildCompactPairBodyListsShaderSource = /* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        positions: ptr<storage, array<vec4f>, read>,
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactCounts: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        activeManifolds: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        pairDispatchCount: u32,
        currentEpoch: u32,
        debugEnabled: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let activeCandidateCount = min(
          atomicLoad(&pairActivity[${pairActiveCandidateSlotsOffset}u]),
          pairDispatchCount,
        );
        if (gid >= activeCandidateCount) { return; }
        let rawManifold = atomicLoad(
          &pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u],
        );
        let manifold = rawManifold & ${ACTIVE_PAIR_VALUE_MASK}u;
        if ((rawManifold & ${RETAINED_SLEEPING_PAIR_BIT}u) != 0u) {
          // The tag is valid only between resolve and adjacency construction.
          atomicStore(
            &pairActivity[${pairActiveCandidateSlotsOffset}u + gid + 1u],
            manifold,
          );
        }
        if (manifold >= ${maxPersistentManifolds}u) { return; }

        let outputMask = atomicLoad(
          &persistentManifoldMap[${persistentOutputMaskOffset}u + manifold],
        );
        if (outputMask == 0u) { return; }
        let logicalBase = manifold * ${pairManifoldSlots}u;
        var manifoldHasActive = false;
        var activeRows: array<u32, ${pairManifoldSlots}>;
        var activeRowCount = 0u;
        var bodyI = 0xffffffffu;
        var bodyJ = 0xffffffffu;
        var dynamicI = false;
        var dynamicJ = false;
        for (var slot = 0u; slot < ${pairManifoldSlots}u; slot++) {
          if ((outputMask & (1u << slot)) == 0u) { continue; }
          let physicalRow = atomicLoad(
            &persistentManifoldMap[${persistentSlotRowOffset}u + logicalBase + slot],
          );
          if (physicalRow >= ${this.compactContactPoolCapacity}u) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.duplicateRowOwnership}u,
              currentEpoch,
            );
            continue;
          }
          let pairInfo = loadContactMeta(pairContacts, physicalRow);
          if (pairInfo.z < 0.5) { continue; }
          manifoldHasActive = true;

          let i = u32(pairInfo.x);
          let j = u32(pairInfo.y);
          if (i >= bodyCount || j >= bodyCount) {
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.endpointDropped}u,
              currentEpoch,
            );
            continue;
          }
          let rowDynamicI = positions[i].w > 0.0;
          let rowDynamicJ = positions[j].w > 0.0;
          if (!rowDynamicI && !rowDynamicJ) { continue; }

          if (activeRowCount == 0u) {
            bodyI = i;
            bodyJ = j;
            dynamicI = rowDynamicI;
            dynamicJ = rowDynamicJ;
          } else if (i != bodyI || j != bodyJ) {
            // Persistent descriptors own exactly one canonical body pair. A
            // mixed-endpoint row must never be attached to either endpoint.
            atomicAdd(&debugCounters[25u], 1u);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.endpointDropped}u,
              currentEpoch,
            );
            continue;
          }
          activeRows[activeRowCount] = physicalRow;
          activeRowCount += 1u;
        }

        var reservationI = emptyPairBodyReservation();
        if (dynamicI && activeRowCount > 0u) {
          reservationI = reservePairBodyContacts(
            bodyI,
            activeRowCount,
            pairBodyContactCounts,
            pairBodyContactIndices,
            persistentManifoldMap,
            debugCounters,
            currentEpoch,
          );
        }
        var reservationJ = emptyPairBodyReservation();
        if (dynamicJ && activeRowCount > 0u) {
          reservationJ = reservePairBodyContacts(
            bodyJ,
            activeRowCount,
            pairBodyContactCounts,
            pairBodyContactIndices,
            persistentManifoldMap,
            debugCounters,
            currentEpoch,
          );
        }

        var keepManifold = activeRowCount > 0u;
        if (dynamicI) { keepManifold = keepManifold && reservationI.wrote; }
        if (dynamicJ) { keepManifold = keepManifold && reservationJ.wrote; }
        if (dynamicI && dynamicJ && !keepManifold) {
          atomicAdd(&debugCounters[25u], activeRowCount);
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.endpointDropped}u,
            currentEpoch,
          );
        }

        if (keepManifold && ${this.activeManifoldStreamEnabled ? 'true' : 'false'}) {
          let activeManifoldWrite = atomicAdd(&activeManifolds[0], 1u);
          if (activeManifoldWrite < ${maxPersistentManifolds}u) {
            atomicStore(&activeManifolds[activeManifoldWrite + 1u], manifold);
          } else {
            // A unique live descriptor cannot exceed descriptor capacity. If
            // it ever does, fail through the existing compact safety gate
            // rather than silently omitting solver work.
            atomicAdd(&debugCounters[26u], 1u);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.activeContactListOverflow}u,
              currentEpoch,
            );
          }
        }

        // Exactly one global reservation per accepted manifold. Physical rows
        // remain in logical-slot order and every downstream consumer still
        // sees one raw row reference per accepted contact.
        var activeBlockStart = 0u;
        if (keepManifold) {
          activeBlockStart = atomicAdd(
            &pairActivity[${pairActiveContactsOffset}u],
            activeRowCount,
          );
        }

        for (var rowOrdinal = 0u; rowOrdinal < activeRowCount; rowOrdinal++) {
          let physicalRow = activeRows[rowOrdinal];
          let storedRow = select(${maxPairContacts}u, physicalRow, keepManifold);
          if (dynamicI && reservationI.wrote) {
            storeReservedPairBodyContact(
              reservationI,
              bodyI,
              rowOrdinal,
              storedRow,
              pairBodyContactIndices,
            );
          }
          if (dynamicJ && reservationJ.wrote) {
            storeReservedPairBodyContact(
              reservationJ,
              bodyJ,
              rowOrdinal,
              storedRow,
              pairBodyContactIndices,
            );
          }
          if (!keepManifold) { continue; }

          let activeWrite = activeBlockStart + rowOrdinal;
          if (activeWrite < ${maxActivePairContacts}u) {
            // Reservation tags address fixed/spill storage only; the immutable
            // canonical stream still publishes the raw physical contact row.
            atomicStore(
              &pairActivity[${pairActiveContactsOffset}u + activeWrite + 1u],
              physicalRow,
            );
          } else {
            atomicAdd(&debugCounters[26u], 1u);
            recordCompactFatal(
              persistentManifoldMap,
              ${CompactContactPoolFatalBit.activeContactListOverflow}u,
              currentEpoch,
            );
          }
        }
        if (manifoldHasActive && debugEnabled > 0u) {
          atomicAdd(&debugCounters[1u], 1u);
        }
      }

      struct PairBodyReservation {
        wrote: bool,
        slotStart: u32,
        directCount: u32,
        spillStart: u32,
        spillCount: u32,
        previousSpillHead: u32,
      };

      fn emptyPairBodyReservation() -> PairBodyReservation {
        return PairBodyReservation(false, 0u, 0u, 0u, 0u, 0xffffffffu);
      }

      fn reservePairBodyContacts(
        body: u32,
        contactCount: u32,
        pairBodyContactCounts: ptr<storage, array<atomic<u32>>, read_write>,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        currentEpoch: u32,
      ) -> PairBodyReservation {
        // One endpoint-count atomic reserves every physical row in this
        // manifold, including a possible fixed-to-spill boundary crossing.
        let slotStart = atomicAdd(&pairBodyContactCounts[body], contactCount);
        let boundedStart = min(slotStart, ${maxPairContactsPerBody}u);
        let directCount = min(
          contactCount,
          ${maxPairContactsPerBody}u - boundedStart,
        );
        let spillCount = contactCount - directCount;
        if (spillCount == 0u) {
          return PairBodyReservation(
            true, slotStart, directCount, 0u, 0u, 0xffffffffu,
          );
        }

        // Spill fallback is bulk-reserved as one contiguous node interval and
        // published by one head exchange. Linking newest-to-oldest preserves
        // the old per-row traversal order without its per-row atomics.
        let spillStart = atomicAdd(
          &pairBodyContactIndices[${pairBodyOverflowCounterOffset}u],
          spillCount,
        );
        if (
          spillStart >= ${pairBodyOverflowCapacity}u
          || spillCount > ${pairBodyOverflowCapacity}u - spillStart
        ) {
          var available = 0u;
          if (spillStart < ${pairBodyOverflowCapacity}u) {
            available = ${pairBodyOverflowCapacity}u - spillStart;
          }
          atomicAdd(&debugCounters[24u], spillCount - min(spillCount, available));
          recordCompactFatal(
            persistentManifoldMap,
            ${CompactContactPoolFatalBit.pairBodySpillOverflow}u,
            currentEpoch,
          );
          return PairBodyReservation(
            false, slotStart, directCount, spillStart, spillCount, 0xffffffffu,
          );
        }

        let newestNode = spillStart + spillCount - 1u;
        let previousHead = atomicExchange(
          &pairBodyContactIndices[${pairBodyOverflowHeadOffset}u + body],
          newestNode,
        );
        return PairBodyReservation(
          true,
          slotStart,
          directCount,
          spillStart,
          spillCount,
          previousHead,
        );
      }

      fn storeReservedPairBodyContact(
        reservation: PairBodyReservation,
        body: u32,
        rowOrdinal: u32,
        contact: u32,
        pairBodyContactIndices: ptr<storage, array<atomic<u32>>, read_write>,
      ) {
        var taggedDestination = 0u;
        if (rowOrdinal < reservation.directCount) {
          taggedDestination = body * ${maxPairContactsPerBody}u
            + reservation.slotStart + rowOrdinal;
        } else {
          let spillOrdinal = rowOrdinal - reservation.directCount;
          taggedDestination = ${PAIR_BODY_SPILL_DESTINATION_BIT}u
            | (reservation.spillStart + spillOrdinal);
        }

        if ((taggedDestination & ${PAIR_BODY_SPILL_DESTINATION_BIT}u) == 0u) {
          atomicStore(&pairBodyContactIndices[taggedDestination], contact);
          return;
        }
        // Decode the transient destination tag before writing the persistent,
        // untagged physical-row reference consumed by AVBD.
        let spillOrdinal = rowOrdinal - reservation.directCount;
        let spillNode = taggedDestination & ${PAIR_BODY_DESTINATION_VALUE_MASK}u;
        let nodeBase = ${pairBodyOverflowNodeOffset}u + spillNode * 2u;
        var nextNode = reservation.previousSpillHead;
        if (spillOrdinal > 0u) { nextNode = spillNode - 1u; }
        atomicStore(&pairBodyContactIndices[nodeBase], contact);
        atomicStore(&pairBodyContactIndices[nodeBase + 1u], nextNode);
      }

      fn recordCompactFatal(
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        bit: u32,
        currentEpoch: u32,
      ) {
        atomicOr(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u], bit);
        for (var attempt = 0u; attempt < ${PERSISTENT_CAS_MAX_ATTEMPTS}u; attempt++) {
          let result = atomicCompareExchangeWeak(
            &persistentManifoldMap[${CompactContactPoolHeaderOffset.firstFatalEpoch}u],
            0u,
            currentEpoch,
          );
          if (result.exchanged || result.old_value != 0u) { break; }
        }
      }
    `;

    const buildPairBodyListsShaderReleaseSource = buildPairBodyListsShaderSource
      .replace(
        `        debugEnabled: u32,\n`,
        '',
      )
      .replace(
        `          if (debugEnabled > 0u) {\n            atomicAdd(&debugCounters[1], 1u);\n          }\n`,
        '',
      )
      ;

    const buildPairBodyListsShaderDebug = wgslFn(
      this.compactMode ? buildCompactPairBodyListsShaderSource : buildPairBodyListsShaderSource,
      [contactRecordHelpers],
    );
    const buildPairBodyListsShaderRelease = wgslFn(
      this.compactMode ? buildCompactPairBodyListsShaderSource : buildPairBodyListsShaderReleaseSource,
      [contactRecordHelpers],
    );

    this.buildPairBodyListsKernel = buildPairBodyListsShaderRelease({
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      pairBodyContactCounts: storage(pairBodyContactCounts, 'uint', maxBodies).toAtomic(),
      pairBodyContactIndices: storage(pairBodyContactIndices, 'uint', pairBodyContactIndices.count).toAtomic(),
      ...(this.compactMode ? {
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        activeManifolds: storage(
          this.activeManifoldsAttr,
          'uint',
          this.activeManifoldsAttr.count,
        ).toAtomic(),
        currentEpoch: uniform(0),
        debugEnabled: uniform(0),
      } : {}),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      bodyCount: uniform(0),
      pairDispatchCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Build Body Lists');

    this.buildPairBodyListsKernelDebug = buildPairBodyListsShaderDebug({
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      pairBodyContactCounts: storage(pairBodyContactCounts, 'uint', maxBodies).toAtomic(),
      pairBodyContactIndices: storage(pairBodyContactIndices, 'uint', pairBodyContactIndices.count).toAtomic(),
      ...(this.compactMode ? {
        persistentManifoldMap: storage(persistentManifoldMap, 'uint', persistentManifoldMap.count).toAtomic(),
        activeManifolds: storage(
          this.activeManifoldsAttr,
          'uint',
          this.activeManifoldsAttr.count,
        ).toAtomic(),
        currentEpoch: uniform(0),
      } : {}),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      bodyCount: uniform(0),
      pairDispatchCount: uniform(0),
      debugEnabled: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('Contact Build Body Lists');
    assertStorageBufferBudget(
      'Contact Build Body Lists',
      this.compactMode ? 8 : 6,
    );

    const finalizeDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<u32>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        debugEnabled: u32,
      ) -> void {
        if (debugEnabled == 0u) { return; }
        atomicStore(&debugCounters[2], pairActivity[${pairActiveCandidateSlotsOffset}u]);
      }
    `);

    this.finalizeDebugCountersKernel = finalizeDebugCountersShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      debugEnabled: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Contact Finalize Debug Counters');

    const compactFatalLoad = this.compactMode
      ? `atomicLoad(&persistentManifoldMap[${CompactContactPoolHeaderOffset.fatalBits}u])`
      : '0u';
    const finalizeConstraintSafetyShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<atomic<u32>>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        ccdFatalState: ptr<storage, array<atomic<u32>>, read_write>,
        persistentManifoldMap: ptr<storage, array<atomic<u32>>, read_write>,
        activeManifolds: ptr<storage, array<atomic<u32>>, read_write>,
        constraintSafetyGate: ptr<storage, array<atomic<u32>>, read_write>,
        fatalBodyDispatch: ptr<storage, array<u32>, read_write>,
        bodyWorkgroups: u32,
      ) -> void {
        let contactFatal = atomicLoad(&debugCounters[24u])
          | atomicLoad(&debugCounters[25u])
          | atomicLoad(&debugCounters[26u])
          | atomicLoad(&debugCounters[27u])
          | atomicLoad(&debugCounters[29u])
          | ${compactFatalLoad};
        if (contactFatal != 0u) {
          atomicOr(
            &constraintSafetyGate[0u],
            ${CONSTRAINT_SAFETY_CONTACT_FATAL_BIT}u,
          );
        }
        if (atomicLoad(&ccdFatalState[0u]) != 0u) {
          atomicOr(
            &constraintSafetyGate[0u],
            ${CONSTRAINT_SAFETY_CCD_FATAL_BIT}u,
          );
        }

        let failed = atomicLoad(&constraintSafetyGate[0u]) != 0u;
        if (failed) {
          // No solver pass may consume a prefix of an incomplete contact graph.
          // The indirect body pass that follows also clears per-body adjacency.
          atomicStore(&pairActivity[${pairActiveContactsOffset}u], 0u);
          atomicStore(&pairActivity[${pairActiveCandidateSlotsOffset}u], 0u);
          atomicStore(&activeManifolds[0u], 0u);
          // Retained-prefix evidence belongs only to the current healthy
          // candidate transaction. Never carry it through rollback.
          atomicStore(&constraintSafetyGate[1u], 0u);
        }
        fatalBodyDispatch[0u] = select(0u, bodyWorkgroups, failed);
        fatalBodyDispatch[1u] = 1u;
        fatalBodyDispatch[2u] = 1u;
      }
    `);
    this.finalizeConstraintSafetyKernel = finalizeConstraintSafetyShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toAtomic(),
      debugCounters: storage(this.debugCountersAttr, 'uint', 30).toAtomic(),
      ccdFatalState: storage(this.ccdFatalStateAttr, 'uint', 1).toAtomic(),
      persistentManifoldMap: storage(
        persistentManifoldMap,
        'uint',
        persistentManifoldMap.count,
      ).toAtomic(),
      activeManifolds: storage(
        this.activeManifoldsAttr,
        'uint',
        this.activeManifoldsAttr.count,
      ).toAtomic(),
      constraintSafetyGate: storage(
        this.constraintSafetyGateAttr,
        'uint',
        2,
      ).toAtomic(),
      fatalBodyDispatch: storage(this.fatalBodyDispatchAttr, 'uint', 3),
      bodyWorkgroups: uniform(0),
    }).computeKernel([1, 1, 1]).setName('Contact Finalize Constraint Safety Gate');
    assertStorageBufferBudget('Contact Finalize Constraint Safety Gate', 7);
  }

  setFriction(staticFriction: number): void {
    const clamped = Math.max(0.0, staticFriction);
    this.pairKernel.computeNode.parameters.frictionStatic.value = clamped;
    this.pairKernelDebug.computeNode.parameters.frictionStatic.value = clamped;
  }

  setCollisionMargin(collisionMargin: number): void {
    const clamped = Math.max(0.0, collisionMargin);
    this.pairKernel.computeNode.parameters.collisionMargin.value = clamped;
    this.pairKernelDebug.computeNode.parameters.collisionMargin.value = clamped;
    if (this.speculativeContactKernel) {
      this.speculativeContactKernel.computeNode.parameters.collisionMargin.value = clamped;
    }
  }

  setDebugLogInterval(intervalFrames: number): void {
    this.debugEveryNFrames = Math.max(1, Math.floor(intervalFrames));
    this.lastDebugLogFrame = -1;
  }

  setDebugEnabled(enabled: boolean): void {
    this.debugEnabled = enabled;
    this.debugReadbackInFlight = false;
    this.lastDebugLogFrame = -1;
  }

  setFloorDebugBody(bodyIndex: number): void {
    this.floorDebugBody = Number.isFinite(bodyIndex) && bodyIndex >= 0
      ? Math.floor(bodyIndex)
      : 0xffffffff;
  }

  /** Regression-only hook for exercising the non-zero u32 epoch wrap path. */
  setManifoldEpochForTesting(epoch: number): void {
    if (!Number.isSafeInteger(epoch) || epoch < 0 || epoch > COMPACT_CONTACT_POOL_EPOCH_MAX) {
      throw new Error('Contact manifold test epoch exceeds the exact uniform range.');
    }
    this.manifoldEpoch = epoch;
  }

  dispatch(
    renderer: any,
    bodyCount: number,
    pairCount: number,
    pairDispatchCount: number,
    useCandidatePairs: boolean,
    frameId: number,
  ): void {
    const computeNodes: any[] = [];
    const collectingRenderer = {
      compute: (nodes: any | any[], dispatch: any): void => {
        const list = Array.isArray(nodes) ? nodes : [nodes];
        for (const node of list) {
          node.count = dispatch;
          computeNodes.push(node);
        }
      },
    };
    this.dispatchPairKernelPhase(
      collectingRenderer,
      bodyCount,
      pairCount,
      pairDispatchCount,
      useCandidatePairs,
    );
    this.dispatchBodyListPhase(collectingRenderer, bodyCount, pairDispatchCount, frameId);
    if (computeNodes.length > 0) {
      renderer.compute(computeNodes);
    }
    this.pollCapacityLoss(renderer, frameId, bodyCount, pairDispatchCount);
  }

  private dispatchPairKernelPhase(
    renderer: any,
    bodyCount: number,
    pairCount: number,
    pairDispatchCount: number,
    useCandidatePairs: boolean,
  ): void {
    if (this.compactMode && pairDispatchCount > 0 && !useCandidatePairs) {
      throw new Error('Compact persistent contacts require the candidate-pair descriptor path.');
    }
    const epochAdvance = advanceCompactContactPoolEpoch(this.manifoldEpoch);
    this.manifoldEpoch = epochAdvance.epoch;
    const epochWrapped = epochAdvance.wrapped;
    const currentEpoch = this.manifoldEpoch;
    const candidateFlag = useCandidatePairs ? 1 : 0;
    const debugFlag = this.debugEnabled ? 1 : 0;
    const pairKernel = debugFlag ? this.pairKernelDebug : this.pairKernel;
    const buildActiveCandidateSlotsKernel = this.ccdEnabled
      ? this.buildActiveCandidateSlotsCcdKernel!
      : this.buildActiveCandidateSlotsKernel;
    const buildPairBodyListsKernel = debugFlag ? this.buildPairBodyListsKernelDebug : this.buildPairBodyListsKernel;

    if (this.compactMode) {
      this.beginCompactEpochKernel!.computeNode.parameters.currentEpoch.value = currentEpoch;
      this.beginCompactEpochKernel!.computeNode.parameters.epochWrapped.value = epochWrapped ? 1 : 0;
      this.reclaimCompactStaleKernel!.computeNode.parameters.currentEpoch.value = currentEpoch;
      this.buildCompactFinalizeArgsKernel!.computeNode.parameters.currentEpoch.value = currentEpoch;
      this.finalizeCompactFreeRowsKernel!.computeNode.parameters.currentEpoch.value = currentEpoch;
      if (!this.compactInitialized) {
        renderer.compute(
          this.initializeCompactPoolKernel!,
          this.compactInitializeDispatch!,
        );
        this.compactInitialized = true;
      }
      renderer.compute(this.beginCompactEpochKernel!, [1, 1, 1]);
      renderer.compute(
        this.reclaimCompactStaleKernel!,
        this.compactReclaimIndirectAttr!,
      );
    }

    pairKernel.computeNode.parameters.bodyCount.value = bodyCount;
    pairKernel.computeNode.parameters.pairCount.value = pairCount;
    pairKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    pairKernel.computeNode.parameters.useCandidatePairs.value = candidateFlag;
    pairKernel.computeNode.parameters.manifoldEpoch.value = currentEpoch;
    if (debugFlag) {
      pairKernel.computeNode.parameters.floorDebugBody.value = this.floorDebugBody >>> 0;
      pairKernel.computeNode.parameters.debugEnabled.value = debugFlag;
    }
    this.clearDebugCountersKernel.computeNode.parameters.debugEnabled.value = debugFlag;

    this.clearPairBodyCountsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    if (bodyCount > 0) {
      const bodyWorkgroups = Math.ceil(bodyCount / WORKGROUP_SIZE);
      renderer.compute(this.clearPairBodyCountsKernel, [bodyWorkgroups, 1, 1]);
    }

    buildPairBodyListsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    buildPairBodyListsKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    if (this.compactMode) {
      buildPairBodyListsKernel.computeNode.parameters.currentEpoch.value = currentEpoch;
    }
    if (debugFlag) {
      buildPairBodyListsKernel.computeNode.parameters.debugEnabled.value = debugFlag;
    }
    buildActiveCandidateSlotsKernel.computeNode.parameters.pairCount.value = pairCount;
    buildActiveCandidateSlotsKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    buildActiveCandidateSlotsKernel.computeNode.parameters.useCandidatePairs.value = candidateFlag;
    this.clearActiveCandidateSlotsKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    this.clearActiveCandidateSlotsKernel.computeNode.parameters.useCandidatePairs.value = candidateFlag;
    this.resolvePersistentManifoldsKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    this.resolvePersistentManifoldsKernel.computeNode.parameters.useCandidatePairs.value = candidateFlag;
    this.resolvePersistentManifoldsKernel.computeNode.parameters.currentEpoch.value = currentEpoch;
    this.countPersistentCacheMissesKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    this.buildPairDispatchArgsKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    this.finalizeDebugCountersKernel.computeNode.parameters.debugEnabled.value = debugFlag;
    if (debugFlag) {
      renderer.compute(this.clearDebugCountersKernel, [1, 1, 1]);
    }
    if (this.ccdEnabled) {
      if (this.ccdDiagnosticsEnabled) {
        renderer.compute(this.clearCcdCountersKernel!, [1, 1, 1]);
      }
      this.speculativeContactKernel!.computeNode.parameters.bodyCount.value = bodyCount;
      this.speculativeContactKernel!.computeNode.parameters.pairCount.value = pairCount;
      this.speculativeContactKernel!.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
      this.speculativeContactKernel!.computeNode.parameters.useCandidatePairs.value = candidateFlag;
      if (this.compactMode) {
        this.speculativeContactKernel!.computeNode.parameters.currentEpoch.value = currentEpoch;
      }
    }
    if (pairDispatchCount > 0) {
      renderer.compute(this.clearActiveCandidateSlotsKernel, [1, 1, 1]);
      renderer.compute(buildActiveCandidateSlotsKernel, this.candidateDispatchIndirectAttr);
      renderer.compute(this.buildPairDispatchArgsKernel, [1, 1, 1]);
      if (candidateFlag > 0) {
        if (!this.compactMode) {
          renderer.compute(this.countPersistentCacheMissesKernel, this.persistentProbeIndirectAttr);
          renderer.compute(this.clearPersistentCacheKernel, this.persistentResetIndirectAttr);
        }
        renderer.compute(this.resolvePersistentManifoldsKernel, this.pairDispatchIndirectAttr);
      }
      renderer.compute(pairKernel, this.pairDispatchIndirectAttr);
      if (this.ccdEnabled) {
        renderer.compute(this.speculativeContactKernel!, this.pairDispatchIndirectAttr);
        this.ccdFatalStateSubmitted = true;
      }
    }
  }

  private dispatchBodyListPhase(
    renderer: any,
    bodyCount: number,
    pairDispatchCount: number,
    frameId: number,
  ): void {
    const debugFlag = this.debugEnabled ? 1 : 0;
    const buildPairBodyListsKernel = debugFlag ? this.buildPairBodyListsKernelDebug : this.buildPairBodyListsKernel;
    buildPairBodyListsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    buildPairBodyListsKernel.computeNode.parameters.pairDispatchCount.value = pairDispatchCount;
    if (this.compactMode) {
      buildPairBodyListsKernel.computeNode.parameters.currentEpoch.value = this.manifoldEpoch;
    }
    if (debugFlag) {
      buildPairBodyListsKernel.computeNode.parameters.debugEnabled.value = debugFlag;
    }
    this.finalizeDebugCountersKernel.computeNode.parameters.debugEnabled.value = debugFlag;
    this.finalizeConstraintSafetyKernel.computeNode.parameters.bodyWorkgroups.value =
      Math.ceil(bodyCount / WORKGROUP_SIZE);

    if (pairDispatchCount > 0) {
      renderer.compute(buildPairBodyListsKernel, this.pairDispatchIndirectAttr);
      if (debugFlag) {
        renderer.compute(this.finalizeDebugCountersKernel, [1, 1, 1]);
      }
    }
    if (this.compactMode && this.compactFinalizedEpoch !== this.manifoldEpoch) {
      renderer.compute(this.buildCompactFinalizeArgsKernel!, [1, 1, 1]);
      renderer.compute(
        this.finalizeCompactFreeRowsKernel!,
        this.compactFinalizeIndirectAttr!,
      );
      this.compactFinalizedEpoch = this.manifoldEpoch;
    }
    // This is the commit barrier for the generated constraint graph. It runs
    // after compact finalization because that pass can itself raise a fatal
    // allocator invariant, and it also observes an earlier broadphase bit in
    // the shared scene-cumulative gate.
    renderer.compute(this.finalizeConstraintSafetyKernel, [1, 1, 1]);
    // dispatch() performs readback only after the combined compute group has
    // been submitted. Direct legacy callers still receive no implicit stall.
  }

  private latchSafetyReadbackInfrastructureFailure(
    readbackGeneration: number,
    message: string,
    error: unknown,
  ): void {
    if (readbackGeneration !== this.resetGeneration) return;
    // A failed map makes the step uncertifiable, but it does not prove that
    // contact capacity or CCD itself failed. Keep this cause independent so
    // PhysicsEngine can report the fail-closed reason without inventing an
    // overflow bit that the GPU never published.
    this.standaloneSafetyReadbackInfrastructureFailure = true;
    if (!this.safetyReadbackFailureWarned) {
      console.error(message, error);
      this.safetyReadbackFailureWarned = true;
    }
  }

  pollCapacityLoss(renderer: any, frameId: number, bodyCount: number, pairDispatchCount: number): void {
    if (this.disposed) return;
    if (
      (
        this.capacityOverflowDetected
        || this.hasCcdFatal()
        || this.standaloneSafetyReadbackInfrastructureFailure
      )
      && !this.debugEnabled
    ) return;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return;
    if (this.debugReadbackInFlight) return;
    if (this.lastDebugLogFrame === frameId) return;
    if (this.lastDebugLogFrame >= 0 && frameId - this.lastDebugLogFrame < this.debugEveryNFrames) return;

    this.debugReadbackInFlight = true;
    this.lastDebugLogFrame = frameId;
    const readbackGeneration = this.resetGeneration;
    const injectedFailure = this.safetyReadbackFailureForTesting;
    this.safetyReadbackFailureForTesting = null;
    let safetyGateReadback: Promise<ArrayBuffer>;
    try {
      const beginReadback = (): Promise<ArrayBuffer> => {
        if (injectedFailure === 'sync') {
          throw new Error('Injected synchronous contact safety readback failure.');
        }
        // The finalize kernel has already OR-reduced every contact, compact
        // allocator and CCD correctness source into this two-word gate. The
        // watchdog must cover this single authoritative map, rather than a
        // serial chain of debug-counter, CCD and allocator-header maps whose
        // accumulated latency can falsely fail a healthy busy GPU.
        const readback = renderer.getArrayBufferAsync(
          this.constraintSafetyGateAttr,
        ) as Promise<ArrayBuffer>;
        if (injectedFailure === 'reject') {
          return readback.then(() => Promise.reject(
            new Error('Injected rejected contact safety readback.'),
          ));
        }
        if (injectedFailure === 'timeout') {
          return readback.then((raw) => new Promise<ArrayBuffer>((resolve) => {
            setTimeout(() => resolve(raw), this.safetyReadbackTimeoutMs * 3);
          }));
        }
        return readback;
      };
      safetyGateReadback = this.submissionLedger
        ? this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId,
            submissionClass: 'diagnostic',
            label: 'contact-capacity-readback',
          },
          beginReadback,
        )
        : beginReadback();
      this.submissionLedger?.record('diagnostic', 'contact-capacity-readback', { frameId });
    } catch (error) {
      this.debugReadbackInFlight = false;
      this.latchSafetyReadbackInfrastructureFailure(
        readbackGeneration,
        `Contact safety readback failed synchronously at frame=${frameId}; physics must fail closed.`,
        error,
      );
      return;
    }
    const safetyReadback = safetyGateReadback.then((gateRaw: ArrayBuffer) => {
      if (this.disposed || readbackGeneration !== this.resetGeneration) return;
      const gateBits = new Uint32Array(gateRaw)[0] ?? 0;
      const contactSafetyBits = gateBits & (
        CONSTRAINT_SAFETY_CONTACT_FATAL_BIT | CONSTRAINT_SAFETY_CCD_FATAL_BIT
      );
      this.latchConstraintSafetyBits(contactSafetyBits);

      // Detailed counters remain useful diagnostics, but they are not part of
      // the correctness deadline. Run their slower serial maps only when the
      // user enabled debug output or after the one-map gate already proved a
      // fatal. A rejected detail map cannot turn a healthy authoritative gate
      // into a false physics halt.
      if (!this.debugEnabled && contactSafetyBits === 0) return;
      if (this.pendingDetailReadback !== null) return;
      // Promise continuations publish CPU-only state. A later observed
      // maintenance frame owns each individual detail readback submit.
      this.pendingDetailReadback = {
        phase: 'debug-pending',
        readbackGeneration,
        frameId,
        bodyCount,
        pairDispatchCount,
        lastMaintenanceFrameId: null,
        debugRaw: null,
        ccdFatalBits: 0,
        compactState: null,
        error: null,
      };
    });
    const timeoutError = new Error(
      `Contact safety readback timed out after ${this.safetyReadbackTimeoutMs}ms.`,
    );
    const timeoutHandle = setTimeout(() => {
      if (
        !this.disposed
        && readbackGeneration === this.resetGeneration
        && this.debugReadbackInFlight
      ) {
        this.latchSafetyReadbackInfrastructureFailure(
          readbackGeneration,
          `Contact safety readback timed out at frame=${frameId}; physics must fail closed.`,
          timeoutError,
        );
      }
    }, this.safetyReadbackTimeoutMs);
    void safetyReadback.catch((error: unknown) => {
      this.latchSafetyReadbackInfrastructureFailure(
        readbackGeneration,
        `Contact safety readback rejected at frame=${frameId}; physics must fail closed.`,
        error,
      );
    }).finally(() => {
      clearTimeout(timeoutHandle);
      if (!this.disposed && readbackGeneration === this.resetGeneration) {
        this.debugReadbackInFlight = false;
      }
    });
  }

  hasPendingDetailReadback(): boolean {
    return !this.disposed && this.pendingDetailReadback !== null;
  }

  /**
   * Services one phase of the deferred diagnostic chain. A maintenance frame
   * may synchronously start at most one GPU readback; its Promise continuation
   * only stores the result and makes the next phase eligible.
   */
  servicePendingDetailReadback(renderer: any, maintenanceFrameId: number): boolean {
    if (this.disposed) return false;
    const pending = this.pendingDetailReadback;
    if (pending === null) return false;
    if (pending.readbackGeneration !== this.resetGeneration) {
      this.pendingDetailReadback = null;
      return false;
    }
    // Even if a test renderer resolves immediately, never let two detail maps
    // be launched under the same maintenance frame's submission budget.
    if (pending.lastMaintenanceFrameId === maintenanceFrameId) return false;

    if (pending.phase === 'complete') {
      try {
        this.finishPendingDetailReadback(pending);
      } catch (error) {
        console.warn('Contact detailed capacity diagnostics were unavailable:', error);
      }
      if (this.pendingDetailReadback === pending) {
        this.pendingDetailReadback = null;
      }
      return false;
    }
    if (pending.phase === 'failed') {
      console.warn(
        'Contact detailed capacity diagnostics were unavailable:',
        pending.error,
      );
      if (this.pendingDetailReadback === pending) {
        this.pendingDetailReadback = null;
      }
      return false;
    }
    if (
      pending.phase === 'debug-in-flight'
      || pending.phase === 'ccd-in-flight'
      || pending.phase === 'allocator-in-flight'
    ) {
      return false;
    }

    const isCurrent = (): boolean => (
      !this.disposed
      && pending.readbackGeneration === this.resetGeneration
      && this.pendingDetailReadback === pending
    );

    if (pending.phase === 'debug-pending') {
      if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return false;
      pending.phase = 'debug-in-flight';
      pending.lastMaintenanceFrameId = maintenanceFrameId;
      try {
        const beginReadback = (): Promise<ArrayBuffer> => (
          renderer.getArrayBufferAsync(this.debugCountersAttr) as Promise<ArrayBuffer>
        );
        const readback = this.submissionLedger
          ? this.submissionLedger.withActualSubmitScope(
            this.device.queue,
            {
              frameId: maintenanceFrameId,
              submissionClass: 'diagnostic',
              label: 'contact-capacity-readback',
            },
            beginReadback,
          )
          : beginReadback();
        this.submissionLedger?.record('diagnostic', 'contact-capacity-readback', {
          frameId: maintenanceFrameId,
        });
        void readback.then(
          (raw: ArrayBuffer) => {
            if (!isCurrent()) return;
            pending.debugRaw = raw;
            pending.phase = this.ccdEnabled && this.ccdFatalStateSubmitted
              ? 'ccd-pending'
              : this.compactMode
                ? 'allocator-pending'
                : 'complete';
          },
          (error: unknown) => {
            if (!isCurrent()) return;
            pending.error = error;
            pending.phase = 'failed';
          },
        );
      } catch (error) {
        pending.error = error;
        pending.phase = 'failed';
        return false;
      }
      return true;
    }

    if (pending.phase === 'ccd-pending') {
      if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return false;
      pending.phase = 'ccd-in-flight';
      pending.lastMaintenanceFrameId = maintenanceFrameId;
      try {
        const beginReadback = (): Promise<ArrayBuffer> => (
          renderer.getArrayBufferAsync(this.ccdFatalStateAttr) as Promise<ArrayBuffer>
        );
        const readback = this.submissionLedger
          ? this.submissionLedger.withActualSubmitScope(
            this.device.queue,
            {
              frameId: maintenanceFrameId,
              submissionClass: 'diagnostic',
              label: 'contact-ccd-fatal-readback',
            },
            beginReadback,
          )
          : beginReadback();
        this.submissionLedger?.record('diagnostic', 'contact-ccd-fatal-readback', {
          frameId: maintenanceFrameId,
        });
        void readback.then(
          (raw: ArrayBuffer) => {
            if (!isCurrent()) return;
            pending.ccdFatalBits = new Uint32Array(raw)[0] ?? 0;
            pending.phase = this.compactMode ? 'allocator-pending' : 'complete';
          },
          (error: unknown) => {
            if (!isCurrent()) return;
            pending.error = error;
            pending.phase = 'failed';
          },
        );
      } catch (error) {
        pending.error = error;
        pending.phase = 'failed';
        return false;
      }
      return true;
    }

    if (pending.phase === 'allocator-pending') {
      if (!renderer) return false;
      pending.phase = 'allocator-in-flight';
      pending.lastMaintenanceFrameId = maintenanceFrameId;
      try {
        const readback = this.readPersistentAllocatorStateAsync(
          renderer,
          maintenanceFrameId,
        );
        void readback.then(
          (compactState) => {
            if (!isCurrent()) return;
            pending.compactState = compactState;
            pending.phase = 'complete';
          },
          (error: unknown) => {
            if (!isCurrent()) return;
            pending.error = error;
            pending.phase = 'failed';
          },
        );
      } catch (error) {
        pending.error = error;
        pending.phase = 'failed';
        return false;
      }
      return true;
    }

    return false;
  }

  private finishPendingDetailReadback(pending: PendingContactDetailReadback): void {
    const debugRaw = pending.debugRaw;
    if (debugRaw === null) {
      throw new Error('Contact detail readback completed without debug counters.');
    }
    const {
      frameId,
      bodyCount,
      pairDispatchCount,
      ccdFatalBits,
      compactState,
    } = pending;
    const values = new Uint32Array(debugRaw);
    const warmstartResets = values[0] ?? 0;
    const activeManifolds = values[1] ?? 0;
    const activeCandidateSlots = values[2] ?? 0;
    const stalePrunedSlots = values[3] ?? 0;
    const separatingKeptSlots = values[4] ?? 0;
    const stickingAnchorReuses = values[5] ?? 0;
    const sharedTangentSignFlips = values[6] ?? 0;
    const floorPairCandidates = values[7] ?? 0;
    const floorPairWarmstartResets = values[8] ?? 0;
    const floorPairKeepRejected = values[9] ?? 0;
    const floorPairDegenerateFallbacks = values[10] ?? 0;
    const floorPairWriteZero = values[11] ?? 0;
    const floorPairActive = values[12] ?? 0;
    const floorPairSeparatedRejects = values[13] ?? 0;
    const floorPairInactiveThresholdRejects = values[14] ?? 0;
    const floorPairHysteresisRejects = values[15] ?? 0;
    const floorPairSeparatedFaceA0 = values[16] ?? 0;
    const floorPairSeparatedFaceA1 = values[17] ?? 0;
    const floorPairSeparatedFaceA2 = values[18] ?? 0;
    const floorPairSeparatedFaceB0 = values[19] ?? 0;
    const floorPairSeparatedFaceB1 = values[20] ?? 0;
    const floorPairSeparatedFaceB2 = values[21] ?? 0;
    const floorPairSeparatedEdge = values[22] ?? 0;
    const floorPairSeparatedOther = values[23] ?? 0;
    const pairBodyListOverflowWrites = values[24] ?? 0;
    const pairBodyListDroppedContacts = values[25] ?? 0;
    const activeContactListOverflowWrites = values[26] ?? 0;
    const persistentManifoldPoolOverflowWrites = values[27] ?? 0;
    const persistentManifoldPoolReclaims = values[28] ?? 0;
    const persistentManifoldHashProtocolFailures = values[29] ?? 0;
    const compactFatalBits = compactState?.fatalBits ?? 0;
    const contactOverflow = pairBodyListOverflowWrites > 0
      || pairBodyListDroppedContacts > 0
      || activeContactListOverflowWrites > 0
      || persistentManifoldPoolOverflowWrites > 0
      || compactFatalBits !== 0;
    const ccdFatal = ccdFatalBits !== 0;
    if (contactOverflow) {
      this.latchConstraintSafetyBits(CONSTRAINT_SAFETY_CONTACT_FATAL_BIT);
    }
    if (ccdFatal) {
      this.latchConstraintSafetyBits(CONSTRAINT_SAFETY_CCD_FATAL_BIT);
    }
    const safetyFatal = contactOverflow || ccdFatal;
    if (this.debugEnabled || safetyFatal) {
      const log = safetyFatal ? console.error : console.info;
      log(
        `[Contact ${safetyFatal ? 'Safety Fatal' : 'Debug'}] frame=${frameId} bodies=${bodyCount} pairDispatch=${pairDispatchCount} ` +
        `activeCandidates=${activeCandidateSlots} activeManifolds=${activeManifolds} ` +
        `warmstartResets=${warmstartResets} stalePruned=${stalePrunedSlots} ` +
        `separatingKept=${separatingKeptSlots} stickAnchorReuses=${stickingAnchorReuses} ` +
        `sharedTangentSignFlips=${sharedTangentSignFlips} ` +
        `floorPairs=${floorPairCandidates} floorWarmstartResets=${floorPairWarmstartResets} ` +
        `floorRejected=${floorPairKeepRejected} floorDegenerate=${floorPairDegenerateFallbacks} ` +
        `floorWriteZero=${floorPairWriteZero} floorActive=${floorPairActive} ` +
        `floorRejectSeparated=${floorPairSeparatedRejects} ` +
        `floorRejectInactiveGap=${floorPairInactiveThresholdRejects} ` +
        `floorRejectHysteresis=${floorPairHysteresisRejects} ` +
        `floorSepFaceA=(${floorPairSeparatedFaceA0},${floorPairSeparatedFaceA1},${floorPairSeparatedFaceA2}) ` +
        `floorSepFaceB=(${floorPairSeparatedFaceB0},${floorPairSeparatedFaceB1},${floorPairSeparatedFaceB2}) ` +
        `floorSepEdge=${floorPairSeparatedEdge} floorSepOther=${floorPairSeparatedOther} ` +
        `bodyListOverflowWrites=${pairBodyListOverflowWrites} bodyListDroppedContacts=${pairBodyListDroppedContacts} ` +
        `activeListOverflowWrites=${activeContactListOverflowWrites} ` +
        `manifoldPoolOverflowWrites=${persistentManifoldPoolOverflowWrites} ` +
        `manifoldCacheResets=${persistentManifoldPoolReclaims} ` +
        `manifoldHashProtocolFailures=${persistentManifoldHashProtocolFailures} ` +
        `ccdFatalBits=0x${ccdFatalBits.toString(16)} ` +
        `compactFatalBits=0x${compactFatalBits.toString(16)} ` +
        `compactFirstFatalEpoch=${compactState?.firstFatalEpoch ?? 0}`,
      );
    }
  }

  hasCapacityOverflow(): boolean {
    return this.capacityOverflowDetected;
  }

  /** Receives the contact/CCD subset of the fused large-scene safety sample. */
  latchConstraintSafetyBits(bits: number): void {
    if (this.disposed) return;
    const relevant = bits & (
      CONSTRAINT_SAFETY_CONTACT_FATAL_BIT | CONSTRAINT_SAFETY_CCD_FATAL_BIT
    );
    this.latchedConstraintSafetyBits = (
      this.latchedConstraintSafetyBits | relevant
    ) >>> 0;
    this.capacityOverflowDetected ||= (
      relevant & CONSTRAINT_SAFETY_CONTACT_FATAL_BIT
    ) !== 0;
  }

  hasCcdFatal(): boolean {
    return (
      this.latchedConstraintSafetyBits & CONSTRAINT_SAFETY_CCD_FATAL_BIT
    ) !== 0;
  }

  getLatchedConstraintSafetyBits(): number {
    return this.latchedConstraintSafetyBits;
  }

  hasStandaloneSafetyReadbackInfrastructureFailure(): boolean {
    return this.standaloneSafetyReadbackInfrastructureFailure;
  }

  /** Compatibility alias; ContactGeneration owns only the standalone map. */
  hasSafetyReadbackInfrastructureFailure(): boolean {
    return this.hasStandaloneSafetyReadbackInfrastructureFailure();
  }

  /** Regression-only injection into the next automatic safety poll. */
  injectSafetyReadbackFailureForTesting(mode: 'sync' | 'reject' | 'timeout'): void {
    this.safetyReadbackFailureForTesting = mode;
    // Guarantee that the next dispatched frame polls instead of waiting for
    // the production telemetry cadence.
    this.lastDebugLogFrame = -1;
  }

  /** Regression hook: forces the standalone gate-map path even with LBVH. */
  requiresStandaloneSafetyPollForTesting(): boolean {
    return this.safetyReadbackFailureForTesting !== null;
  }

  /** Regression-only: make the next eligible substep take a healthy gate map. */
  requestStandaloneSafetyPollForTesting(): void {
    this.lastDebugLogFrame = -1;
  }

  getConstraintSafetyGateAttribute(): StorageBufferAttribute {
    return this.constraintSafetyGateAttr;
  }

  getFatalBodyDispatchAttribute(): IndirectStorageBufferAttribute {
    return this.fatalBodyDispatchAttr;
  }

  /** Exact compact-manifold stream produced alongside canonical adjacency. */
  getActiveManifoldsAttribute(): StorageBufferAttribute {
    return this.activeManifoldsAttr;
  }

  /** Regression-only: inject the same always-on GPU fatal word written by CCD. */
  injectCcdFatalForTesting(bits = 1): void {
    if (!Number.isSafeInteger(bits) || bits <= 0 || bits > 0xffffffff) {
      throw new RangeError('Injected CCD fatal bits must be a non-zero u32.');
    }
    const values = this.ccdFatalStateAttr.array as Uint32Array;
    values[0] = (values[0] | (bits >>> 0)) >>> 0;
    this.ccdFatalStateAttr.needsUpdate = true;
    this.ccdFatalStateSubmitted = true;
  }

  async readConstraintSafetyGateAsync(renderer: any): Promise<number> {
    const raw = await renderer.getArrayBufferAsync(this.constraintSafetyGateAttr) as ArrayBuffer;
    return new Uint32Array(raw)[0] ?? 0;
  }

  /** Regression-only snapshot: [fatalBits, retainedSleepingPrefix]. */
  async readConstraintSafetyWordsAsync(renderer: any): Promise<Uint32Array> {
    const raw = await renderer.getArrayBufferAsync(this.constraintSafetyGateAttr) as ArrayBuffer;
    return new Uint32Array(raw).slice(0, 2);
  }

  /** Regression-only proof that the healthy transaction path dispatches no body work. */
  async readFatalBodyDispatchAsync(renderer: any): Promise<Uint32Array> {
    const raw = await renderer.getArrayBufferAsync(this.fatalBodyDispatchAttr) as ArrayBuffer;
    return new Uint32Array(raw).slice(0, 3);
  }

  isCcdEnabled(): boolean {
    return this.ccdEnabled;
  }

  isCcdDiagnosticsEnabled(): boolean {
    return this.ccdDiagnosticsEnabled;
  }

  async readCcdDiagnosticsAsync(renderer: any): Promise<CcdDiagnostics | null> {
    if (!this.ccdDiagnosticsEnabled || !this.ccdCountersAttr) return null;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;
    const readbackGeneration = this.resetGeneration;
    const raw = await renderer.getArrayBufferAsync(this.ccdCountersAttr) as ArrayBuffer;
    if (readbackGeneration !== this.resetGeneration) return null;
    const values = new Uint32Array(raw);
    return {
      enabled: true,
      candidateTests: values[0] ?? 0,
      candidateHits: values[1] ?? 0,
      speculativeTests: values[2] ?? 0,
      speculativeRows: values[3] ?? 0,
      failOpen: values[4] ?? 0,
      initialOverlapRejected: values[5] ?? 0,
      analyticRows: values[6] ?? 0,
      conservativeAdvancementRows: values[7] ?? 0,
      unsupported: values[8] ?? 0,
      nonFinite: values[9] ?? 0,
      iterationLimit: values[10] ?? 0,
      invalidInput: values[11] ?? 0,
      dcdActiveSkips: values[12] ?? 0,
      noClosingMisses: values[13] ?? 0,
      totalIterations: values[14] ?? 0,
      cheapMisses: (values[15] ?? 0) + (values[16] ?? 0),
      prefilterCheapMisses: values[15] ?? 0,
      speculativeCheapMisses: values[16] ?? 0,
      prefilterCaTests: values[17] ?? 0,
      prefilterCaIterations: values[18] ?? 0,
      speculativeCaTests: values[19] ?? 0,
      speculativeCaIterations: values[20] ?? 0,
      activationSkips: (values[21] ?? 0) + (values[22] ?? 0),
      prefilterActivationSkips: values[21] ?? 0,
      speculativeActivationSkips: values[22] ?? 0,
      conservativeFallbacks: values[23] ?? 0,
      fatalFailOpen: values[24] ?? 0,
    };
  }

  async readCapacityLossAsync(renderer: any): Promise<{
    pairBodyListOverflowWrites: number;
    pairBodyListDroppedContacts: number;
    activeContactListOverflowWrites: number;
    persistentManifoldPoolOverflowWrites: number;
    persistentManifoldPoolReclaims: number;
    persistentManifoldHashProtocolFailures: number;
    ccdFatalBits: number;
    compactFatalBits: number;
    compactFirstFatalEpoch: number;
  } | null> {
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return null;
    const readbackGeneration = this.resetGeneration;
    const raw = await renderer.getArrayBufferAsync(this.debugCountersAttr) as ArrayBuffer;
    if (readbackGeneration !== this.resetGeneration) return null;
    let ccdFatalBits = 0;
    if (this.ccdEnabled && this.ccdFatalStateSubmitted) {
      const fatalRaw = await renderer.getArrayBufferAsync(this.ccdFatalStateAttr) as ArrayBuffer;
      if (readbackGeneration !== this.resetGeneration) return null;
      ccdFatalBits = new Uint32Array(fatalRaw)[0] ?? 0;
    }
    const compactState = this.compactMode
      ? await this.readPersistentAllocatorStateAsync(renderer)
      : null;
    if (readbackGeneration !== this.resetGeneration) return null;
    const values = new Uint32Array(raw);
    const result = {
      pairBodyListOverflowWrites: values[24] ?? 0,
      pairBodyListDroppedContacts: values[25] ?? 0,
      activeContactListOverflowWrites: values[26] ?? 0,
      persistentManifoldPoolOverflowWrites: values[27] ?? 0,
      persistentManifoldPoolReclaims: values[28] ?? 0,
      persistentManifoldHashProtocolFailures: values[29] ?? 0,
      ccdFatalBits,
      compactFatalBits: compactState?.fatalBits ?? 0,
      compactFirstFatalEpoch: compactState?.firstFatalEpoch ?? 0,
    };
    this.capacityOverflowDetected ||= result.pairBodyListOverflowWrites > 0
      || result.pairBodyListDroppedContacts > 0
      || result.activeContactListOverflowWrites > 0
      || result.persistentManifoldPoolOverflowWrites > 0
      || result.ccdFatalBits !== 0
      || result.compactFatalBits !== 0;
    return result;
  }

  async readPersistentAllocatorStateAsync(
    renderer: any,
    automaticSampleFrameId?: number,
  ): Promise<{
    highWater: number;
    freeHead: number;
    freeTail?: number;
    nextManifoldFreeCount?: number;
    liveEntries: number;
    mode: number;
    rowHighWater?: number;
    rowLive?: number;
    rowPeakLive?: number;
    epochParity?: number;
    freeCount0?: number;
    freeCount1?: number;
    freeCursor0?: number;
    freeCursor1?: number;
    nextFreeCount?: number;
    fatalBits?: number;
    firstFatalEpoch?: number;
    newRowsThisStep?: number;
    retiredRowsThisStep?: number;
    reclaimCandidateThreads?: number;
    reclaimWorkgroups?: number;
    finalizeCarryThreads?: number;
    finalizeWorkgroups?: number;
    reclaimMode?: number;
    reclaimPhase?: number;
    reclaimPeriod?: number;
    reclaimedManifoldsThisStep?: number;
    reclaimedRowsThisStep?: number;
    pressureFullSweepsThisStep?: number;
    newManifoldsThisStep?: number;
    preReclaimAvailableManifolds?: number;
    preReclaimAvailableRows?: number;
  } | null> {
    const sourceBuffer = (renderer as any)?.backend?.get?.(this.persistentManifoldMapAttr)?.buffer as
      | GPUBuffer
      | undefined;
    if (!sourceBuffer) return null;
    const readbackGeneration = this.resetGeneration;
    const headerWords = this.compactMode
      ? COMPACT_CONTACT_POOL_HEADER_WORDS
      : PERSISTENT_MANIFOLD_HEADER_WORDS;
    const staging = this.device.createBuffer({
      label: 'Persistent Manifold Header Readback',
      size: headerWords * 4,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });
    let mapped = false;
    try {
      const encoder = this.device.createCommandEncoder({
        label: 'Persistent Manifold Header Readback Encoder',
      });
      encoder.copyBufferToBuffer(
        sourceBuffer,
        0,
        staging,
        0,
        headerWords * 4,
      );
      const submit = (): void => this.device.queue.submit([encoder.finish()]);
      if (automaticSampleFrameId !== undefined && this.submissionLedger) {
        this.submissionLedger.withActualSubmitScope(
          this.device.queue,
          {
            frameId: automaticSampleFrameId,
            submissionClass: 'diagnostic',
            label: 'contact-allocator-header-readback',
          },
          submit,
        );
      } else {
        submit();
      }
      if (automaticSampleFrameId !== undefined) {
        this.submissionLedger?.record('diagnostic', 'contact-allocator-header-readback', {
          frameId: automaticSampleFrameId,
        });
      }
      await staging.mapAsync(GPUMapMode.READ);
      mapped = true;
      if (readbackGeneration !== this.resetGeneration) return null;
      const values = new Uint32Array(staging.getMappedRange().slice(0));
      const result: {
        highWater: number;
        freeHead: number;
        freeTail?: number;
        nextManifoldFreeCount?: number;
        liveEntries: number;
        mode: number;
        rowHighWater?: number;
        rowLive?: number;
        rowPeakLive?: number;
        epochParity?: number;
        freeCount0?: number;
        freeCount1?: number;
        freeCursor0?: number;
        freeCursor1?: number;
        nextFreeCount?: number;
        fatalBits?: number;
        firstFatalEpoch?: number;
        newRowsThisStep?: number;
        retiredRowsThisStep?: number;
        reclaimCandidateThreads?: number;
        reclaimWorkgroups?: number;
        finalizeCarryThreads?: number;
        finalizeWorkgroups?: number;
        reclaimMode?: number;
        reclaimPhase?: number;
        reclaimPeriod?: number;
        reclaimedManifoldsThisStep?: number;
        reclaimedRowsThisStep?: number;
        pressureFullSweepsThisStep?: number;
        newManifoldsThisStep?: number;
        preReclaimAvailableManifolds?: number;
        preReclaimAvailableRows?: number;
      } = {
        highWater: values[0] ?? 0,
        freeHead: values[1] ?? 0,
        liveEntries: values[2] ?? 0,
        mode: values[3] ?? 0,
      };
      if (this.compactMode) {
        result.freeTail = values[CompactContactPoolHeaderOffset.manifoldFreeTail] ?? 0;
        result.nextManifoldFreeCount = Math.max(0, result.freeTail - result.freeHead);
        result.rowHighWater = values[CompactContactPoolHeaderOffset.rowHighWater] ?? 0;
        result.rowLive = values[CompactContactPoolHeaderOffset.rowLive] ?? 0;
        result.rowPeakLive = values[CompactContactPoolHeaderOffset.rowPeakLive] ?? 0;
        result.epochParity = values[CompactContactPoolHeaderOffset.epochParity] ?? 0;
        result.freeCount0 = values[CompactContactPoolHeaderOffset.freeCount0] ?? 0;
        result.freeCount1 = values[CompactContactPoolHeaderOffset.freeCount1] ?? 0;
        result.freeCursor0 = values[CompactContactPoolHeaderOffset.freeCursor0] ?? 0;
        result.freeCursor1 = values[CompactContactPoolHeaderOffset.freeCursor1] ?? 0;
        result.nextFreeCount = result.epochParity === 0
          ? result.freeCount1
          : result.freeCount0;
        result.fatalBits = values[CompactContactPoolHeaderOffset.fatalBits] ?? 0;
        result.firstFatalEpoch = values[CompactContactPoolHeaderOffset.firstFatalEpoch] ?? 0;
        result.newRowsThisStep = values[CompactContactPoolHeaderOffset.newRowsThisStep] ?? 0;
        result.retiredRowsThisStep = values[CompactContactPoolHeaderOffset.retiredRowsThisStep] ?? 0;
        result.reclaimCandidateThreads = values[
          CompactContactPoolHeaderOffset.reclaimCandidateThreads
        ] ?? 0;
        result.reclaimWorkgroups = values[CompactContactPoolHeaderOffset.reclaimWorkgroups] ?? 0;
        result.finalizeCarryThreads = values[
          CompactContactPoolHeaderOffset.finalizeCarryThreads
        ] ?? 0;
        result.finalizeWorkgroups = values[CompactContactPoolHeaderOffset.finalizeWorkgroups] ?? 0;
        result.reclaimMode = values[CompactContactPoolHeaderOffset.reclaimMode] ?? 0;
        result.reclaimPhase = values[CompactContactPoolHeaderOffset.reclaimPhase] ?? 0;
        result.reclaimPeriod = values[CompactContactPoolHeaderOffset.reclaimPeriod] ?? 0;
        result.reclaimedManifoldsThisStep = values[
          CompactContactPoolHeaderOffset.reclaimedManifoldsThisStep
        ] ?? 0;
        result.reclaimedRowsThisStep = values[
          CompactContactPoolHeaderOffset.reclaimedRowsThisStep
        ] ?? 0;
        result.pressureFullSweepsThisStep = values[
          CompactContactPoolHeaderOffset.pressureFullSweepsThisStep
        ] ?? 0;
        result.newManifoldsThisStep = values[
          CompactContactPoolHeaderOffset.newManifoldsThisStep
        ] ?? 0;
        result.preReclaimAvailableManifolds = values[
          CompactContactPoolHeaderOffset.preReclaimAvailableManifolds
        ] ?? 0;
        result.preReclaimAvailableRows = values[
          CompactContactPoolHeaderOffset.preReclaimAvailableRows
        ] ?? 0;
      }
      return result;
    } finally {
      if (mapped) staging.unmap();
      staging.destroy();
    }
  }

  reset(): void {
    if (this.disposed) return;
    this.resetGeneration = (this.resetGeneration + 1) >>> 0;
    this.manifoldEpoch = 0;
    this.compactInitialized = false;
    this.compactFinalizedEpoch = 0;
    const values = this.debugCountersAttr.array as Uint32Array;
    values.fill(0);
    this.debugCountersAttr.needsUpdate = true;
    if (this.ccdCountersAttr) {
      const ccdValues = this.ccdCountersAttr.array as Uint32Array;
      ccdValues.fill(0);
      this.ccdCountersAttr.needsUpdate = true;
    }
    const ccdFatalValues = this.ccdFatalStateAttr.array as Uint32Array;
    ccdFatalValues.fill(0);
    this.ccdFatalStateAttr.needsUpdate = true;
    this.ccdFatalStateSubmitted = false;
    const safetyGateValues = this.constraintSafetyGateAttr.array as Uint32Array;
    safetyGateValues.fill(0);
    this.constraintSafetyGateAttr.needsUpdate = true;
    const fatalDispatchValues = this.fatalBodyDispatchAttr.array as Uint32Array;
    fatalDispatchValues[0] = 0;
    fatalDispatchValues[1] = 1;
    fatalDispatchValues[2] = 1;
    this.fatalBodyDispatchAttr.needsUpdate = true;
    this.capacityOverflowDetected = false;
    this.latchedConstraintSafetyBits = 0;
    this.standaloneSafetyReadbackInfrastructureFailure = false;
    this.safetyReadbackFailureWarned = false;
    this.safetyReadbackFailureForTesting = null;
    this.debugReadbackInFlight = false;
    this.pendingDetailReadback = null;
    this.lastDebugLogFrame = -1;
  }

  /**
   * Invalidates asynchronous safety/detail continuations before PhysicsEngine
   * releases the compact raw allocation they could otherwise inspect.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.resetGeneration = (this.resetGeneration + 1) >>> 0;
    this.capacityOverflowDetected = false;
    this.latchedConstraintSafetyBits = 0;
    this.standaloneSafetyReadbackInfrastructureFailure = false;
    this.safetyReadbackFailureForTesting = null;
    this.debugReadbackInFlight = false;
    this.pendingDetailReadback = null;
  }
}
